import {type EngagementSettings} from "./NewsletterEngagementPanel";
import { NewsletterReportMetrics } from "./NewsletterReportMetrics";
import { NewsletterRecoveryNotice } from "./NewsletterRecoveryNotice";
import { NewsletterProtectionPanel } from "./NewsletterProtectionPanel";
import { canReviewSendingList, protectionView, type NewsletterProtectionDecision } from "./newsletterProtection";
import {
  type CSSProperties,
  Component,
  createContext,
  useContext,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent as ReactClipboardEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { Alert, Stepper, Switch } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, useNavigate, useLocation, useBlocker } from "react-router-dom";

import {
  type ApiError,
  fetchJson,
  installEasyAppBridge,
  readSessionJson,
  resolveMountedBasename,
  writeSessionJson,
} from "@easy-core";
import { MediaLibraryModal, type MediaLibraryItem } from "@easy-media";
import { AppActionBar, AppChrome, AppSecondaryBar, AppSurface } from "@easy-ui";
import type { NewsletterDefaultDesignSnapshot } from "@easy-types";

import "@easy-ui/legacy/appBase.css";
import "./newsletter.css";
import "./newsletterSettings.css";
import "./newsletterRichText.css";
import "./festiveRefresh.css";
import { NewsletterFromEventsBuilder } from "../../event-planner/src/NewsletterFromEventsBuilder";
import {
  persistNewsletterComposeDraft,
  suggestNewsletterPeriod,
  type EventRecord,
} from "../../event-planner/src/eventPlannerUtils";
import { AssistantVisualEditor } from "./AssistantVisualEditor";
import { NewsletterDetailPreview } from "./NewsletterDetailPreview";
import { RichTextEditor } from "./RichTextEditor";
import {
  type EmailOnboardingRecommendation,
  getGuardrailProfileLabel,
  getIntendedEmailTypeLabel,
  getPreviousRouteLabel,
  getRecommendedRouteLabel,
  getSendFrequencyLabel,
  getSendSizeLabel,
  hasCompletedEmailOnboarding,
  INTENDED_EMAIL_TYPE_OPTIONS,
  PREVIOUS_ROUTE_OPTIONS,
  SEND_FREQUENCY_OPTIONS,
  SEND_SIZE_OPTIONS,
} from "./emailOnboarding";
import {
  NewsletterIcon as Icon,
  NewsletterIconButton as IconButton,
  NewsletterIconLabel as IconLabel,
  type NewsletterIconId,
} from "./newsletterIcons";
import {
  assistantDraftHasMeaningfulContent,
  applyEmailProviderPreset,
  ASSISTANT_THEME_STYLE_DEFAULTS,
  EMAIL_PROVIDER_PRESETS,
  buildAssistantHtml,
  buildClassicCopyName,
  buildPlainClassicEmailHtmlFromAssistantDraft,
  createDefaultAssistantTheme,
  buildGeneratedDraftName,
  analyzeRecipients,
  buildCampaignPayload,
  buildPublicUrlNotice,
  buildTestEmailPayload,
  classicComposerHasMeaningfulContent,
  createEmptyAssistantDraft,
  createEmptyAssistantSection,
  createAssistantDraftFromDefaultDesign,
  clearIncomingComposeDraft,
  clearSettingsDraft,
  createEmptyCampaignForm,
  createEmptySettingsForm,
  detectEmailProviderPreset,
  editorHtmlHasContent,
  extractEmailPreviewHtml,
  getEmailProviderPreset,
  humanizeReleaseChannel,
  inferCampaignFieldErrors,
  inferSettingsFieldErrors,
  inferTestEmailFieldErrors,
  hasAssistantNewsletterMetadata,
  hasConfiguredSmtpSettings,
  MAX_CAMPAIGN_RECIPIENTS,
  normalizeSettingsForm,
  normalizeAssistantDraft,
  parseAssistantDraftFromHtml,
  persistAssistantLogoDefaults,
  persistAssistantThemeDefaults,
  persistSettingsDraft,
  readAssistantLogoDefaults,
  readAssistantThemeDefaults,
  readIncomingComposeDraft,
  readSettingsDraft,
  resolveDefaultAssistantSectionLayout,
  resolveAssistantImageLayoutsForThemeChange,
  resolveFieldErrors,
  stripComposerMetadata,
  TEST_RECIPIENT_STORAGE_KEY,
  toCampaignForm,
  type AssistantDraftState,
  type CampaignFormState,
  type ComposerContentMode,
  type EmailAccountKind,
  type EmailProviderChoiceId,
  type NewsletterSettings,
  type EmailProviderPresetId,
  type NewsletterCampaign,
  type NewsletterCampaignDetail,
  type NewsletterGuardrailEvaluation,
  type NewsletterMeta,
  type StatusState,
  type TabId,
} from "./newsletterUtils";

function statusClassName(status: StatusState) {
  if (status.tone === "error") {
    return "status-line error";
  }
  if (status.tone === "success") {
    return "status-line success";
  }
  return "status-line";
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function hasFieldError(fieldErrors: string[], fieldId: string) {
  return fieldErrors.includes(fieldId);
}

function normalizeApiError(error: unknown) {
  return error as ApiError;
}

const EMAIL_ONBOARDING_API_FIELD_IDS: Record<string, keyof NewsletterSettings> = {
  previous_sending_route: "email_onboarding_previous_sending_route",
  previous_send_size_range: "email_onboarding_previous_send_size_range",
  previous_send_frequency: "email_onboarding_previous_send_frequency",
  intended_send_size_range: "email_onboarding_intended_send_size_range",
  intended_send_frequency: "email_onboarding_intended_send_frequency",
  intended_email_type: "email_onboarding_intended_email_type",
};

function resolveEmailOnboardingFieldErrors(error: unknown) {
  return resolveFieldErrors(error, [])
    .map((fieldId) => EMAIL_ONBOARDING_API_FIELD_IDS[fieldId] || "")
    .filter(Boolean);
}

const EMAIL_PROVIDER_FIELD_IDS = ["smtp_host", "smtp_port", "smtp_security", "imap_host", "imap_port", "imap_ssl"];
const EMAIL_PROVIDER_USERNAME_FIELD_IDS = ["smtp_username", "imap_username"];
const EMAIL_PROVIDER_PASSWORD_FIELD_IDS = ["smtp_password", "imap_password"];
const EMAIL_PROVIDER_CHOICE_IDS = ["infomaniak", "gmail", "outlook", "yahoo", "icloud", "aol", "custom", "unknown"] as const;
const EMAIL_ACCOUNT_KIND_OPTIONS: { value: EmailAccountKind; label: string; hint: string }[] = [
  {
    value: "professional",
    label: "Adresse professionnelle",
    hint: "Adresse liée à votre domaine ou à un service pro.",
  },
  {
    value: "personal",
    label: "Adresse personnelle",
    hint: "Adresse Gmail, Outlook, Yahoo, iCloud ou équivalent.",
  },
  {
    value: "unknown",
    label: "Je ne sais pas",
    hint: "Relyo partira d'une hypothèse prudente.",
  },
];

type EmailConnectionStep =
  | "onboarding_previous_route"
  | "onboarding_previous_usage"
  | "onboarding_intended_usage"
  | "onboarding_recommendation"
  | "service"
  | "credentials"
  | "review";
type EmailProviderConnectionMode = "standard" | "secure_sign_in" | "app_password" | "manual";

type EmailProviderChoiceCard = {
  id: EmailProviderChoiceId;
  label: string;
  description: string;
  iconUrl?: string;
  brandColor?: string;
  tag: string;
};

type SesIdentityMode = "dedicated_subdomain" | "existing_from_address";

type SesDnsRecord = {
  type: string;
  name: string;
  value: string;
  status?: string;
};

type SesDnsRecordPurpose = "identity" | "dkim" | "mail_from";

type SesDnsRecordGroup = {
  purpose: SesDnsRecordPurpose;
  title: string;
  description: string;
  records: SesDnsRecord[];
};

const EMAIL_PROVIDER_CHOICES: EmailProviderChoiceCard[] = [
  ...EMAIL_PROVIDER_PRESETS.map((preset) => ({
    id: preset.id,
    label: preset.label,
    description: preset.description,
    iconUrl: preset.icon_url,
    brandColor: preset.brand_color,
    tag:
      preset.id === "gmail" || preset.id === "outlook"
        ? "En préparation"
        : preset.id === "yahoo" || preset.id === "icloud" || preset.id === "aol"
          ? "Mot de passe d'app"
          : "Le plus courant",
  })),
  {
    id: "custom",
    label: "Autre service",
    description: "Votre service email n'est pas dans la liste.",
    tag: "Manuel",
  },
  {
    id: "unknown",
    label: "Je ne sais pas",
    description: "Relyo vous guidera avec une connexion plus prudente.",
    tag: "Aide",
  },
];

const EMAIL_CONNECTION_STEPS: { id: EmailConnectionStep; label: string; hint: string }[] = [
  {
    id: "onboarding_previous_route",
    label: "Votre usage",
    hint: "Avant Relyo",
  },
  {
    id: "onboarding_previous_usage",
    label: "Historique",
    hint: "Volume et frequence",
  },
  {
    id: "onboarding_intended_usage",
    label: "Avec Relyo",
    hint: "Vos prochaines semaines",
  },
  {
    id: "onboarding_recommendation",
    label: "Chemin conseille",
    hint: "Route recommandee",
  },
  {
    id: "service",
    label: "Choisir le service",
    hint: "Gmail, Outlook, Infomaniak...",
  },
  {
    id: "credentials",
    label: "Connecter l'adresse",
    hint: "Connexion guidée",
  },
  {
    id: "review",
    label: "Vérifier",
    hint: "Récapitulatif final",
  },
];

function isEmailProviderChoiceId(value: string): value is EmailProviderChoiceId {
  return (EMAIL_PROVIDER_CHOICE_IDS as readonly string[]).includes(value);
}

function resolveEmailProviderChoice(
  settings: Partial<NewsletterSettings>,
  detectedProviderMode?: EmailProviderPresetId
): EmailProviderChoiceId {
  const stored = String(settings.email_provider_selection || "").trim();
  if (isEmailProviderChoiceId(stored)) {
    return stored;
  }
  const detected = detectedProviderMode || detectEmailProviderPreset(settings);
  return detected === "custom" ? "unknown" : detected;
}

function emailProviderIsTemporarilyUnavailable(providerChoice: EmailProviderChoiceId) {
  return providerChoice === "gmail" || providerChoice === "outlook";
}

function emailProviderDevelopmentMessage(providerChoice: EmailProviderChoiceId) {
  if (providerChoice === "gmail") {
    return {
      title: "Connexion Gmail en préparation",
      message:
        "La connexion Gmail est en cours de développement. En attendant, vous pouvez choisir un autre service mail ou utiliser Autre service si vous avez des réglages SMTP et IMAP prêts.",
    };
  }
  if (providerChoice === "outlook") {
    return {
      title: "Connexion Microsoft en préparation",
      message:
        "La connexion Outlook / Microsoft 365 est en cours de développement. En attendant, vous pouvez choisir un autre service mail ou utiliser Autre service si vous avez des réglages SMTP et IMAP prêts.",
    };
  }
  return null;
}

function deriveEmailConnectionStep(
  settings: Partial<NewsletterSettings>,
  providerChoice: EmailProviderChoiceId
): EmailConnectionStep {
  if (
    String(settings.email_route_type || "").trim() === "relyo_managed_ses" ||
    String(settings.email_onboarding_chosen_route || "").trim() === "amazon_ses"
  ) {
    return "review";
  }
  if (hasConfiguredSmtpSettings(settings)) {
    return "review";
  }
  if (!String(settings.email_onboarding_previous_sending_route || "").trim()) {
    return "onboarding_previous_route";
  }
  if (
    !String(settings.email_onboarding_previous_send_size_range || "").trim() ||
    !String(settings.email_onboarding_previous_send_frequency || "").trim()
  ) {
    return "onboarding_previous_usage";
  }
  if (
    !String(settings.email_onboarding_intended_send_size_range || "").trim() ||
    !String(settings.email_onboarding_intended_send_frequency || "").trim() ||
    !String(settings.email_onboarding_intended_email_type || "").trim()
  ) {
    return "onboarding_intended_usage";
  }
  if (!hasCompletedEmailOnboarding(settings)) {
    return "onboarding_intended_usage";
  }
  if (String(settings.email_onboarding_chosen_route || "").trim() === "amazon_ses") {
    return "review";
  }
  if (emailProviderIsTemporarilyUnavailable(providerChoice)) {
    return "service";
  }
  if (
    String(settings.email_onboarding_recommended_route || "").trim() &&
    !String(settings.email_onboarding_chosen_route || "").trim() &&
    !String(settings.smtp_username || "").trim()
  ) {
    return "onboarding_recommendation";
  }
  if (providerChoice !== "unknown" || String(settings.smtp_username || "").trim()) {
    return "credentials";
  }
  return "service";
}

function providerChoiceLabel(providerChoice: EmailProviderChoiceId, providerMode: EmailProviderPresetId) {
  if (providerChoice === "unknown") {
    return "Service à confirmer";
  }
  if (providerChoice === "custom") {
    return "Autre service";
  }
  return getEmailProviderPreset(providerMode === "custom" ? providerChoice : providerMode)?.label || "Boîte mail";
}

function buildEmailOnboardingRecommendationFallback(
  recommendedRoute: string,
  initialGuardrailProfile: string
): EmailOnboardingRecommendation | null {
  if (recommendedRoute === "amazon_ses") {
    return {
      recommended_route: "amazon_ses",
      initial_guardrail_profile: initialGuardrailProfile === "mode_progressif" ? "mode_progressif" : "demarrage",
      can_continue_with_classic_mailbox: true,
      show_ses_cost_note: true,
      reason_codes: [],
      summary_title: "Amazon SES est recommande pour cet usage",
      summary_message: "Pour le volume ou le rythme prevu, une infrastructure dediee protege mieux la delivrabilite.",
      summary_points: [
        "Meilleure adequation pour un usage newsletter regulier.",
        "Risque plus faible qu'une boite mail classique pour ce depart.",
        "Cette option implique un leger surcout.",
      ],
    };
  }
  if (recommendedRoute === "classic_mailbox") {
    return {
      recommended_route: "classic_mailbox",
      initial_guardrail_profile: initialGuardrailProfile === "mode_progressif" ? "mode_progressif" : "demarrage",
      can_continue_with_classic_mailbox: true,
      show_ses_cost_note: false,
      reason_codes: [],
      summary_title: "Votre adresse actuelle peut convenir pour demarrer",
      summary_message: "Relyo peut demarrer avec une adresse classique tout en gardant un cadre prudent.",
      summary_points: [
        "Relyo verifiera chaque campagne avant l'envoi.",
        "Le cadre pourra evoluer si vos volumes augmentent.",
      ],
    };
  }
  return null;
}

function resolveEmailProviderConnectionMode(providerChoice: EmailProviderChoiceId): EmailProviderConnectionMode {
  if (providerChoice === "custom" || providerChoice === "unknown") {
    return "manual";
  }
  if (providerChoice === "gmail" || providerChoice === "outlook") {
    return "secure_sign_in";
  }
  if (providerChoice === "yahoo" || providerChoice === "icloud" || providerChoice === "aol") {
    return "app_password";
  }
  return "standard";
}

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "long",
  timeStyle: "short",
});

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  weekday: "short",
  day: "numeric",
  month: "short",
});

function formatShortDateLabel(value?: string) {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : SHORT_DATE_FORMATTER.format(parsed);
}

function formatDateTimeLabel(value?: string) {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return DATE_TIME_FORMATTER.format(parsed);
}

function normalizeSesIdentityMode(value?: string): SesIdentityMode {
  return value === "existing_from_address" ? "existing_from_address" : "dedicated_subdomain";
}

function emailDomain(value?: string) {
  const trimmed = String(value || "").trim();
  if (!trimmed.includes("@")) {
    return "";
  }
  return trimmed.split("@").pop()?.trim().toLowerCase() || "";
}

function splitDedicatedSesIdentity(domainIdentity?: string) {
  const labels = String(domainIdentity || "")
    .trim()
    .toLowerCase()
    .split(".")
    .filter(Boolean);
  if (labels.length >= 3) {
    return {
      prefix: labels[0] || "news",
      domain: labels.slice(1).join("."),
    };
  }
  return {
    prefix: "news",
    domain: labels.join("."),
  };
}

function sanitizeSesPrefix(value: string) {
  return (
    String(value || "news")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "")
      .replace(/^-+|-+$/g, "") || "news"
  );
}

function parseSesDnsRecords(value?: string): SesDnsRecord[] {
  try {
    const parsed = JSON.parse(String(value || "[]"));
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((record) => ({
        type: String(record?.type || "").trim().toUpperCase(),
        name: String(record?.name || "").trim(),
        value: String(record?.value || "").trim(),
        status: String(record?.status || "").trim(),
      }))
      .filter((record) => record.type && record.name && record.value);
  } catch {
    return [];
  }
}

function resolveSesDnsRecordPurpose(record: SesDnsRecord, mailFromDomain?: string): SesDnsRecordPurpose {
  const name = record.name.toLowerCase();
  const value = record.value.toLowerCase();
  const type = record.type.toUpperCase();
  const normalizedMailFromDomain = String(mailFromDomain || "").trim().toLowerCase();

  if (name.includes("._domainkey.") || value.includes(".dkim.") || value.includes("dkim.amazonses.com")) {
    return "dkim";
  }
  if (
    type === "MX" ||
    value.includes("feedback-smtp.") ||
    (normalizedMailFromDomain && name === normalizedMailFromDomain)
  ) {
    return "mail_from";
  }
  return "identity";
}

function groupSesDnsRecords(records: SesDnsRecord[], mailFromDomain?: string): SesDnsRecordGroup[] {
  const groups: SesDnsRecordGroup[] = [
    {
      purpose: "identity",
      title: "Identité du domaine",
      description: "Prouve que ce domaine peut être utilisé pour vos newsletters.",
      records: [],
    },
    {
      purpose: "dkim",
      title: "DKIM",
      description: "Aide les boîtes mail à reconnaître que les messages viennent bien de vous.",
      records: [],
    },
    {
      purpose: "mail_from",
      title: "MAIL FROM",
      description: "Gère les retours techniques sans exposer votre boîte mail principale.",
      records: [],
    },
  ];
  const groupByPurpose = new Map(groups.map((group) => [group.purpose, group]));
  records.forEach((record) => {
    groupByPurpose.get(resolveSesDnsRecordPurpose(record, mailFromDomain))?.records.push(record);
  });
  return groups.filter((group) => group.records.length > 0);
}

function sesDnsProviderStatusLabel(status?: string) {
  const normalized = String(status || "").trim().toLowerCase();
  if (["success", "verified", "ready", "valid"].includes(normalized)) {
    return "Vérifié";
  }
  if (["failed", "error", "invalid"].includes(normalized)) {
    return "À corriger";
  }
  if (normalized) {
    return "En attente";
  }
  return "";
}

function sesDnsFallbackStatusLabel(purpose: SesDnsRecordPurpose, dkimVerified: boolean, mailFromVerified: boolean) {
  if (purpose === "dkim") {
    return dkimVerified ? "Vérifié" : "En attente";
  }
  if (purpose === "mail_from") {
    return mailFromVerified ? "Vérifié" : "En attente";
  }
  return "En attente";
}

function sesDnsStatusClassName(label: string) {
  if (label === "Vérifié") {
    return "newsletter-ses-dns-status is-verified";
  }
  if (label === "À corriger") {
    return "newsletter-ses-dns-status is-error";
  }
  return "newsletter-ses-dns-status is-pending";
}

async function copyTextToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
  }
}

function restoreClassicMailboxRoute(settings: NewsletterSettings): NewsletterSettings {
  const mailboxSender = settings.smtp_username || settings.imap_username || settings.sender_email;
  return {
    ...settings,
    sender_email: mailboxSender,
    auth_method: settings.auth_method === "ses" ? "" : settings.auth_method,
    email_route_type: "",
    email_onboarding_chosen_route: "classic_mailbox",
    ses_identity_mode: "",
    ses_domain_identity: "",
    ses_mail_from_domain: "",
    ses_status: "",
    ses_dns_records_json: "",
    ses_dkim_verified: "",
    ses_mail_from_verified: "",
    ses_last_checked_at: "",
    ses_last_error: "",
    ses_setup_guidance: "",
    ses_setup_warning: "",
  };
}

function booleanSettingIsTrue(value?: string) {
  return String(value || "").trim().toLowerCase() === "true";
}

function sesStatusLabel(status?: string) {
  switch (String(status || "").trim()) {
    case "ready":
      return "Prêt";
    case "pending_dns":
      return "DNS à finaliser";
    case "failed":
      return "À corriger";
    case "paused":
      return "En pause";
    default:
      return "À préparer";
  }
}

type GuardrailDisplayData = {
  account_mode?: string;
  campaign_risk_level?: string;
  reason_codes?: string[];
  recommended_next_action?: {
    key: string;
    label: string;
    message: string;
  };
  recommended_content_simplification?: {
    level: string;
    message: string;
    points: string[];
  };
  allowed_actions?: Record<string, string>;
  delivery_health_snapshot?: {
    bounce_rate?: number;
    smtp_failure_count?: number;
    imap_bounce_count?: number;
    recent_open_rate?: number | null;
    rolling_open_rate_median?: number | null;
    open_rate_drop_ratio?: number;
    consecutive_healthy_campaigns?: number;
    recovery_campaign_count?: number;
  };
  provider_label?: string;
  max_per_hour?: number;
  max_per_day?: number;
  max_per_30_days?: number;
  send_now_recipient_count?: number;
  deferred_recipient_count?: number;
  next_send_window_at?: string | null;
};

function guardrailRiskMeta(guardrails?: GuardrailDisplayData | null) {
  const risk = String(guardrails?.campaign_risk_level || "").trim().toLowerCase();
  if (risk === "critical") {
    return { label: "Risque critique", toneClassName: "is-critical" };
  }
  if (risk === "high") {
    return { label: "Risque élevé", toneClassName: "is-high" };
  }
  if (risk === "medium") {
    return { label: "À surveiller", toneClassName: "is-medium" };
  }
  return { label: "Situation saine", toneClassName: "is-low" };
}

function buildGuardrailObservedSignals(guardrails?: GuardrailDisplayData | null) {
  const health = guardrails?.delivery_health_snapshot;
  if (!health) {
    return [];
  }
  const signals: string[] = [];
  if ((health.bounce_rate || 0) >= 0.04) {
    signals.push("Les rejets récents sont plus élevés que d'habitude.");
  }
  if ((health.smtp_failure_count || 0) > 0) {
    signals.push("Des erreurs d'envoi récentes ont été détectées.");
  }
  if ((health.imap_bounce_count || 0) > 0) {
    signals.push("Nous avons détecté des rejets depuis la boite mail, au cours des 5 derniers envois.");
  }
  if ((health.open_rate_drop_ratio || 0) >= 0.3) {
    signals.push("Les ouvertures récentes sont en baisse par rapport aux campagnes précédentes.");
  }
  if ((health.recovery_campaign_count || 0) > 0 && (health.consecutive_healthy_campaigns || 0) > 0) {
    signals.push(
      `${health.consecutive_healthy_campaigns} campagne${health.consecutive_healthy_campaigns && health.consecutive_healthy_campaigns > 1 ? "s" : ""} récente${health.consecutive_healthy_campaigns && health.consecutive_healthy_campaigns > 1 ? "s" : ""} se passe${health.consecutive_healthy_campaigns && health.consecutive_healthy_campaigns > 1 ? "nt" : ""} mieux.`
    );
  }
  return signals;
}

function buildGuardrailAutomaticActions(guardrails?: GuardrailDisplayData | null) {
  const actions = guardrails?.allowed_actions || {};
  const lines: string[] = [];
  if (actions.send_full_audience_now === "partial_only") {
    lines.push("Relyo enverra cette campagne par étapes au lieu de tout envoyer d'un coup.");
  }
  if (actions.enable_tracking === "blocked" || actions.enable_tracking === "discouraged") {
    lines.push("Relyo déconseille le suivi des ouvertures pour le moment.");
  }
  if (actions.schedule_now === "confirm") {
    lines.push("Relyo demandera une confirmation finale avant la planification.");
  }
  if (actions.keep_current_route === "discouraged") {
    lines.push("Relyo recommandera bientôt un envoi plus adapté à votre volume.");
  }
  return lines;
}

function buildGuardrailLimitsSummary(guardrails?: GuardrailDisplayData | null) {
  const items: string[] = [];
  if (guardrails?.max_per_hour) {
    items.push(`Jusqu'à ${guardrails.max_per_hour} emails par heure dans le cadre prudent actuel.`);
  }
  if (guardrails?.max_per_day) {
    items.push(`Jusqu'à ${guardrails.max_per_day} emails par jour dans le cadre prudent actuel.`);
  }
  if (guardrails?.max_per_30_days) {
    items.push(`Jusqu'à ${guardrails.max_per_30_days} emails sur 30 jours avant changement de palier.`);
  }
  return items;
}

function accountModeHeadline(accountMode?: string) {
  const mode = String(accountMode || "").trim().toLowerCase();
  if (mode === "demarrage") {
    return "Votre envoi démarre prudemment";
  }
  if (mode === "mode_progressif") {
    return "Votre envoi est en mode progressif";
  }
  if (mode === "protection_active") {
    return "Votre envoi est sous protection active";
  }
  if (mode === "migration_recommandee") {
    return "Votre route actuelle atteint ses limites";
  }
  return "Votre qualité d'envoi est stable";
}

function scheduledAtIsInPast(value?: string) {
  if (!value) {
    return false;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  return parsed.getTime() <= Date.now();
}

function normalizeCampaignStatus(status?: string) {
  return String(status || "draft")
    .trim()
    .toLowerCase();
}

function campaignStatusClassName(status?: string) {
  return `pill pill-${normalizeCampaignStatus(status)}`;
}

function campaignStatusLabel(status?: string) {
  const normalized = normalizeCampaignStatus(status);
  if (normalized === "draft") {
    return "Brouillon";
  }
  if (normalized === "completed") {
    return "Completed";
  }
  if (normalized === "scheduled") {
    return "Scheduled";
  }
  if (normalized === "running") {
    return "Running";
  }
  if (normalized === "failed") {
    return "Failed";
  }
  if (normalized === "cancelled") {
    return "Cancelled";
  }
  return status || "Inconnu";
}

function campaignIsSuspended(campaign?: Partial<NewsletterCampaign> | null) {
  return ['running', 'scheduled'].includes(normalizeCampaignStatus(campaign?.status))
    && campaign?.protection?.action.kind === 'pause';
}

function campaignDeliveryLabel(campaign?: Partial<NewsletterCampaign> | null) {
  if (campaignIsSuspended(campaign)) {
    return canReviewSendingList(campaign?.protection) ? "Suspendu · Action requise" : "Envoi suspendu";
  }
  const deliveryState = String(campaign?.campaign_delivery_state || "").trim().toLowerCase();
  if (deliveryState === "in_progress") {
    return "En cours d'envoi";
  }
  if (deliveryState === "sent" || normalizeCampaignStatus(campaign?.status) === "completed") {
    return "Envoyée";
  }
  if (deliveryState === "scheduled" || normalizeCampaignStatus(campaign?.status) === "scheduled") {
    return "Planifiée";
  }
  if (deliveryState === "cancelled" || normalizeCampaignStatus(campaign?.status) === "cancelled") {
    return "Annulée";
  }
  if (deliveryState === "attention" || normalizeCampaignStatus(campaign?.status) === "failed") {
    return "À vérifier";
  }
  if (normalizeCampaignStatus(campaign?.status) === "running") {
    return "En cours d'envoi";
  }
  return campaignStatusLabel(campaign?.status);
}

function campaignDeliveryClassName(campaign?: Partial<NewsletterCampaign> | null) {
  if (campaignIsSuspended(campaign)) return "pill pill-suspended";
  const deliveryState = String(campaign?.campaign_delivery_state || "").trim().toLowerCase();
  if (deliveryState === "in_progress" || normalizeCampaignStatus(campaign?.status) === "running") {
    return "pill pill-running";
  }
  if (deliveryState === "sent" || normalizeCampaignStatus(campaign?.status) === "completed") {
    return "pill pill-completed";
  }
  if (deliveryState === "scheduled" || normalizeCampaignStatus(campaign?.status) === "scheduled") {
    return "pill pill-scheduled";
  }
  if (deliveryState === "cancelled" || normalizeCampaignStatus(campaign?.status) === "cancelled") {
    return "pill pill-cancelled";
  }
  if (deliveryState === "attention" || normalizeCampaignStatus(campaign?.status) === "failed") {
    return "pill pill-failed";
  }
  return campaignStatusClassName(campaign?.status);
}

function buildTrackingInterventionCopy(guardrails?: NewsletterGuardrailEvaluation | null) {
  const providerLabel = guardrails?.provider_label || "votre service mail";
  if (guardrails?.account_mode === "protection_active") {
    return {
      title: "Mieux vaut attendre avant d'activer le suivi des ouvertures",
      message:
        "Vos derniers emails ont été moins ouverts ou ont généré plus d'erreurs. Pour le prochain envoi, mieux vaut garder un email plus simple et laisser le suivi des ouvertures désactivé.",
      confirmLabel: "Laisser désactivé",
      secondaryLabel: "Activer quand même",
    };
  }
  if (guardrails?.account_mode === "migration_recommandee") {
    return {
      title: "Le suivi des ouvertures peut attendre un peu",
      message: `Avec ${providerLabel}, cette campagne risque déjà d'être ralentie ou partiellement bloquée. Mieux vaut garder le suivi des ouvertures désactivé pour ce premier passage.`,
      confirmLabel: "Laisser désactivé",
      secondaryLabel: "Activer quand même",
    };
  }
  return {
    title: "Mieux vaut garder le suivi des ouvertures désactivé pour l'instant",
    message:
      "Le suivi des ouvertures change la façon dont l'email est envoyé. Pour ce premier envoi, mieux vaut commencer sans lui pour réduire le risque d'indésirables.",
    confirmLabel: "Laisser désactivé",
    secondaryLabel: "Activer quand même",
  };
}

function buildComposerGuardrailAlert(
  step: "content" | "audience",
  guardrails?: NewsletterGuardrailEvaluation | null
) {
  if (!guardrails) {
    return null;
  }
  if (guardrails.account_mode === "protection_active") {
    return {
      tone: "warning",
      title:
        step === "content"
          ? "Pour ce prochain envoi, gardez un email plus simple"
          : "Pour ce prochain envoi, choisissez un groupe plus réduit",
      message:
        step === "content"
          ? "Vos derniers emails ont été moins ouverts ou ont généré plus d'erreurs. Pour réduire le risque d'indésirables, mieux vaut repartir avec un email plus léger."
          : "Vos derniers emails ont été moins ouverts ou ont généré plus d'erreurs. Pour réduire le risque d'indésirables, mieux vaut repartir avec moins de destinataires à la fois.",
      points:
        step === "content"
          ? ["Gardez un email simple avec moins de liens, de boutons et d'images."]
          : ["Pour ce prochain envoi, Relyo gardera aussi un cadre plus prudent."],
    };
  }
  if (guardrails.account_mode === "migration_recommandee") {
    return {
      tone: "warning",
      title: "Votre service mail arrive à sa limite",
      message:
        guardrails.summary_message ||
        "Votre service mail actuel risque de ralentir ou bloquer une partie de cet envoi. Relyo vous aide à continuer prudemment, mais une migration sera vite nécessaire.",
      points: guardrails.summary_points || [],
    };
  }
  if (guardrails.account_mode === "mode_progressif" && guardrails.deferred_recipient_count) {
    return {
      tone: "info",
      title: "Cette campagne partira en plusieurs étapes",
      message:
        guardrails.summary_message ||
        "On n'envoie pas toute la campagne d'un coup, pour réduire le risque d'indésirables.",
      points: guardrails.summary_points || [],
    };
  }
  return null;
}

function buildProgressiveChangePanel(guardrails?: NewsletterGuardrailEvaluation | null) {
  if (!guardrails || guardrails.account_mode !== "mode_progressif") {
    return null;
  }
  const deferred = Math.max(0, Number(guardrails.deferred_recipient_count || 0));
  const sendNow = Math.max(0, Number(guardrails.send_now_recipient_count || 0));
  const providerLabel = guardrails.provider_label || "votre service mail";
  return {
    title: "Ce qui change pour cet envoi",
    message:
      deferred > 0
        ? `Cette campagne est plus grande ou plus marketing que d'habitude. Avec ${providerLabel}, Relyo va l'étaler pour éviter un départ trop brusque.`
        : `Cette campagne demande un peu plus de prudence. Avec ${providerLabel}, Relyo garde un rythme plus progressif pour cet envoi.`,
    points: [
      `${sendNow} email${sendNow > 1 ? "s" : ""} peuvent partir tout de suite.`,
      deferred > 0
        ? `${deferred} email${deferred > 1 ? "s" : ""} partiront ensuite automatiquement.`
        : "Tout peut partir aujourd'hui, mais avec un rythme plus espacé.",
      `Relyo enverra environ ${guardrails.batch_size} emails toutes les ${Math.max(1, Math.round(guardrails.batch_delay_seconds / 60))} min.`,
    ],
  };
}

function buildProtectionBeforeSendCopy(guardrails?: NewsletterGuardrailEvaluation | null) {
  const providerLabel = guardrails?.provider_label || "votre service mail";
  return {
    title: "Avant cet envoi",
    message: `Les derniers signaux sont plus fragiles avec ${providerLabel}. Si vous planifiez cette campagne telle quelle, une partie risque davantage d'arriver dans les indésirables ou d'être moins bien reçue.`,
    recommendations: [
      "Réduire le nombre de destinataires pour ce prochain envoi.",
      "Laisser le suivi des ouvertures désactivé pour repartir plus simplement.",
      "Garder un email plus léger, avec moins de liens, de boutons et d'images.",
    ],
    primaryLabel: "Revenir alléger cet envoi",
    secondaryLabel: "Planifier quand même",
  };
}

function measureEmailComplexity(htmlBody?: string | null) {
  const content = String(htmlBody || "");
  return {
    links: (content.match(/<a\b/gi) || []).length,
    buttons: (content.match(/<button\b/gi) || []).length,
    images: (content.match(/<img\b/gi) || []).length,
  };
}

function protectionRecommendationsNeedConfirmation(
  guardrails: NewsletterGuardrailEvaluation | null | undefined,
  options: {
    totalRecipients: number;
    htmlBody: string;
    trackingEnabled: boolean;
  }
) {
  if (!guardrails || guardrails.account_mode !== "protection_active") {
    return false;
  }

  const actions = guardrails.allowed_actions || {};
  const complexity = measureEmailComplexity(options.htmlBody);
  const simplificationLevel = String(guardrails.recommended_content_simplification?.level || "none").trim().toLowerCase();
  const trackingNeedsAttention =
    (actions.enable_tracking === "blocked" || actions.enable_tracking === "discouraged") && options.trackingEnabled;
  const audienceNeedsAttention =
    actions.reduce_audience === "recommended" &&
    (Number(guardrails.deferred_recipient_count || 0) > 0 ||
      (guardrails.max_per_hour ? options.totalRecipients > Math.max(1, guardrails.max_per_hour) : false));

  let contentNeedsAttention = false;
  if (simplificationLevel === "strong") {
    contentNeedsAttention = complexity.links > 3 || complexity.buttons > 0 || complexity.images > 1;
  } else if (simplificationLevel === "light") {
    contentNeedsAttention = complexity.links > 4 || complexity.buttons > 0 || complexity.images > 2;
  }

  return trackingNeedsAttention || audienceNeedsAttention || contentNeedsAttention;
}

type NewsletterRouteConfig = {
  listPath: string;
  apiBasePath: string;
  contactsGroupsApiPath: string;
  contactsResolveApiPath: string;
  allowStandaloneContactsIntegration: boolean;
};

type ContactsNewsletterGroup = {
  id: number;
  name: string;
  color: string;
  contact_count: number;
};

type ContactsNewsletterResolution = {
  groups: ContactsNewsletterGroup[];
  recipients: string[];
  missing_group_ids: number[];
};

type ComposerStepId = "naming" | "content" | "audience" | "review";

const COMPOSER_STEPS: Array<{ id: ComposerStepId; label: string }> = [
  { id: "naming", label: "Nom" },
  { id: "content", label: "Contenu" },
  { id: "audience", label: "Destinataires" },
  { id: "review", label: "Vérification" },
];

const RECIPIENT_ERROR_MESSAGES = new Set([
  "Ajoutez au moins une adresse email ou un groupe de contacts.",
  "Corrigez les adresses email invalides affichées en rouge avant la planification.",
  "Aucune adresse email valide détectée dans cette saisie.",
  "Les groupes sélectionnés ne contiennent aucun destinataire exploitable.",
  "Aucun destinataire valide n'a été détecté.",
]);

const AUTO_SAVE_DEBOUNCE_MS = 1800;
const AUTO_SAVE_INTERVAL_MS = 12000;

type AutoSaveState = "idle" | "pending" | "saving" | "saved" | "error";
type CampaignStatusFilter = "all" | "scheduled" | "running" | "sent" | "cancelled" | "failed";

type NewsletterViewState = {
  activeTab: TabId;
  selectedDraftId: number | null;
  selectedCampaignId: number | null;
  selectedDraftIds: number[];
  editingDraftId: number | null;
  isComposerOpen: boolean;
  composerStep: ComposerStepId;
  composerContentMode: ComposerContentMode;
};

const NEWSLETTER_VIEW_STATE_STORAGE_KEY = "easy-newsletter:view-state";

type NewsletterMediaTarget =
  | {
      kind: "assistant-section";
      sectionId: string;
    }
  | {
      kind: "assistant-hero";
    }
  | {
      kind: "assistant-logo";
    }
  | {
      kind: "richtext";
    };

function buildComposerDraftSignature(
  form: CampaignFormState,
  contentMode: ComposerContentMode,
  assistant: AssistantDraftState
) {
  return JSON.stringify({
    contentMode,
    name: form.name,
    subject: form.subject,
    scheduled_at: form.scheduled_at,
    raw_recipients: form.raw_recipients,
    contact_group_ids: form.contact_group_ids,
    contact_group_labels: form.contact_group_labels,
    tracking_enabled: form.tracking_enabled,
    unsubscribe_enabled: form.unsubscribe_enabled,
    content:
      contentMode === "assistant"
        ? assistant
        : {
            html_body: stripComposerMetadata(form.html_body).trim(),
          },
  });
}

function isCampaignStatusLinkedToField(fieldId: string, message: string) {
  const content = (message || "").toLowerCase();

  if (!content) {
    return false;
  }

  if (fieldId === "campaign_name") {
    return content.includes("nom de campagne");
  }
  if (fieldId === "campaign_subject") {
    return content.includes("l'objet");
  }
  if (fieldId === "campaign_scheduled_at") {
    return content.includes("date et l'heure d'envoi");
  }
  if (fieldId === "campaign_editor") {
    return content.includes("contenu du mail");
  }
  if (fieldId === "campaign_recipients") {
    return (
      RECIPIENT_ERROR_MESSAGES.has(message) ||
      content.includes("destinataire") ||
      content.includes("adresse email") ||
      content.includes("adresses email") ||
      content.includes("groupe")
    );
  }

  return false;
}

function buildApiUrl(basePath: string, path: string) {
  const normalizedPath = String(path || "").replace(/^\/+/, "");
  return `${basePath}/${normalizedPath}`.replace(/\/{2,}/g, "/");
}

function resolveStandaloneNewsletterApiBasePath() {
  const basename = resolveMountedBasename("newsletter");
  if (basename === "/") {
    return "/api";
  }
  return `${basename}/api`.replace(/\/{2,}/g, "/");
}

const NEWSLETTER_STANDALONE_ROUTES: NewsletterRouteConfig = {
  listPath: "/",
  apiBasePath: resolveStandaloneNewsletterApiBasePath(),
  contactsGroupsApiPath: "/apps/contacts/api/newsletter/groups",
  contactsResolveApiPath: "/apps/contacts/api/newsletter/resolve",
  allowStandaloneContactsIntegration: false,
};

const NEWSLETTER_HOSTED_ROUTES: NewsletterRouteConfig = {
  listPath: "/newsletter",
  apiBasePath: "/api/newsletter",
  contactsGroupsApiPath: "/api/groups",
  contactsResolveApiPath: "/api/newsletter/resolve",
  allowStandaloneContactsIntegration: true,
};

const NewsletterRouteConfigContext = createContext<NewsletterRouteConfig | null>(null);

function useNewsletterRouteConfig() {
  const value = useContext(NewsletterRouteConfigContext);
  return value || NEWSLETTER_STANDALONE_ROUTES;
}

function withNewsletterRouteConfig(element: ReactNode, routes: NewsletterRouteConfig) {
  return <NewsletterRouteConfigContext.Provider value={routes}>{element}</NewsletterRouteConfigContext.Provider>;
}

function mergeUniqueEmails(...lists: string[][]) {
  const merged: string[] = [];
  const seen = new Set<string>();

  lists.forEach((list) => {
    list.forEach((item) => {
      const normalized = String(item || "").trim().toLowerCase();
      if (!normalized || seen.has(normalized)) {
        return;
      }
      seen.add(normalized);
      merged.push(normalized);
    });
  });

  return merged;
}

class NewsletterErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="page-shell">
          <section className="panel panel-primary">
            <p className="section-kicker">Erreur front</p>
            <h2>Le front newsletter a rencontré une erreur.</h2>
            <p className="muted">{this.state.error.message}</p>
          </section>
        </div>
      );
    }

    return this.props.children;
  }
}

function ModalShell({
  isOpen,
  title,
  onClose,
  chromeMode = "default",
  children,
}: {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  chromeMode?: "default" | "minimal";
  children: ReactNode;
}) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="newsletter-modal-backdrop" onClick={onClose}>
      <div
        className={`newsletter-modal${chromeMode === "minimal" ? " is-minimal" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        {chromeMode === "minimal" ? (
          <button
            type="button"
            className="icon-button newsletter-modal-close-floating"
            aria-label="Fermer"
            title="Fermer"
            onClick={onClose}
          >
            <Icon iconId="icon-x" />
          </button>
        ) : (
          <div className="newsletter-modal-head">
            <strong className="newsletter-modal-title">{title}</strong>
            <button type="button" className="icon-button" aria-label="Fermer" title="Fermer" onClick={onClose}>
              <Icon iconId="icon-x" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function NewsletterPage({ settingsOnly = false }: { settingsOnly?: boolean }) {
  const routeConfig = useNewsletterRouteConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const isHostedNewsletter = routeConfig.apiBasePath === NEWSLETTER_HOSTED_ROUTES.apiBasePath;
  const hostedPublicBaseUrl =
    isHostedNewsletter && typeof window !== "undefined" ? window.location.origin.replace(/\/+$/, "") : "";

  function createPreferredAssistantDraft(overrides: Partial<AssistantDraftState> = {}) {
    const storedTheme = readAssistantThemeDefaults();
    const storedLogo = readAssistantLogoDefaults();
    const fallbackTheme = createDefaultAssistantTheme();
    const shouldReuseStoredTheme =
      !overrides.theme ||
      (overrides.theme.style === fallbackTheme.style &&
        overrides.theme.primary_color === fallbackTheme.primary_color &&
        overrides.theme.accent_color === fallbackTheme.accent_color);

    return createEmptyAssistantDraft({
      ...overrides,
      theme: shouldReuseStoredTheme ? storedTheme : overrides.theme,
      header_logo_url: overrides.header_logo_url ?? storedLogo.header_logo_url,
      header_logo_alt: overrides.header_logo_alt ?? storedLogo.header_logo_alt,
    });
  }

  const queryClient = useQueryClient();
  const newsletterApiUrl = (path: string) => buildApiUrl(routeConfig.apiBasePath, path);
  const persistedViewState = readSessionJson<NewsletterViewState>(NEWSLETTER_VIEW_STATE_STORAGE_KEY, {
    activeTab: "drafts",
    selectedDraftId: null,
    selectedCampaignId: null,
    selectedDraftIds: [],
    editingDraftId: null,
    isComposerOpen: false,
    composerStep: "naming",
    composerContentMode: "assistant",
  });
  const initialViewState: NewsletterViewState = {
    ...persistedViewState,
    composerStep: "naming",
    ...(settingsOnly ? { activeTab: "settings" as const, isComposerOpen: false, editingDraftId: null } : {}),
    ...(!settingsOnly && isHostedNewsletter && persistedViewState.activeTab === "settings" ? { activeTab: "drafts" as const } : {}),
  };
  const [activeTab, setActiveTab] = useState<TabId>(initialViewState.activeTab);
  function openSettings() {
    if (isHostedNewsletter) navigate("/settings/newsletter");
    else setActiveTab("settings");
  }

  useEffect(() => {
    if (!settingsOnly && isHostedNewsletter && new URLSearchParams(location.search).get("tab") === "settings") {
      navigate("/settings/newsletter", { replace: true });
    }
  }, [isHostedNewsletter, settingsOnly, location.search, navigate]);

  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(initialViewState.selectedDraftId);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(initialViewState.selectedCampaignId);
  const [selectedDraftIds, setSelectedDraftIds] = useState<number[]>(initialViewState.selectedDraftIds);
  const [editingDraftId, setEditingDraftId] = useState<number | null>(initialViewState.editingDraftId);
  const [isComposerOpen, setIsComposerOpen] = useState(initialViewState.isComposerOpen);
  const [composerStep, setComposerStep] = useState<ComposerStepId>(initialViewState.composerStep);
  const [composerContentMode, setComposerContentMode] = useState<ComposerContentMode>(initialViewState.composerContentMode);
  const [draftSearch, setDraftSearch] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignStatusFilter, setCampaignStatusFilter] = useState<CampaignStatusFilter>("all");
  const [isCampaignFiltersOpen, setIsCampaignFiltersOpen] = useState(false);
  const [isEventNewsletterModalOpen, setIsEventNewsletterModalOpen] = useState(false);
  const [eventNewsletterPeriod, setEventNewsletterPeriod] = useState({ startDate: "", endDate: "" });
  const [campaignForm, setCampaignForm] = useState<CampaignFormState>(createEmptyCampaignForm());
  const [assistantDraft, setAssistantDraft] = useState<AssistantDraftState>(createPreferredAssistantDraft());
  const [assistantRenderedHtml, setAssistantRenderedHtml] = useState("");
  const [isAssistantRenderPending, setIsAssistantRenderPending] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveState>("idle");
  const [selectedAssistantSectionId, setSelectedAssistantSectionId] = useState<string | null>(null);
  const [isClassicSwitchDialogOpen, setIsClassicSwitchDialogOpen] = useState(false);
  const [isClassicSwitchPending, setIsClassicSwitchPending] = useState(false);
  const [isTestEmailDialogOpen, setIsTestEmailDialogOpen] = useState(false);
  const [isTrackingRecommendationDialogOpen, setIsTrackingRecommendationDialogOpen] = useState(false);
  const [isProtectionConfirmationDialogOpen, setIsProtectionConfirmationDialogOpen] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [testEmailState, setTestEmailState] = useState<"idle" | "sending" | "sent">("idle");
  const [testEmailStatus, setTestEmailStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [recipientEntryValue, setRecipientEntryValue] = useState("");
  const [recipientRejectedTokens, setRecipientRejectedTokens] = useState<string[]>([]);
  const [isRecipientFieldFocused, setIsRecipientFieldFocused] = useState(false);
  const [settingsForm, setSettingsForm] = useState<NewsletterSettings>(createEmptySettingsForm());
  const [emailOnboardingRecommendation, setEmailOnboardingRecommendation] = useState<EmailOnboardingRecommendation | null>(
    null
  );
  const [emailProviderMode, setEmailProviderMode] = useState<EmailProviderPresetId>("custom");
  const [emailConnectionStep, setEmailConnectionStep] = useState<EmailConnectionStep>("onboarding_previous_route");
  const [showAdvancedEmailSettings, setShowAdvancedEmailSettings] = useState(false);
  const [isEmailConnectionWizardOpen, setIsEmailConnectionWizardOpen] = useState(false);
  const [emailOnboardingRecommendationStatus, setEmailOnboardingRecommendationStatus] = useState<StatusState>({
    message: "",
    tone: "neutral",
  });
  const [emailConnectionVerifyState, setEmailConnectionVerifyState] = useState<"idle" | "verifying" | "verified">("idle");
  const [emailConnectionVerifyFailureCount, setEmailConnectionVerifyFailureCount] = useState(0);
  const [isEmailOauthPending, setIsEmailOauthPending] = useState(false);
  const [emailConnectionVerifyStatus, setEmailConnectionVerifyStatus] = useState<StatusState>({
    message: "",
    tone: "neutral",
  });
  const [sesDnsCopyStatus, setSesDnsCopyStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [campaignFieldErrors, setCampaignFieldErrors] = useState<string[]>([]);
  const [settingsFieldErrors, setSettingsFieldErrors] = useState<string[]>([]);
  const [campaignStatus, setCampaignStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [defaultDesignStatus, setDefaultDesignStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [defaultDesignMenuCampaignId, setDefaultDesignMenuCampaignId] = useState<number | null>(null);
  const [settingsStatus, setSettingsStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [settingsSaveState, setSettingsSaveState] = useState<"idle" | "saving" | "success">("idle");
  const [isEmbeddedInWorkspace, setIsEmbeddedInWorkspace] = useState(false);
  const [mediaTarget, setMediaTarget] = useState<NewsletterMediaTarget | null>(null);
  const deferredDraftSearch = useDeferredValue(draftSearch);
  const deferredCampaignSearch = useDeferredValue(campaignSearch);
  const hasContactsIntegration = isEmbeddedInWorkspace || routeConfig.allowStandaloneContactsIntegration;
  const settingsHydratedRef = useRef(false);
  const settingsSuccessTimerRef = useRef<number | null>(null);
  const selectedContactGroupIdsRef = useRef<number[]>([]);
  const editingDraftIdRef = useRef<number | null>(initialViewState.editingDraftId);
  const recipientInputRef = useRef<HTMLInputElement | null>(null);
  const richTextImageInsertRef = useRef<((url: string, altText?: string) => void) | null>(null);
  const lastPersistedDraftSignatureRef = useRef("");
  const latestComposerDraftSignatureRef = useRef("");
  const autoSavePromiseRef = useRef<Promise<boolean> | null>(null);
  const lastAutoSaveAtRef = useRef(Date.now());
  const shouldHydrateAutoSaveBaselineRef = useRef(true);
  const skipAssistantDefaultsPersistRef = useRef<{
    theme: AssistantDraftState["theme"] | null;
    logo: Pick<AssistantDraftState, "header_logo_url" | "header_logo_alt"> | null;
  }>({ theme: null, logo: null });
  const emailOauthPopupRef = useRef<Window | null>(null);
  const emailWizardModalBodyRef = useRef<HTMLDivElement | null>(null);
  const sendingProtectionRef = useRef<HTMLDivElement | null>(null);
  const emailWizardModalScrollTopRef = useRef(0);
  const trackingRecommendationSeenRef = useRef(false);
  const incomingComposePendingRef = useRef(false);
  const incomingComposeGenerationRef = useRef(0);
  useEffect(()=>()=>{incomingComposeGenerationRef.current++;},[]);

  const metaQuery = useQuery({
    queryKey: ["newsletter-meta"],
    queryFn: () => fetchJson<NewsletterMeta>(newsletterApiUrl("meta")),
  });
  const settingsQuery = useQuery({
    queryKey: ["newsletter-settings"],
    queryFn: () => fetchJson<NewsletterSettings>(newsletterApiUrl("settings")),
  });
  const settingsBaseline = settingsQuery.data ? {
    ...normalizeSettingsForm(settingsQuery.data),
    ...(isHostedNewsletter && hostedPublicBaseUrl ? {public_base_url:hostedPublicBaseUrl} : {}),
  } : null;
  const settingsAreDirty = settingsHydratedRef.current && !!settingsBaseline && JSON.stringify(settingsForm)!==JSON.stringify(settingsBaseline);
  const settingsBlocker = useBlocker(({currentLocation,nextLocation})=>settingsOnly && (settingsAreDirty || settingsSaveState === "saving") && currentLocation.pathname!==nextLocation.pathname);
  useEffect(()=>{
    if(settingsBlocker.state!=="blocked" || settingsSaveState === "saving") return;
    if(!settingsAreDirty || window.confirm("Quitter sans enregistrer les modifications de l’expéditeur ?")){clearSettingsDraft();settingsBlocker.proceed();}
    else settingsBlocker.reset();
  },[settingsBlocker,settingsAreDirty,settingsSaveState]);
  useEffect(()=>{
    const onClose=(event:BeforeUnloadEvent)=>{if(settingsOnly && settingsAreDirty){event.preventDefault();event.returnValue="";}};
    window.addEventListener("beforeunload",onClose);return()=>window.removeEventListener("beforeunload",onClose);
  },[settingsOnly,settingsAreDirty]);
  function cancelSenderSettings(){
    if(!settingsBaseline)return;
    setSettingsForm(settingsBaseline);clearSettingsDraft();setSettingsFieldErrors([]);setSettingsStatus({message:"",tone:"neutral"});
  }
  const engagementQuery = useQuery({
    queryKey: ["newsletter-engagement",30],
    queryFn: () => fetchJson<EngagementSettings>(newsletterApiUrl("engagement?period_days=30")),
    enabled: isHostedNewsletter, retry: false,
  });
  const draftsQuery = useQuery({
    queryKey: ["newsletter-drafts"],
    queryFn: () => fetchJson<NewsletterCampaign[]>(newsletterApiUrl("drafts")),
    refetchInterval: 15000,
  });
  const campaignsQuery = useQuery({
    queryKey: ["newsletter-campaigns"],
    queryFn: () => fetchJson<NewsletterCampaign[]>(newsletterApiUrl("campaigns")),
    refetchInterval: 15000,
  });
  const defaultDesignQuery = useQuery({
    queryKey: ["newsletter-default-design"],
    queryFn: () =>
      fetchJson<{ default_design?: NewsletterDefaultDesignSnapshot | Record<string, never> }>(
        newsletterApiUrl("default-design")
      ),
    retry: false,
  });
  const eventNewsletterEventsQuery = useQuery({
    queryKey: ["event-planner-events"],
    queryFn: () => fetchJson<EventRecord[]>("/api/events"),
    enabled: isHostedNewsletter,
    retry: false,
  });
  const detailQuery = useQuery({
    queryKey: ["newsletter-campaign-detail", selectedCampaignId],
    queryFn: () => fetchJson<NewsletterCampaignDetail>(newsletterApiUrl(`campaigns/${selectedCampaignId}?schema_version=3`)),
    enabled: selectedCampaignId !== null,
    refetchInterval: selectedCampaignId ? 15000 : false,
  });
  const contactsGroupsQuery = useQuery({
    queryKey: ["newsletter-contacts-groups", routeConfig.contactsGroupsApiPath, hasContactsIntegration],
    queryFn: () => fetchJson<ContactsNewsletterGroup[]>(routeConfig.contactsGroupsApiPath),
    enabled: hasContactsIntegration,
    retry: 0,
  });
  const groupResolutionQuery = useQuery({
    queryKey: ["newsletter-contacts-resolution", campaignForm.contact_group_ids],
    queryFn: () =>
      fetchJson<ContactsNewsletterResolution>(routeConfig.contactsResolveApiPath, {
        method: "POST",
        body: JSON.stringify({ group_ids: campaignForm.contact_group_ids }),
      }),
    enabled: hasContactsIntegration && campaignForm.contact_group_ids.length > 0,
    retry: 0,
  });

  const drafts = draftsQuery.data || [];
  const campaigns = campaignsQuery.data || [];
  const eventNewsletterEvents = eventNewsletterEventsQuery.data || [];
  const defaultDesign =
    defaultDesignQuery.data?.default_design && "schema_version" in defaultDesignQuery.data.default_design
      ? (defaultDesignQuery.data.default_design as NewsletterDefaultDesignSnapshot)
      : null;
  function createAssistantDraftForNewCampaign(overrides: Partial<AssistantDraftState> = {}) {
    if (defaultDesign && Object.keys(overrides).length === 0) {
      return createAssistantDraftFromDefaultDesign(defaultDesign);
    }
    return createPreferredAssistantDraft(overrides);
  }
  const filteredDrafts = useMemo(() => {
    const normalizedSearch = deferredDraftSearch.trim().toLowerCase();
    if (!normalizedSearch) {
      return drafts;
    }

    return drafts.filter((draft) =>
      [draft.name, draft.subject, ...(draft.contact_group_labels || [])]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch))
    );
  }, [deferredDraftSearch, drafts]);
  const filteredCampaigns = useMemo(() => {
    const normalizedSearch = deferredCampaignSearch.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        [campaign.name, campaign.subject, ...(campaign.contact_group_labels || [])]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));
      const matchesStatus = campaignStatusFilter === "all" || normalizeCampaignStatus(campaign.status) === campaignStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [campaignStatusFilter, campaigns, deferredCampaignSearch]);
  const selectedDraftCount = selectedDraftIds.length;
  const filteredDraftIds = useMemo(() => filteredDrafts.map((draft) => draft.id), [filteredDrafts]);
  const areAllDraftsSelected =
    filteredDraftIds.length > 0 && filteredDraftIds.every((draftId) => selectedDraftIds.includes(draftId));
  const activeDraftFiltersCount = useMemo(() => [draftSearch.trim().length > 0].filter(Boolean).length, [draftSearch]);
  const activeCampaignFiltersCount = useMemo(
    () => [campaignSearch.trim().length > 0, campaignStatusFilter !== "all"].filter(Boolean).length,
    [campaignSearch, campaignStatusFilter]
  );
  const recipientAnalysis = useMemo(
    () => analyzeRecipients(campaignForm.raw_recipients),
    [campaignForm.raw_recipients]
  );
  const availableContactGroups = contactsGroupsQuery.data || [];
  const publicUrlNotice = useMemo(() => {
    const notice = buildPublicUrlNotice(
      campaignForm,
      isHostedNewsletter && hostedPublicBaseUrl ? { ...settingsForm, public_base_url: hostedPublicBaseUrl } : settingsForm
    );

    if (isHostedNewsletter && notice && !notice.isWarning) {
      return null;
    }

    return notice;
  }, [campaignForm, hostedPublicBaseUrl, isHostedNewsletter, settingsForm]);
  const selectedContactGroups = useMemo(() => {
    const liveGroupsById = new Map(availableContactGroups.map((group) => [group.id, group]));
    return campaignForm.contact_group_ids.map((groupId, index) => {
      const liveGroup = liveGroupsById.get(groupId);
      if (liveGroup) {
        return liveGroup;
      }
      return {
        id: groupId,
        name: campaignForm.contact_group_labels[index] || `Groupe ${groupId}`,
        color: "#d5dced",
        contact_count: 0,
      };
    });
  }, [availableContactGroups, campaignForm.contact_group_ids, campaignForm.contact_group_labels]);
  const visibleManualRecipients = useMemo(() => recipientAnalysis.valid.slice(0, 8), [recipientAnalysis.valid]);
  const hiddenManualRecipientsCount = Math.max(0, recipientAnalysis.valid.length - visibleManualRecipients.length);
  const invalidRecipientTokens = recipientAnalysis.invalid.length > 0 ? recipientAnalysis.invalid : recipientRejectedTokens;
  const recipientSearchValue = recipientEntryValue.trim().toLowerCase();
  const exactRecipientGroupMatch = useMemo(
    () =>
      availableContactGroups.find(
        (group) =>
          !campaignForm.contact_group_ids.includes(group.id) &&
          group.name.trim().toLowerCase() === recipientSearchValue
      ) || null,
    [availableContactGroups, campaignForm.contact_group_ids, recipientSearchValue]
  );
  const recipientGroupSuggestions = useMemo(() => {
    if (!hasContactsIntegration) {
      return [];
    }

    return availableContactGroups
      .filter((group) => !campaignForm.contact_group_ids.includes(group.id))
      .filter((group) => !recipientSearchValue || group.name.toLowerCase().includes(recipientSearchValue))
      .slice(0, 6);
  }, [availableContactGroups, campaignForm.contact_group_ids, hasContactsIntegration, recipientSearchValue]);
  const totalAudienceRecipients = useMemo(
    () => mergeUniqueEmails(recipientAnalysis.valid, groupResolutionQuery.data?.recipients || []),
    [groupResolutionQuery.data, recipientAnalysis.valid]
  );
  useEffect(() => {
    selectedContactGroupIdsRef.current = campaignForm.contact_group_ids;
  }, [campaignForm.contact_group_ids]);

  useEffect(() => {
    if (assistantDraft.sections.length === 0) {
      if (selectedAssistantSectionId !== null) {
        setSelectedAssistantSectionId(null);
      }
      return;
    }

    if (!selectedAssistantSectionId || !assistantDraft.sections.some((section) => section.id === selectedAssistantSectionId)) {
      setSelectedAssistantSectionId(assistantDraft.sections[0].id);
    }
  }, [assistantDraft.sections, selectedAssistantSectionId]);

  useEffect(() => {
    if (skipAssistantDefaultsPersistRef.current.theme === assistantDraft.theme) {
      skipAssistantDefaultsPersistRef.current.theme = null;
      return;
    }
    persistAssistantThemeDefaults(assistantDraft.theme);
  }, [assistantDraft.theme]);

  useEffect(() => {
    const skippedLogo = skipAssistantDefaultsPersistRef.current.logo;
    if (
      skippedLogo &&
      skippedLogo.header_logo_url === assistantDraft.header_logo_url &&
      skippedLogo.header_logo_alt === assistantDraft.header_logo_alt
    ) {
      skipAssistantDefaultsPersistRef.current.logo = null;
      return;
    }
    persistAssistantLogoDefaults({
      header_logo_url: assistantDraft.header_logo_url,
      header_logo_alt: assistantDraft.header_logo_alt,
    });
  }, [assistantDraft.header_logo_alt, assistantDraft.header_logo_url]);

  function resetAutoSaveTracking() {
    shouldHydrateAutoSaveBaselineRef.current = true;
    lastAutoSaveAtRef.current = Date.now();
    setAutoSaveState("idle");
  }

  function updateEditingDraftId(nextDraftId: number | null) {
    editingDraftIdRef.current = nextDraftId;
    setEditingDraftId(nextDraftId);
  }

  function markDraftAsPersisted(signature: string) {
    lastPersistedDraftSignatureRef.current = signature;
    lastAutoSaveAtRef.current = Date.now();
    shouldHydrateAutoSaveBaselineRef.current = false;
  }

  function ensureDraftName(form: CampaignFormState) {
    const trimmedName = form.name.trim();
    if (trimmedName) {
      return form;
    }

    const generatedName = buildGeneratedDraftName();
    setCampaignForm((current) => ({ ...current, name: generatedName }));
    return {
      ...form,
      name: generatedName,
    };
  }

  function syncComposerContentSource(form: CampaignFormState, fallbackMode: ComposerContentMode = "richText") {
    const assistantContent = parseAssistantDraftFromHtml(form.html_body);
    if (assistantContent) {
      setAssistantDraft(assistantContent);
      setSelectedAssistantSectionId(assistantContent.sections[0]?.id || null);
      setAssistantRenderedHtml(form.html_body);
      setIsAssistantRenderPending(false);
      setComposerContentMode("assistant");
      return;
    }

    const nextAssistantDraft = createPreferredAssistantDraft({
      intro_kicker: "",
      intro_title: "",
      intro_body: "",
      intro_image_url: "",
      intro_image_alt: "",
    });
    setAssistantDraft(nextAssistantDraft);
    setSelectedAssistantSectionId(nextAssistantDraft.sections[0]?.id || null);
    setAssistantRenderedHtml("");
    setIsAssistantRenderPending(false);
    setComposerContentMode(fallbackMode);
  }

  function updateAssistantSection(sectionId: string, patch: Partial<AssistantDraftState["sections"][number]>) {
    setAssistantDraft((current) => ({
      ...current,
      sections: current.sections.map((section) => (section.id === sectionId ? { ...section, ...patch } : section)),
    }));
  }

  function addAssistantSection(insertionIndex: number) {
    const nextSection = createEmptyAssistantSection({
      media_layout: resolveDefaultAssistantSectionLayout(assistantDraft.theme),
    });
    setAssistantDraft((current) => ({
      ...current,
      sections: [
        ...current.sections.slice(0, insertionIndex),
        nextSection,
        ...current.sections.slice(insertionIndex),
      ],
    }));
    setSelectedAssistantSectionId(nextSection.id);
  }

  function duplicateAssistantSection(sectionId: string) {
    setAssistantDraft((current) => {
      const sourceSection = current.sections.find((section) => section.id === sectionId);
      if (!sourceSection) {
        return current;
      }

      const duplicatedSection = createEmptyAssistantSection({
        title: sourceSection.title,
        body: sourceSection.body,
        body_html: sourceSection.body_html,
        image_url: sourceSection.image_url,
        image_alt: sourceSection.image_alt,
        button_label: sourceSection.button_label,
        button_url: sourceSection.button_url,
        media_layout: sourceSection.media_layout,
        media_width_ratio: sourceSection.media_width_ratio,
        media_height: sourceSection.media_height,
      });
      setSelectedAssistantSectionId(duplicatedSection.id);

      return {
        ...current,
        sections: current.sections.flatMap((section) =>
          section.id === sectionId ? [section, duplicatedSection] : [section]
        ),
      };
    });
  }

  function removeAssistantSection(sectionId: string) {
    setAssistantDraft((current) => {
      if (current.sections.length === 1) {
        return current;
      }

      const nextSections = current.sections.filter((section) => section.id !== sectionId);
      if (selectedAssistantSectionId === sectionId) {
        setSelectedAssistantSectionId(nextSections[0]?.id || null);
      }

      return {
        ...current,
        sections: nextSections,
      };
    });
  }

  function moveAssistantSection(sectionId: string, direction: "up" | "down") {
    setAssistantDraft((current) => {
      const sectionIndex = current.sections.findIndex((section) => section.id === sectionId);
      if (sectionIndex < 0) {
        return current;
      }

      const targetIndex = direction === "up" ? sectionIndex - 1 : sectionIndex + 1;
      if (targetIndex < 0 || targetIndex >= current.sections.length) {
        return current;
      }

      const nextSections = [...current.sections];
      const [movedSection] = nextSections.splice(sectionIndex, 1);
      nextSections.splice(targetIndex, 0, movedSection);
      setSelectedAssistantSectionId(movedSection.id);

      return {
        ...current,
        sections: nextSections,
      };
    });
  }

  async function renderAssistantSnapshotHtml() {
    setIsAssistantRenderPending(true);
    try {
      const nextHtml = await buildAssistantHtml(assistantDraft);
      setAssistantRenderedHtml(nextHtml);
      setCampaignForm((current) => (current.html_body === nextHtml ? current : { ...current, html_body: nextHtml }));
      return nextHtml;
    } finally {
      setIsAssistantRenderPending(false);
    }
  }

  function applySelectedContactGroups(nextGroupIds: number[], fallbackLabels: string[] = []) {
    const liveGroupsById = new Map(availableContactGroups.map((group) => [group.id, group]));
    const uniqueGroupIds = Array.from(new Set(nextGroupIds.filter((groupId) => Number.isFinite(groupId) && groupId > 0)));

    setCampaignForm((current) => ({
      ...current,
      contact_group_ids: uniqueGroupIds,
      contact_group_labels: uniqueGroupIds.map((groupId, index) => {
        const liveGroup = liveGroupsById.get(groupId);
        if (liveGroup) {
          return liveGroup.name;
        }
        const currentIndex = current.contact_group_ids.indexOf(groupId);
        if (currentIndex >= 0 && current.contact_group_labels[currentIndex]) {
          return current.contact_group_labels[currentIndex];
        }
        return fallbackLabels[index] || `Groupe ${groupId}`;
      }),
    }));
  }

  function syncManualRecipients(nextEmails: string[]) {
    setCampaignForm((current) => ({
      ...current,
      raw_recipients: mergeUniqueEmails(nextEmails).join("\n"),
    }));
  }

  function removeManualRecipient(email: string) {
    clearCampaignFieldError("campaign_recipients");
    syncManualRecipients(recipientAnalysis.valid.filter((item) => item !== email));
  }

  function removeSelectedContactGroup(groupId: number) {
    clearCampaignFieldError("campaign_recipients");
    applySelectedContactGroups(campaignForm.contact_group_ids.filter((value) => value !== groupId));
  }

  function focusRecipientInput() {
    window.setTimeout(() => {
      recipientInputRef.current?.focus();
    }, 0);
  }

  function commitRecipientEntry(value = recipientEntryValue) {
    const entry = value.trim();
    const analysis = analyzeRecipients(value);
    const matchingGroup =
      analysis.valid.length === 0
        ? availableContactGroups.find(
            (group) =>
              !campaignForm.contact_group_ids.includes(group.id) &&
              group.name.trim().toLowerCase() === entry.toLowerCase()
          ) || (analysis.valid.length === 0 && recipientGroupSuggestions.length === 1 ? recipientGroupSuggestions[0] : null)
        : null;

    if (!entry) {
      return { nextEmails: recipientAnalysis.valid, nextGroupIds: campaignForm.contact_group_ids };
    }

    let nextEmails = recipientAnalysis.valid;
    let nextGroupIds = campaignForm.contact_group_ids;

    if (analysis.valid.length > 0) {
      nextEmails = mergeUniqueEmails(recipientAnalysis.valid, analysis.valid);
      syncManualRecipients(nextEmails);
    }

    if (matchingGroup) {
      nextGroupIds = Array.from(new Set([...campaignForm.contact_group_ids, matchingGroup.id]));
      applySelectedContactGroups(nextGroupIds);
    }

    if (analysis.valid.length > 0 || matchingGroup) {
      clearCampaignFieldError("campaign_recipients");
      setRecipientEntryValue("");
      setRecipientRejectedTokens(analysis.invalid);
      if (analysis.invalid.length > 0) {
        setCampaignStatus({
          message: `${analysis.valid.length} adresse${analysis.valid.length > 1 ? "s" : ""} ajoutée${analysis.valid.length > 1 ? "s" : ""}. ${analysis.invalid.length} entrée${analysis.invalid.length > 1 ? "s" : ""} ignorée${analysis.invalid.length > 1 ? "s" : ""}.`,
          tone: "error",
        });
      }
      focusRecipientInput();
      return { nextEmails, nextGroupIds };
    }

    if (entry.includes("@") || /[\n,;]+/.test(entry)) {
      setCampaignFieldErrors((current) =>
        current.includes("campaign_recipients") ? current : [...current, "campaign_recipients"]
      );
      setRecipientRejectedTokens(analysis.invalid.length > 0 ? analysis.invalid : [entry]);
      setCampaignStatus({ message: "Aucune adresse email valide détectée dans cette saisie.", tone: "error" });
    }

    return { nextEmails, nextGroupIds };
  }

  function clearInvalidRecipients() {
    syncManualRecipients(recipientAnalysis.valid);
    setRecipientRejectedTokens([]);
    clearCampaignFieldError("campaign_recipients");
  }

  function validateContentStep() {
    const nextErrors: string[] = [];
    const hasEditorContent =
      composerContentMode === "assistant"
        ? assistantDraftHasMeaningfulContent(assistantDraft)
        : editorHtmlHasContent(campaignForm.html_body);

    if (!hasEditorContent) {
      nextErrors.push("campaign_editor");
    }

    if (nextErrors.length > 0) {
      setCampaignFieldErrors(nextErrors);
      setCampaignStatus({ message: "Le contenu du mail est obligatoire.", tone: "error" });
      return false;
    }

    return true;
  }

  function validateNamingStep() {
    if (campaignForm.name.trim()) {
      return true;
    }

    setCampaignFieldErrors(["campaign_name"]);
    setCampaignStatus({
      message: "Ajoutez un nom pour retrouver ce brouillon.",
      tone: "error",
    });
    return false;
  }

  function validateAudienceStep() {
    const { nextEmails, nextGroupIds } = commitRecipientEntry();
    const nextErrors: string[] = [];

    if (!campaignForm.subject.trim()) {
      nextErrors.push("campaign_subject");
    }
    if (nextEmails.length === 0 && nextGroupIds.length === 0) {
      nextErrors.push("campaign_recipients");
    }
    if (!campaignForm.scheduled_at.trim()) {
      nextErrors.push("campaign_scheduled_at");
    } else if (scheduledAtIsInPast(campaignForm.scheduled_at)) {
      nextErrors.push("campaign_scheduled_at");
    }

    if (nextErrors.length > 0) {
      setCampaignFieldErrors((current) => Array.from(new Set([...current, ...nextErrors])));
      setCampaignStatus({
        message: !campaignForm.subject.trim()
          ? "L'objet est obligatoire."
          : nextEmails.length === 0 && nextGroupIds.length === 0
            ? "Ajoutez au moins une adresse email ou un groupe de contacts."
            : !campaignForm.scheduled_at.trim()
              ? "La date et l'heure d'envoi sont obligatoires."
              : "La date et l'heure d'envoi doivent être dans le futur.",
        tone: "error",
      });
      return false;
    }

    return true;
  }

  async function goToComposerStep(step: ComposerStepId) {
    if (step === composerStep) {
      return;
    }

    if (step === "naming") {
      setComposerStep(step);
      return;
    }

    if (composerStep === "naming" && !validateNamingStep()) {
      return;
    }

    if (step === "content") {
      setComposerStep(step);
      return;
    }

    if (step === "audience") {
      if (validateContentStep()) {
        setComposerStep(step);
      }
      return;
    }

    if (validateContentStep() && validateAudienceStep()) {
      if (step === "review" && composerContentMode === "assistant") {
        await renderAssistantSnapshotHtml();
      }
      setComposerStep(step);
    }
  }

  function buildCampaignFormSnapshot() {
    const entry = recipientEntryValue.trim();
    const analysis = analyzeRecipients(recipientEntryValue);
    const matchingGroup =
      entry && analysis.valid.length === 0
        ? availableContactGroups.find(
            (group) =>
              !campaignForm.contact_group_ids.includes(group.id) &&
              group.name.trim().toLowerCase() === entry.toLowerCase()
          ) || (recipientGroupSuggestions.length === 1 ? recipientGroupSuggestions[0] : null)
        : null;
    const nextEmails = analysis.valid.length > 0
      ? mergeUniqueEmails(recipientAnalysis.valid, analysis.valid)
      : recipientAnalysis.valid;
    const nextGroupIds = matchingGroup
      ? Array.from(new Set([...campaignForm.contact_group_ids, matchingGroup.id]))
      : campaignForm.contact_group_ids;

    return {
      ...campaignForm,
      raw_recipients: nextEmails.join("\n"),
      contact_group_ids: nextGroupIds,
      contact_group_labels: nextGroupIds.map((groupId) => {
        const liveGroup = availableContactGroups.find((group) => group.id === groupId);
        if (liveGroup) {
          return liveGroup.name;
        }
        const currentIndex = campaignForm.contact_group_ids.indexOf(groupId);
        if (currentIndex >= 0 && campaignForm.contact_group_labels[currentIndex]) {
          return campaignForm.contact_group_labels[currentIndex];
        }
        return `Groupe ${groupId}`;
      }),
    };
  }

  function syncComposerSnapshot(form: CampaignFormState) {
    const groupIdsChanged = form.contact_group_ids.join(",") !== campaignForm.contact_group_ids.join(",");
    const groupLabelsChanged = form.contact_group_labels.join("||") !== campaignForm.contact_group_labels.join("||");
    const recipientsChanged = form.raw_recipients !== campaignForm.raw_recipients;

    if (!groupIdsChanged && !groupLabelsChanged && !recipientsChanged) {
      return;
    }

    setCampaignForm(form);
    setRecipientEntryValue("");
  }

  function upsertDraftInCache(draft: NewsletterCampaign) {
    queryClient.setQueryData<NewsletterCampaign[]>(["newsletter-drafts"], (current) => {
      const draftList = Array.isArray(current) ? current : [];
      const nextDrafts = draftList.some((item) => item.id === draft.id)
        ? draftList.map((item) => (item.id === draft.id ? draft : item))
        : [draft, ...draftList];

      return nextDrafts.sort((left, right) => {
        const leftDate = left.updated_at ? new Date(left.updated_at).getTime() : 0;
        const rightDate = right.updated_at ? new Date(right.updated_at).getTime() : 0;
        if (leftDate !== rightDate) {
          return rightDate - leftDate;
        }
        return right.id - left.id;
      });
    });
  }

  const composerFormSnapshot = useMemo(
    () => ({ ...buildCampaignFormSnapshot(), unsubscribe_enabled: composerContentMode === "assistant" || campaignForm.unsubscribe_enabled }),
    [availableContactGroups, campaignForm, composerContentMode, recipientAnalysis.valid, recipientEntryValue, recipientGroupSuggestions]
  );
  const composerDraftSignature = useMemo(
    () => buildComposerDraftSignature(composerFormSnapshot, composerContentMode, assistantDraft),
    [assistantDraft, composerContentMode, composerFormSnapshot]
  );
  latestComposerDraftSignatureRef.current = composerDraftSignature;
  const guardrailPayload = useMemo(() => {
    const htmlBody =
      composerContentMode === "assistant" && assistantRenderedHtml ? assistantRenderedHtml : composerFormSnapshot.html_body;
    return {
      ...buildCampaignPayload(
        {
          ...composerFormSnapshot,
          html_body: htmlBody,
        },
        null,
        false
      ),
      resolved_group_recipients: groupResolutionQuery.data?.recipients || [],
      sender_email: settingsForm.smtp_username,
    };
  }, [
    assistantRenderedHtml,
    composerContentMode,
    composerFormSnapshot,
    settingsForm.smtp_username,
    groupResolutionQuery.data?.recipients,
  ]);
  const guardrailsQuery = useQuery({
    queryKey: ["newsletter-guardrails", JSON.stringify(guardrailPayload)],
    queryFn: () =>
      fetchJson<NewsletterProtectionDecision>(newsletterApiUrl("guardrails/evaluate"), {
        method: "POST",
        body: JSON.stringify({...guardrailPayload, schema_version: 2, scope: "campaign"}),
      }).then(protectionView),
    enabled:
      isHostedNewsletter &&
      isComposerOpen &&
      (composerStep === "audience" || composerStep === "review") &&
      !groupResolutionQuery.isLoading &&
      editorHtmlHasContent(String(guardrailPayload.html_body || "")) &&
      Boolean(String(guardrailPayload.subject || "").trim()) &&
      (analyzeRecipients(String(guardrailPayload.raw_recipients || "")).valid.length > 0 ||
        (Array.isArray(guardrailPayload.resolved_group_recipients) &&
          guardrailPayload.resolved_group_recipients.length > 0)),
    staleTime: 15_000,
  });
  const contentGuardrailAlert = useMemo(
    () => buildComposerGuardrailAlert("content", guardrailsQuery.data),
    [guardrailsQuery.data]
  );
  const audienceGuardrailAlert = useMemo(
    () => buildComposerGuardrailAlert("audience", guardrailsQuery.data),
    [guardrailsQuery.data]
  );
  const progressiveChangePanel = useMemo(
    () => buildProgressiveChangePanel(guardrailsQuery.data),
    [guardrailsQuery.data]
  );
  const trackingInterventionCopy = useMemo(
    () => buildTrackingInterventionCopy(guardrailsQuery.data),
    [guardrailsQuery.data]
  );
  const protectionBeforeSendCopy = useMemo(
    () => buildProtectionBeforeSendCopy(guardrailsQuery.data),
    [guardrailsQuery.data]
  );

  async function handleAutoSaveDraft(force = false): Promise<boolean> {
    if (!isComposerOpen || (!force && composerDraftSignature === lastPersistedDraftSignatureRef.current)) {
      return true;
    }

    if (autoSavePromiseRef.current) {
      return autoSavePromiseRef.current;
    }

    const savePromise = (async () => {
      setAutoSaveState("saving");

      try {
        const namedForm = ensureDraftName(composerFormSnapshot);
        let workingForm = namedForm;
        if (composerContentMode === "assistant") {
          const htmlBody = await buildAssistantHtml(assistantDraft);
          setAssistantRenderedHtml(htmlBody);
          setCampaignForm((current) => {
            const nextName = current.name.trim() || namedForm.name;
            if (current.html_body === htmlBody && current.name === nextName) {
              return current;
            }
            return {
              ...current,
              name: nextName,
              html_body: htmlBody,
            };
          });
          workingForm = {
            ...namedForm,
            html_body: htmlBody,
          };
        }

        const signature = buildComposerDraftSignature(workingForm, composerContentMode, assistantDraft);
        const payload = buildCampaignPayload(workingForm, editingDraftIdRef.current, true);
        if (workingForm.contact_group_ids.length > 0 && (groupResolutionQuery.data?.recipients.length || 0) > 0) {
          payload.resolved_group_recipients = groupResolutionQuery.data?.recipients || [];
        }

        const savedDraft = await saveDraftRequest(payload);
        upsertDraftInCache(savedDraft);
        setSelectedDraftId(savedDraft.id);
        updateEditingDraftId(savedDraft.id);
        markDraftAsPersisted(signature);
        setAutoSaveState("saved");
        void draftsQuery.refetch();
        return true;
      } catch {
        lastAutoSaveAtRef.current = Date.now();
        setAutoSaveState("error");
        return false;
      } finally {
        autoSavePromiseRef.current = null;
      }
    })();

    autoSavePromiseRef.current = savePromise;
    return savePromise;
  }

  useEffect(() => {
    if (!isComposerOpen) {
      return;
    }

    if (shouldHydrateAutoSaveBaselineRef.current) {
      markDraftAsPersisted(composerDraftSignature);
      setAutoSaveState("idle");
      return;
    }

    if (composerDraftSignature !== lastPersistedDraftSignatureRef.current) {
      setAutoSaveState((current) => (current === "saving" ? current : "pending"));
      return;
    }

    setAutoSaveState((current) => (current === "saving" ? current : "idle"));
  }, [composerDraftSignature, isComposerOpen]);

  useEffect(() => {
    if (!isComposerOpen || composerDraftSignature === lastPersistedDraftSignatureRef.current || autoSaveState === "saving") {
      return;
    }

    const elapsedSinceLastSave = Date.now() - lastAutoSaveAtRef.current;
    const delay = Math.max(
      250,
      Math.min(AUTO_SAVE_DEBOUNCE_MS, Math.max(250, AUTO_SAVE_INTERVAL_MS - elapsedSinceLastSave))
    );
    const timer = window.setTimeout(() => {
      void handleAutoSaveDraft();
    }, delay);

    return () => {
      window.clearTimeout(timer);
    };
  }, [autoSaveState, composerDraftSignature, handleAutoSaveDraft, isComposerOpen]);

  async function handleCloseComposer() {
    if (latestComposerDraftSignatureRef.current !== lastPersistedDraftSignatureRef.current) {
      const savedCurrentDraft = await handleAutoSaveDraft();
      if (!savedCurrentDraft) {
        return;
      }
    }

    if (latestComposerDraftSignatureRef.current !== lastPersistedDraftSignatureRef.current) {
      const savedLatestDraft = await handleAutoSaveDraft();
      if (!savedLatestDraft || latestComposerDraftSignatureRef.current !== lastPersistedDraftSignatureRef.current) {
        return;
      }
    }

    setIsComposerOpen(false);
  }

  function handleRecipientInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === "," || event.key === ";" || event.key === "Tab") {
      if (!recipientEntryValue.trim()) {
        return;
      }
      event.preventDefault();
      commitRecipientEntry();
    }
  }

  function handleRecipientInputPaste(event: ReactClipboardEvent<HTMLInputElement>) {
    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) {
      return;
    }

    const analysis = analyzeRecipients(pastedText);
    if (analysis.valid.length === 0 && !pastedText.includes("\n") && !pastedText.includes(",") && !pastedText.includes(";")) {
      return;
    }

    event.preventDefault();
    clearCampaignFieldError("campaign_recipients");
    syncManualRecipients(mergeUniqueEmails(recipientAnalysis.valid, analysis.valid));
    setRecipientRejectedTokens(analysis.invalid);
    setRecipientEntryValue("");
    if (analysis.invalid.length > 0) {
      setCampaignStatus({
        message: `${analysis.valid.length} adresse${analysis.valid.length > 1 ? "s" : ""} ajoutée${analysis.valid.length > 1 ? "s" : ""}. ${analysis.invalid.length} entrée${analysis.invalid.length > 1 ? "s" : ""} ignorée${analysis.invalid.length > 1 ? "s" : ""}.`,
        tone: "error",
      });
    }
  }

  useEffect(() => {
    document.title = settingsOnly ? "Relyo · Réglages · Envoi des emails" : "Relyo · Newsletter";
    if (settingsOnly) return;
    const bridge = installEasyAppBridge("newsletter");
    setIsEmbeddedInWorkspace(bridge.isEmbedded());
    const applyIfVisible = () => {
      if (bridge.isEmbedded() && document.visibilityState === "hidden") {
        return;
      }
      void applyIncomingComposeDraft();
    };
    const bootTimer = window.setTimeout(applyIfVisible, 0);
    const handleVisibilityChange = () => applyIfVisible();
    window.addEventListener("focus", applyIfVisible);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const unsubscribe = bridge.onShellEvent((event) => {
      if (event.topic === "easy-shell:context") {
        if (event.payload.activeAppId === "newsletter") {
          if (bridge.isEmbedded() || routeConfig.allowStandaloneContactsIntegration) {
            void queryClient.refetchQueries({ queryKey: ["newsletter-contacts-groups"] });
            if (selectedContactGroupIdsRef.current.length > 0) {
              void queryClient.refetchQueries({ queryKey: ["newsletter-contacts-resolution"] });
            }
          }
          applyIfVisible();
        }
        return;
      }

      if (event.topic === "contacts.groups.updated") {
        if (!bridge.isEmbedded() && !routeConfig.allowStandaloneContactsIntegration) {
          return;
        }
        void queryClient.refetchQueries({ queryKey: ["newsletter-contacts-groups"] });
        if (selectedContactGroupIdsRef.current.length > 0) {
          void queryClient.refetchQueries({ queryKey: ["newsletter-contacts-resolution"] });
        }
        return;
      }

      if (event.topic === "newsletter.groups.selected") {
        const incomingGroupIds = Array.isArray(event.payload?.groupIds)
          ? event.payload.groupIds
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value) && value > 0)
          : [];
        const incomingGroupNames = Array.isArray(event.payload?.groupNames)
          ? event.payload.groupNames.map((value) => String(value || "").trim()).filter(Boolean)
          : [];

        if (incomingGroupIds.length === 0) {
          return;
        }

        setActiveTab("drafts");
        setIsComposerOpen(true);
        setComposerStep("naming");
        applySelectedContactGroups(incomingGroupIds, incomingGroupNames);
        setCampaignStatus({
          message: `${incomingGroupIds.length} groupe${incomingGroupIds.length > 1 ? "s" : ""} ajoute${incomingGroupIds.length > 1 ? "s" : ""} a la campagne.`,
          tone: "success",
        });
        return;
      }

      if (event.topic !== "contacts.import") {
        return;
      }

      const importedEmails = Array.isArray(event.payload?.emails)
        ? event.payload.emails
            .map((value) => String(value || "").trim().toLowerCase())
            .filter(Boolean)
        : [];

      if (importedEmails.length === 0) {
        return;
      }

      setCampaignForm((current) => {
        const existingRawValue = current.raw_recipients.trim();
        const existingEmails = new Set(analyzeRecipients(existingRawValue).valid.map((email) => email.toLowerCase()));
        const freshEmails = importedEmails.filter((email) => !existingEmails.has(email));

        if (freshEmails.length === 0) {
          setActiveTab("drafts");
          setIsComposerOpen(true);
          setComposerStep("naming");
          setCampaignStatus({
            message: "Les destinataires importés sont déjà présents dans la campagne.",
            tone: "success",
          });
          return current;
        }

        setActiveTab("drafts");
        setIsComposerOpen(true);
        setComposerStep("naming");
        setCampaignStatus({
          message: `${freshEmails.length} destinataire${freshEmails.length > 1 ? "s" : ""} ajouté${freshEmails.length > 1 ? "s" : ""} depuis Évènements.`,
          tone: "success",
        });

        return {
          ...current,
          raw_recipients: [existingRawValue, ...freshEmails].filter(Boolean).join("\n"),
        };
      });
    });

    return () => {
      window.clearTimeout(bootTimer);
      window.removeEventListener("focus", applyIfVisible);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      unsubscribe();
    };
  }, [queryClient, routeConfig.allowStandaloneContactsIntegration, settingsOnly]);

  useEffect(() => {
    if (settingsOnly) return;
    writeSessionJson(NEWSLETTER_VIEW_STATE_STORAGE_KEY, {
      activeTab,
      selectedDraftId,
      selectedCampaignId,
      selectedDraftIds,
      editingDraftId,
      isComposerOpen,
      composerStep,
      composerContentMode,
    } satisfies NewsletterViewState);
  }, [
    settingsOnly,
    activeTab,
    composerContentMode,
    composerStep,
    editingDraftId,
    isComposerOpen,
    selectedCampaignId,
    selectedDraftId,
    selectedDraftIds,
  ]);

  useEffect(() => {
    if (settingsHydratedRef.current || !settingsQuery.data) {
      return;
    }

    const draft = readSettingsDraft();
    const nextSettingsForm = draft
      ? normalizeSettingsForm({ ...settingsQuery.data, ...draft })
      : normalizeSettingsForm(settingsQuery.data);
    const nextResolvedSettings =
      isHostedNewsletter && hostedPublicBaseUrl
        ? { ...nextSettingsForm, public_base_url: hostedPublicBaseUrl }
        : nextSettingsForm;
    const detectedProviderMode = detectEmailProviderPreset(nextResolvedSettings);
    const nextProviderChoice = resolveEmailProviderChoice(
      nextResolvedSettings,
      detectedProviderMode === "custom" ? undefined : detectedProviderMode
    );
    setSettingsForm(nextResolvedSettings);
    setEmailProviderMode(nextProviderChoice === "custom" || nextProviderChoice === "unknown" ? "custom" : nextProviderChoice);
    setEmailConnectionStep(deriveEmailConnectionStep(nextResolvedSettings, nextProviderChoice));
    setShowAdvancedEmailSettings(nextProviderChoice === "custom" || nextProviderChoice === "unknown");
    setEmailConnectionVerifyState("idle");
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
    settingsHydratedRef.current = true;
  }, [hostedPublicBaseUrl, isHostedNewsletter, settingsQuery.data]);

  useEffect(() => {
    // Keep the active draft id stable while the composer is open; the drafts list may lag behind
    // just after an autosave, and clearing the id here would make the next autosave create a duplicate.
    if (isComposerOpen) {
      return;
    }

    if (editingDraftId && !drafts.some((draft) => draft.id === editingDraftId)) {
      updateEditingDraftId(null);
    }
  }, [drafts, editingDraftId, isComposerOpen]);

  useEffect(() => {
    const availableDraftIds = new Set(drafts.map((draft) => draft.id));
    setSelectedDraftIds((current) => current.filter((draftId) => availableDraftIds.has(draftId)));
  }, [drafts]);

  useEffect(() => {
    if (drafts.length === 0) {
      if (selectedDraftId !== null) {
        setSelectedDraftId(null);
      }
      return;
    }

    if (!selectedDraftId || !drafts.some((draft) => draft.id === selectedDraftId)) {
      setSelectedDraftId(drafts[0].id);
    }
  }, [drafts, selectedDraftId]);

  useEffect(() => {
    if (campaigns.length === 0) {
      if (selectedCampaignId !== null) {
        setSelectedCampaignId(null);
      }
      return;
    }

    if (!selectedCampaignId || !campaigns.some((campaign) => campaign.id === selectedCampaignId)) {
      setSelectedCampaignId(campaigns[0].id);
    }
  }, [campaigns, selectedCampaignId]);

  useEffect(() => {
    if (selectedCampaignId && !campaigns.some((campaign) => campaign.id === selectedCampaignId)) {
      setSelectedCampaignId(null);
    }
  }, [campaigns, selectedCampaignId]);

  useEffect(() => {
    if (filteredDrafts.length === 0) {
      return;
    }
    if (!selectedDraftId || !filteredDrafts.some((draft) => draft.id === selectedDraftId)) {
      setSelectedDraftId(filteredDrafts[0].id);
    }
  }, [filteredDrafts, selectedDraftId]);

  useEffect(() => {
    if (filteredCampaigns.length === 0) {
      return;
    }
    if (!selectedCampaignId || !filteredCampaigns.some((campaign) => campaign.id === selectedCampaignId)) {
      setSelectedCampaignId(filteredCampaigns[0].id);
    }
  }, [filteredCampaigns, selectedCampaignId]);

  useEffect(() => {
    return () => {
      if (settingsSuccessTimerRef.current) {
        window.clearTimeout(settingsSuccessTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (composerContentMode !== "assistant") {
      setIsAssistantRenderPending(false);
      return;
    }

    let cancelled = false;
    setIsAssistantRenderPending(true);

    void (async () => {
      try {
        const nextHtml = await buildAssistantHtml(assistantDraft);
        if (cancelled) {
          return;
        }

        setAssistantRenderedHtml(nextHtml);
        setCampaignForm((current) => (current.html_body === nextHtml ? current : { ...current, html_body: nextHtml }));
      } finally {
        if (!cancelled) {
          setIsAssistantRenderPending(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [assistantDraft, composerContentMode]);

  useEffect(() => {
    function handleOauthMessage(event: MessageEvent) {
      const payload = event.data;
      if (!payload || typeof payload !== "object" || payload.type !== "relyo:newsletter-oauth") {
        return;
      }
      if (event.origin !== window.location.origin) {
        return;
      }
      if (emailOauthPopupRef.current && event.source && event.source !== emailOauthPopupRef.current) {
        return;
      }
      emailOauthPopupRef.current = null;
      setIsEmailOauthPending(false);
      if (payload.success) {
        setEmailConnectionVerifyState("verified");
        setEmailConnectionVerifyFailureCount(0);
        setEmailConnectionVerifyStatus({
          message: payload.message || "Connexion sécurisée vérifiée.",
          tone: "success",
        });
        void settingsQuery.refetch().then((result) => {
          if (result.data) {
            const normalizedSettings = normalizeSettingsForm(result.data);
            const nextResolvedSettings =
              isHostedNewsletter && hostedPublicBaseUrl
                ? { ...normalizedSettings, public_base_url: hostedPublicBaseUrl }
                : normalizedSettings;
            const detectedProviderMode = detectEmailProviderPreset(nextResolvedSettings);
            const nextProviderChoice = resolveEmailProviderChoice(
              nextResolvedSettings,
              detectedProviderMode === "custom" ? undefined : detectedProviderMode
            );
            setSettingsForm(nextResolvedSettings);
            setEmailProviderMode(
              nextProviderChoice === "custom" || nextProviderChoice === "unknown" ? "custom" : nextProviderChoice
            );
          }
          setEmailConnectionStep("review");
          setShowAdvancedEmailSettings(false);
        });
        return;
      }
      setEmailConnectionVerifyState("idle");
      setEmailConnectionVerifyStatus({
        message: String(payload.message || "La connexion sécurisée a échoué."),
        tone: "error",
      });
    }

    window.addEventListener("message", handleOauthMessage);
    return () => {
      window.removeEventListener("message", handleOauthMessage);
    };
  }, [hostedPublicBaseUrl, isHostedNewsletter, settingsQuery]);

  useEffect(() => {
    if (!isEmailConnectionWizardOpen) {
      emailWizardModalScrollTopRef.current = 0;
      return;
    }
    emailWizardModalScrollTopRef.current = 0;
    if (emailWizardModalBodyRef.current) {
      emailWizardModalBodyRef.current.scrollTop = 0;
    }
  }, [emailConnectionStep, isEmailConnectionWizardOpen]);

  useLayoutEffect(() => {
    if (!isEmailConnectionWizardOpen || !emailWizardModalBodyRef.current) {
      return;
    }
    emailWizardModalBodyRef.current.scrollTop = emailWizardModalScrollTopRef.current;
  });

  useEffect(() => {
    if (!isEmailConnectionWizardOpen || emailConnectionStep !== "onboarding_recommendation") {
      return;
    }
    if (emailOnboardingRecommendation || onboardingRecommendationMutation.isPending || !hasCompletedEmailOnboarding(settingsForm)) {
      return;
    }
    onboardingRecommendationMutation.mutate(buildEmailOnboardingPayload(settingsForm));
  }, [
    emailConnectionStep,
    emailOnboardingRecommendation,
    isEmailConnectionWizardOpen,
    settingsForm,
  ]);

  function clearCampaignFieldError(fieldId: string) {
    setCampaignFieldErrors((current) => current.filter((value) => value !== fieldId));
    setCampaignStatus((current) =>
      current.tone === "error" && isCampaignStatusLinkedToField(fieldId, current.message)
        ? { message: "", tone: "neutral" }
        : current
    );
  }

  function clearSettingsFieldError(fieldId: string) {
    setSettingsFieldErrors((current) => current.filter((value) => value !== fieldId));
  }

  function clearSettingsFieldErrors(fieldIds: string[]) {
    setSettingsFieldErrors((current) => current.filter((value) => !fieldIds.includes(value)));
  }

  function buildEmailOnboardingPayload(source: Partial<NewsletterSettings>) {
    return {
      previous_sending_route: String(source.email_onboarding_previous_sending_route || "").trim(),
      previous_send_size_range: String(source.email_onboarding_previous_send_size_range || "").trim(),
      previous_send_frequency: String(source.email_onboarding_previous_send_frequency || "").trim(),
      intended_send_size_range: String(source.email_onboarding_intended_send_size_range || "").trim(),
      intended_send_frequency: String(source.email_onboarding_intended_send_frequency || "").trim(),
      intended_email_type: String(source.email_onboarding_intended_email_type || "").trim(),
    };
  }

  function updateEmailOnboardingAnswers(updates: Partial<NewsletterSettings>) {
    onboardingRecommendationMutation.reset();
    setSettingsForm((current) => ({
      ...current,
      ...updates,
      email_onboarding_recommended_route: "",
      email_onboarding_chosen_route: "",
      email_onboarding_initial_guardrail_profile: "",
      email_onboarding_declined_ses_recommendation_at: "",
    }));
    setEmailOnboardingRecommendation(null);
    setEmailOnboardingRecommendationStatus({ message: "", tone: "neutral" });
  }

  function continueEmailOnboardingToService() {
    onboardingRecommendationMutation.mutate(buildEmailOnboardingPayload(effectiveSettingsForm));
  }

  function acceptClassicMailboxAfterRecommendation() {
    setSettingsForm((current) => ({
      ...current,
      email_onboarding_chosen_route: "classic_mailbox",
      email_onboarding_declined_ses_recommendation_at:
        current.email_onboarding_recommended_route === "amazon_ses" ? new Date().toISOString() : "",
    }));
    setEmailOnboardingRecommendationStatus({ message: "", tone: "neutral" });
    if (resolvedEmailProviderChoice !== "unknown" || String(effectiveSettingsForm.smtp_username || "").trim()) {
      setEmailConnectionStep("credentials");
      return;
    }
    setEmailConnectionStep("service");
  }

  function acceptSesRecommendation() {
    setSettingsForm((current) => ({
      ...current,
      email_onboarding_chosen_route: "amazon_ses",
      email_onboarding_declined_ses_recommendation_at: "",
      ses_identity_mode: normalizeSesIdentityMode(current.ses_identity_mode),
      ses_domain_identity:
        current.ses_domain_identity ||
        (emailDomain(current.sender_email || current.smtp_username) ? `news.${emailDomain(current.sender_email || current.smtp_username)}` : ""),
    }));
    setEmailOnboardingRecommendationStatus({ message: "", tone: "neutral" });
    setEmailConnectionStep("review");
  }

  function switchToSesRouteFromClassicReview() {
    setSettingsForm((current) => ({
      ...current,
      email_onboarding_chosen_route: "amazon_ses",
      email_onboarding_declined_ses_recommendation_at: "",
      ses_identity_mode: normalizeSesIdentityMode(current.ses_identity_mode),
      ses_domain_identity:
        current.ses_domain_identity ||
        (emailDomain(current.sender_email || current.smtp_username) ? `news.${emailDomain(current.sender_email || current.smtp_username)}` : ""),
      ses_mail_from_domain:
        current.ses_mail_from_domain ||
        (emailDomain(current.sender_email || current.smtp_username)
          ? `bounce.news.${emailDomain(current.sender_email || current.smtp_username)}`
          : ""),
    }));
    setEmailOnboardingRecommendationStatus({ message: "", tone: "neutral" });
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
    setSesDnsCopyStatus({ message: "", tone: "neutral" });
    setEmailConnectionStep("review");
  }

  function returnToClassicMailboxFlow() {
    setSettingsForm((current) => ({
      ...restoreClassicMailboxRoute(current),
      email_onboarding_chosen_route: "classic_mailbox",
      email_onboarding_declined_ses_recommendation_at: new Date().toISOString(),
    }));
    setEmailOnboardingRecommendationStatus({ message: "", tone: "neutral" });
    setSesDnsCopyStatus({ message: "", tone: "neutral" });
    setEmailConnectionStep("service");
  }

  function updateSesIdentityMode(nextMode: SesIdentityMode) {
    setSettingsForm((current) => {
      if (nextMode === "existing_from_address") {
        return {
          ...current,
          ses_identity_mode: nextMode,
          sender_email: current.sender_email || current.smtp_username,
          ses_domain_identity: emailDomain(current.sender_email || current.smtp_username),
        };
      }
      const currentIdentity = splitDedicatedSesIdentity(current.ses_domain_identity);
      const fallbackDomain = currentIdentity.domain || emailDomain(current.sender_email || current.smtp_username);
      return {
        ...current,
        ses_identity_mode: nextMode,
        ses_domain_identity: fallbackDomain ? `${currentIdentity.prefix || "news"}.${fallbackDomain}` : "",
        ses_mail_from_domain: fallbackDomain ? `bounce.${currentIdentity.prefix || "news"}.${fallbackDomain}` : "",
      };
    });
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
  }

  function updateDedicatedSesDomain(nextDomain: string) {
    setSettingsForm((current) => {
      const identity = splitDedicatedSesIdentity(current.ses_domain_identity);
      const cleanDomain = nextDomain.trim().toLowerCase();
      const prefix = sanitizeSesPrefix(identity.prefix);
      return {
        ...current,
        ses_identity_mode: "dedicated_subdomain",
        ses_domain_identity: cleanDomain ? `${prefix}.${cleanDomain}` : "",
        ses_mail_from_domain: cleanDomain ? `bounce.${prefix}.${cleanDomain}` : "",
      };
    });
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
    setSesDnsCopyStatus({ message: "", tone: "neutral" });
  }

  function updateDedicatedSesPrefix(nextPrefix: string) {
    setSettingsForm((current) => {
      const identity = splitDedicatedSesIdentity(current.ses_domain_identity);
      const cleanPrefix = sanitizeSesPrefix(nextPrefix);
      const domain = identity.domain || emailDomain(current.sender_email || current.smtp_username);
      return {
        ...current,
        ses_identity_mode: "dedicated_subdomain",
        ses_domain_identity: domain ? `${cleanPrefix}.${domain}` : "",
        ses_mail_from_domain: domain ? `bounce.${cleanPrefix}.${domain}` : "",
      };
    });
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
    setSesDnsCopyStatus({ message: "", tone: "neutral" });
  }

  function updateExistingSesSender(nextEmail: string) {
    setSettingsForm((current) => ({
      ...current,
      ses_identity_mode: "existing_from_address",
      sender_email: nextEmail,
      ses_domain_identity: emailDomain(nextEmail),
      ses_mail_from_domain: emailDomain(nextEmail) ? `bounce.${emailDomain(nextEmail)}` : "",
    }));
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
    setSesDnsCopyStatus({ message: "", tone: "neutral" });
  }

  async function handleCopySesDnsText(text: string, copiedLabel: string) {
    try {
      const copied = await copyTextToClipboard(text);
      setSesDnsCopyStatus({
        message: copied ? `${copiedLabel} copiée.` : `Copie manuelle : ${text}`,
        tone: copied ? "success" : "neutral",
      });
    } catch {
      setSesDnsCopyStatus({ message: `Copie manuelle : ${text}`, tone: "neutral" });
    }
  }

  function handlePrepareSesRoute() {
    const mode = normalizeSesIdentityMode(settingsForm.ses_identity_mode);
    const payload =
      mode === "existing_from_address"
        ? {
            identity_mode: mode,
            sender_name: settingsForm.sender_name,
            from_email: settingsForm.sender_email,
            reply_to_email: settingsForm.reply_to_email,
          }
        : {
            identity_mode: mode,
            sender_name: settingsForm.sender_name,
            domain: splitDedicatedSesIdentity(settingsForm.ses_domain_identity).domain,
            subdomain_prefix: splitDedicatedSesIdentity(settingsForm.ses_domain_identity).prefix || "news",
            reply_to_email: settingsForm.reply_to_email,
          };
    prepareSesRouteMutation.mutate(payload);
  }

  function handleEmailProviderChoiceSelect(nextChoice: EmailProviderChoiceId) {
    clearSettingsFieldErrors([
      ...EMAIL_PROVIDER_FIELD_IDS,
      ...EMAIL_PROVIDER_USERNAME_FIELD_IDS,
      ...EMAIL_PROVIDER_PASSWORD_FIELD_IDS,
      "imap_folder",
    ]);
    setEmailConnectionStep(emailProviderIsTemporarilyUnavailable(nextChoice) ? "service" : "credentials");
    setShowAdvancedEmailSettings(nextChoice === "custom" || nextChoice === "unknown");
    setEmailConnectionVerifyFailureCount(0);
    setEmailConnectionVerifyState("idle");
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });

    if (nextChoice === "custom" || nextChoice === "unknown") {
      setEmailProviderMode("custom");
      setSettingsForm((current) => ({
        ...restoreClassicMailboxRoute(current),
        auth_method: "",
        oauth_provider: "",
        oauth_connected_email: "",
        oauth_connected_at: "",
        oauth_connection_mode: "",
        email_provider_selection: nextChoice,
      }));
      return;
    }

    setEmailProviderMode(nextChoice);
    setSettingsForm((current) => ({
      ...restoreClassicMailboxRoute(applyEmailProviderPreset(current, nextChoice)),
      auth_method: "",
      oauth_provider: "",
      oauth_connected_email: "",
      oauth_connected_at: "",
      oauth_connection_mode: "",
      email_provider_selection: nextChoice,
    }));
  }

  function handleEmailAccountKindChange(nextKind: EmailAccountKind) {
    setSettingsForm((current) => ({ ...current, email_account_kind: nextKind }));
  }

  function openEmailConnectionWizard() {
    onboardingRecommendationMutation.reset();
    setEmailOnboardingRecommendationStatus({ message: "", tone: "neutral" });
    const nextProviderChoice = resolveEmailProviderChoice(
      settingsForm,
      emailProviderMode === "custom" ? undefined : emailProviderMode
    );
    setEmailConnectionStep(deriveEmailConnectionStep(settingsForm, nextProviderChoice));
    setShowAdvancedEmailSettings(nextProviderChoice === "custom" || nextProviderChoice === "unknown");
    setEmailConnectionVerifyFailureCount(0);
    setEmailConnectionVerifyState("idle");
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
    setIsEmailConnectionWizardOpen(true);
  }

  function closeEmailConnectionWizard() {
    if (emailOauthPopupRef.current && !emailOauthPopupRef.current.closed) {
      emailOauthPopupRef.current.close();
    }
    emailOauthPopupRef.current = null;
    setIsEmailConnectionWizardOpen(false);
    onboardingRecommendationMutation.reset();
    setEmailOnboardingRecommendationStatus({ message: "", tone: "neutral" });
    setEmailConnectionVerifyFailureCount(0);
    setIsEmailOauthPending(false);
    setEmailConnectionVerifyState("idle");
    setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
  }

  async function handleStartSecureProviderConnection() {
    if (resolvedEmailProviderChoice !== "gmail" && resolvedEmailProviderChoice !== "outlook") {
      return;
    }
    try {
      setSettingsFieldErrors([]);
      setIsEmailOauthPending(true);
      setEmailConnectionVerifyState("verifying");
      setEmailConnectionVerifyStatus({ message: "Ouverture de la connexion sécurisée...", tone: "neutral" });
      const oauthStartQuery = new URLSearchParams({
        frontend_origin: window.location.origin,
      });
      oauthStartQuery.set("email_account_kind", effectiveSettingsForm.email_account_kind.trim() || "unknown");
      const result = await fetchJson<{ authorize_url: string }>(
        newsletterApiUrl(`oauth/${resolvedEmailProviderChoice}/start?${oauthStartQuery.toString()}`)
      );
      const popup = window.open(
        result.authorize_url,
        "relyo-newsletter-oauth",
        "popup=yes,width=620,height=760,left=120,top=80"
      );
      if (!popup) {
        throw new Error("La fenêtre de connexion a été bloquée par le navigateur.");
      }
      emailOauthPopupRef.current = popup;
      popup.focus();
      setEmailConnectionVerifyStatus({
        message: `Terminez la connexion ${selectedEmailProviderChoice.label} dans la fenêtre qui vient de s'ouvrir.`,
        tone: "neutral",
      });
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      setIsEmailOauthPending(false);
      setEmailConnectionVerifyState("idle");
      setEmailConnectionVerifyStatus({ message: normalizedError.message, tone: "error" });
    }
  }

  function resetCampaignForm(options: { resetDraft?: boolean; clearStatus?: boolean; trackingEnabled?: boolean } = {}) {
    resetAutoSaveTracking();
    const nextForm = {
      ...createEmptyCampaignForm(options.trackingEnabled ?? Boolean(engagementQuery.data?.enabled && engagementQuery.data?.operational)),
      name: buildGeneratedDraftName(),
    };
    setCampaignForm(nextForm);
    syncComposerContentSource(nextForm, "assistant");
    setComposerStep("naming");
    setIsClassicSwitchDialogOpen(false);
    setIsClassicSwitchPending(false);
    setIsTestEmailDialogOpen(false);
    setIsTrackingRecommendationDialogOpen(false);
    setTestEmailState("idle");
    setTestEmailStatus({ message: "", tone: "neutral" });
    setRecipientEntryValue("");
    setRecipientRejectedTokens([]);
    setCampaignFieldErrors([]);
    trackingRecommendationSeenRef.current = false;
    if (options.resetDraft !== false) {
      updateEditingDraftId(null);
    }
    if (options.clearStatus !== false) {
      setCampaignStatus({ message: "", tone: "neutral" });
    }
  }

  function openDraftInEditor(draft: NewsletterCampaign, message = "Brouillon chargé.") {
    resetAutoSaveTracking();
    const nextForm = toCampaignForm(draft);
    setSelectedDraftId(draft.id);
    updateEditingDraftId(draft.id);
    setCampaignForm(nextForm);
    syncComposerContentSource(nextForm);
    setComposerStep("naming");
    setIsClassicSwitchDialogOpen(false);
    setIsClassicSwitchPending(false);
    setIsTestEmailDialogOpen(false);
    setIsTrackingRecommendationDialogOpen(false);
    setTestEmailState("idle");
    setTestEmailStatus({ message: "", tone: "neutral" });
    setRecipientEntryValue("");
    setRecipientRejectedTokens([]);
    setCampaignFieldErrors([]);
    trackingRecommendationSeenRef.current = false;
    setCampaignStatus({ message, tone: "neutral" });
    setActiveTab("drafts");
    setIsComposerOpen(true);
  }

  function toggleDraftSelection(draftId: number) {
    setSelectedDraftIds((current) =>
      current.includes(draftId) ? current.filter((currentId) => currentId !== draftId) : [...current, draftId]
    );
  }

  function toggleAllDraftsSelection() {
    setSelectedDraftIds((current) => {
      if (areAllDraftsSelected) {
        const filteredSet = new Set(filteredDraftIds);
        return current.filter((draftId) => !filteredSet.has(draftId));
      }
      return Array.from(new Set([...current, ...filteredDraftIds]));
    });
  }

  async function applyIncomingComposeDraft() {
    if(incomingComposePendingRef.current||!readIncomingComposeDraft())return false;
    incomingComposePendingRef.current=true;
    const generation=incomingComposeGenerationRef.current;
    try {
      const refreshed=isHostedNewsletter?await engagementQuery.refetch():null;
      if(generation!==incomingComposeGenerationRef.current)return false;
      const current=refreshed?.isSuccess?refreshed.data:null;
      const draft=readIncomingComposeDraft();
      if(!draft)return false;

      const nextForm = {
        ...createEmptyCampaignForm(Boolean(current?.enabled && current?.operational)),
        name: draft.name,
        subject: draft.subject,
        html_body: draft.html_body,
      };
      clearIncomingComposeDraft();
      resetAutoSaveTracking();
      // Imported content has no server draft yet; it must not become the saved baseline.
      shouldHydrateAutoSaveBaselineRef.current = false;
      lastPersistedDraftSignatureRef.current = "";
      updateEditingDraftId(null);
      setSelectedCampaignId(null);
      setActiveTab("drafts");
      setIsComposerOpen(true);
      setComposerStep("naming");
      setCampaignFieldErrors([]);
      setRecipientEntryValue("");
      setRecipientRejectedTokens([]);
      setCampaignForm(nextForm);
      if (draft.assistant_draft) {
        const nextAssistantDraft = normalizeAssistantDraft(draft.assistant_draft);
        setAssistantDraft(nextAssistantDraft);
        setSelectedAssistantSectionId(nextAssistantDraft.sections[0]?.id || null);
        setAssistantRenderedHtml(draft.html_body);
        setIsAssistantRenderPending(false);
        setComposerContentMode("assistant");
      } else {
        syncComposerContentSource(nextForm);
      }
      setCampaignStatus({
        message: `Newsletter importée depuis Évènements (${draft.source_event_ids.length} événement${draft.source_event_ids.length > 1 ? "s" : ""}). Complétez les destinataires puis planifiez l'envoi.`,
        tone: "success",
      });
      return true;
    } finally {incomingComposePendingRef.current=false;}
  }

  function openEventNewsletterModal() {
    setEventNewsletterPeriod(suggestNewsletterPeriod(eventNewsletterEvents, todayIso()));
    setIsEventNewsletterModalOpen(true);
  }

  async function handleOpenEventNewsletterDraft(draft: Parameters<typeof persistNewsletterComposeDraft>[0]) {
    persistNewsletterComposeDraft(draft);
    if (!await applyIncomingComposeDraft()) {
      throw new Error("Impossible d'ouvrir la newsletter depuis cette session.");
    }
    setIsEventNewsletterModalOpen(false);
  }

  const saveSettingsMutation = useMutation({
    mutationFn: (payload: NewsletterSettings) =>
      fetchJson<{ settings: NewsletterSettings; message: string }>(newsletterApiUrl("settings"), {
        method: "POST",
        body: JSON.stringify(
          isHostedNewsletter && hostedPublicBaseUrl ? { ...payload, public_base_url: hostedPublicBaseUrl } : payload
        ),
      }),
    onMutate: () => {
      setSettingsFieldErrors([]);
      setSettingsSaveState("saving");
      setSettingsStatus({ message: "Vérification des paramètres en cours...", tone: "neutral" });
    },
    onSuccess: (data) => {
      const normalizedSettings = normalizeSettingsForm(data.settings);
      const nextResolvedSettings =
        isHostedNewsletter && hostedPublicBaseUrl
          ? { ...normalizedSettings, public_base_url: hostedPublicBaseUrl }
          : normalizedSettings;
      const detectedProviderMode = detectEmailProviderPreset(nextResolvedSettings);
      const nextProviderChoice = resolveEmailProviderChoice(
        nextResolvedSettings,
        detectedProviderMode === "custom" ? undefined : detectedProviderMode
      );
      setSettingsForm(nextResolvedSettings);
      setEmailProviderMode(nextProviderChoice === "custom" || nextProviderChoice === "unknown" ? "custom" : nextProviderChoice);
      setEmailConnectionStep(deriveEmailConnectionStep(nextResolvedSettings, nextProviderChoice));
      setShowAdvancedEmailSettings(nextProviderChoice === "custom" || nextProviderChoice === "unknown");
      setIsEmailConnectionWizardOpen(false);
      setEmailConnectionVerifyState("verified");
      setEmailConnectionVerifyFailureCount(0);
      setEmailConnectionVerifyStatus({
        message: data.message || "Connexion enregistrée.",
        tone: "success",
      });
      queryClient.setQueryData(["newsletter-settings"], data.settings);
      clearSettingsDraft();
      setSettingsSaveState("success");
      setSettingsStatus({ message: data.message || "Paramètres enregistrés.", tone: "success" });

      if (settingsSuccessTimerRef.current) {
        window.clearTimeout(settingsSuccessTimerRef.current);
      }
      settingsSuccessTimerRef.current = window.setTimeout(() => {
        setSettingsSaveState("idle");
      }, 1800);
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      persistSettingsDraft(settingsForm);
      setSettingsSaveState("idle");
      setSettingsFieldErrors(resolveFieldErrors(normalizedError, inferSettingsFieldErrors(normalizedError.message)));
      setSettingsStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const onboardingRecommendationMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof buildEmailOnboardingPayload>) =>
      fetchJson<EmailOnboardingRecommendation>(newsletterApiUrl("settings/onboarding/recommend"), {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onMutate: () => {
      setSettingsFieldErrors([]);
      setEmailOnboardingRecommendationStatus({
        message: "Relyo prépare votre recommandation d'envoi...",
        tone: "neutral",
      });
    },
    onSuccess: (recommendation) => {
      setSettingsForm((current) => ({
        ...current,
        email_onboarding_recommended_route: recommendation.recommended_route,
        email_onboarding_initial_guardrail_profile: recommendation.initial_guardrail_profile,
        email_onboarding_chosen_route: "",
        email_onboarding_declined_ses_recommendation_at: "",
      }));
      setEmailOnboardingRecommendation(recommendation);
      setEmailOnboardingRecommendationStatus({ message: "", tone: "neutral" });
      setEmailConnectionStep("onboarding_recommendation");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setSettingsFieldErrors(resolveEmailOnboardingFieldErrors(normalizedError));
      setEmailOnboardingRecommendationStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const verifyEmailConnectionMutation = useMutation({
    mutationFn: (payload: NewsletterSettings) =>
      fetchJson<{ message: string }>(newsletterApiUrl("settings/verify"), {
        method: "POST",
        body: JSON.stringify(
          isHostedNewsletter && hostedPublicBaseUrl ? { ...payload, public_base_url: hostedPublicBaseUrl } : payload
        ),
      }),
    onMutate: () => {
      setSettingsFieldErrors([]);
      setEmailConnectionVerifyState("verifying");
      setEmailConnectionVerifyStatus({ message: "Vérification de la connexion en cours...", tone: "neutral" });
    },
    onSuccess: (data) => {
      setEmailConnectionVerifyState("verified");
      setEmailConnectionVerifyFailureCount(0);
      setEmailConnectionVerifyStatus({
        message: data.message || "Connexion vérifiée.",
        tone: "success",
      });
      setEmailConnectionStep("review");
      setShowAdvancedEmailSettings(resolvedEmailProviderChoice === "custom" || resolvedEmailProviderChoice === "unknown");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setEmailConnectionVerifyState("idle");
      setEmailConnectionVerifyStatus({ message: normalizedError.message, tone: "error" });
      setSettingsFieldErrors(resolveFieldErrors(normalizedError, inferSettingsFieldErrors(normalizedError.message)));
      setEmailConnectionVerifyFailureCount((current) => {
        const nextCount = current + 1;
        const shouldRevealAdvanced =
          resolvedEmailProviderChoice === "custom" || resolvedEmailProviderChoice === "unknown" || nextCount >= 2;
        setShowAdvancedEmailSettings(shouldRevealAdvanced);
        return nextCount;
      });
    },
  });

  const prepareSesRouteMutation = useMutation({
    mutationFn: (payload: Record<string, string>) =>
      fetchJson<{ settings: NewsletterSettings; dns_records: SesDnsRecord[] }>(newsletterApiUrl("settings/ses/prepare"), {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onMutate: () => {
      setSettingsFieldErrors([]);
      setEmailConnectionVerifyStatus({ message: "Préparation de la configuration SES...", tone: "neutral" });
    },
    onSuccess: (data) => {
      const normalizedSettings = normalizeSettingsForm(data.settings);
      setSettingsForm(normalizedSettings);
      queryClient.setQueryData(["newsletter-settings"], data.settings);
      setEmailConnectionVerifyStatus({
        message: "Configuration préparée. Ajoutez les enregistrements DNS, puis vérifiez la configuration.",
        tone: "success",
      });
      setEmailConnectionStep("review");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setSettingsFieldErrors(resolveFieldErrors(normalizedError, ["ses_domain_identity"]));
      setEmailConnectionVerifyStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const refreshSesRouteMutation = useMutation({
    mutationFn: () =>
      fetchJson<{ settings: NewsletterSettings; dns_records: SesDnsRecord[] }>(newsletterApiUrl("settings/ses/refresh"), {
        method: "POST",
      }),
    onMutate: () => {
      setSettingsFieldErrors([]);
      setEmailConnectionVerifyStatus({ message: "Vérification de la configuration SES...", tone: "neutral" });
    },
    onSuccess: (data) => {
      const normalizedSettings = normalizeSettingsForm(data.settings);
      setSettingsForm(normalizedSettings);
      queryClient.setQueryData(["newsletter-settings"], data.settings);
      setEmailConnectionVerifyStatus({
        message:
          normalizedSettings.ses_status === "ready"
            ? "Domaine vérifié. Consultez la protection pour les autres conditions d’envoi."
            : "La configuration n'est pas encore prête. Vérifiez les enregistrements DNS puis réessayez.",
        tone: normalizedSettings.ses_status === "ready" ? "success" : "neutral",
      });
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setSettingsFieldErrors(resolveFieldErrors(normalizedError, ["ses_status"]));
      setEmailConnectionVerifyStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  async function saveDraftRequest(payload: Record<string, unknown>) {
    return fetchJson<NewsletterCampaign>(newsletterApiUrl("campaigns"), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  const saveDraftMutation = useMutation({
    mutationFn: saveDraftRequest,
    onSuccess: async (draft) => {
      upsertDraftInCache(draft);
      const nextForm = toCampaignForm(draft);
      const nextSignature = buildComposerDraftSignature(
        nextForm,
        parseAssistantDraftFromHtml(nextForm.html_body) ? "assistant" : "richText",
        parseAssistantDraftFromHtml(nextForm.html_body) || createPreferredAssistantDraft()
      );
      setSelectedDraftId(draft.id);
      updateEditingDraftId(draft.id);
      setCampaignForm(nextForm);
      syncComposerContentSource(nextForm, composerContentMode === "assistant" ? "assistant" : "richText");
      setRecipientEntryValue("");
      setRecipientRejectedTokens([]);
      markDraftAsPersisted(nextSignature);
      setAutoSaveState("saved");
      setCampaignStatus({
        message:
          recipientAnalysis.invalid.length > 0
            ? "Brouillon enregistré. Corrigez les adresses en rouge avant la planification."
            : nextForm.scheduled_at
              ? "Brouillon enregistré. L'envoi ne partira qu'après « Planifier l'envoi »."
              : "Brouillon enregistré.",
        tone: recipientAnalysis.invalid.length > 0 ? "error" : "neutral",
      });
      await draftsQuery.refetch();
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCampaignFieldErrors(resolveFieldErrors(normalizedError, inferCampaignFieldErrors(normalizedError.message)));
      setCampaignStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const createCampaignMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      fetchJson<NewsletterCampaign>(newsletterApiUrl("campaigns"), {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: async (campaign) => {
      setSelectedCampaignId(campaign.id);
      updateEditingDraftId(null);
      resetCampaignForm({ resetDraft: false, clearStatus: false });
      setCampaignStatus({ message: "Campagne planifiée.", tone: "neutral" });
      setActiveTab("sent");
      setIsComposerOpen(false);
      await Promise.all([
        draftsQuery.refetch(),
        campaignsQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ["newsletter-campaign-detail", campaign.id] }),
      ]);
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCampaignFieldErrors(resolveFieldErrors(normalizedError, inferCampaignFieldErrors(normalizedError.message)));
      setCampaignStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const deleteDraftMutation = useMutation({
    mutationFn: (draftId: number) =>
      fetchJson<{ ok: boolean }>(newsletterApiUrl(`drafts/${draftId}`), {
        method: "DELETE",
      }),
    onSuccess: async (_, draftId) => {
      if (editingDraftId === draftId) {
        resetCampaignForm();
      }
      if (selectedDraftId === draftId) {
        setSelectedDraftId(null);
      }
      setSelectedDraftIds((current) => current.filter((currentId) => currentId !== draftId));
      await draftsQuery.refetch();
    },
    onError: (error) => {
      window.alert(normalizeApiError(error).message);
    },
  });

  const bulkDeleteDraftsMutation = useMutation({
    mutationFn: async (draftIds: number[]) => {
      for (const draftId of draftIds) {
        await fetchJson<{ ok: boolean }>(newsletterApiUrl(`drafts/${draftId}`), {
          method: "DELETE",
        });
      }
      return { deletedCount: draftIds.length, draftIds };
    },
    onSuccess: async ({ deletedCount, draftIds }) => {
      if (editingDraftId && draftIds.includes(editingDraftId)) {
        resetCampaignForm();
      }
      if (selectedDraftId && draftIds.includes(selectedDraftId)) {
        setSelectedDraftId(null);
      }
      setSelectedDraftIds([]);
      await draftsQuery.refetch();
      setCampaignStatus({
        message: `${deletedCount} brouillon${deletedCount > 1 ? "s" : ""} supprimé${deletedCount > 1 ? "s" : ""}.`,
        tone: "neutral",
      });
    },
    onError: (error) => {
      window.alert(normalizeApiError(error).message);
    },
  });

  const duplicateCampaignMutation = useMutation({
    mutationFn: (campaignId: number) =>
      fetchJson<NewsletterCampaign>(newsletterApiUrl(`campaigns/${campaignId}/duplicate`), {
        method: "POST",
        body: "{}",
      }),
    onSuccess: async (draft) => {
      await draftsQuery.refetch();
      openDraftInEditor(
        draft,
        "Campagne copiée dans un nouveau brouillon. La date d'envoi a été vidée pour éviter un départ immédiat."
      );
    },
    onError: (error) => {
      window.alert(normalizeApiError(error).message);
    },
  });

  const cancelCampaignMutation = useMutation({
    mutationFn: (campaignId: number) =>
      fetchJson<NewsletterCampaign>(newsletterApiUrl(`campaigns/${campaignId}/cancel`), {
        method: "POST",
        body: "{}",
      }),
    onSuccess: async (_, campaignId) => {
      await Promise.all([
        campaignsQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ["newsletter-campaign-detail", campaignId] }),
      ]);
    },
    onError: (error) => {
      window.alert(normalizeApiError(error).message);
    },
  });

  const setDefaultDesignMutation = useMutation({
    mutationFn: (campaignId: number) =>
      fetchJson<{ default_design: NewsletterDefaultDesignSnapshot; message: string }>(
        newsletterApiUrl(`campaigns/${campaignId}/default-design`),
        {
          method: "POST",
          body: "{}",
        }
      ),
    onSuccess: async (result) => {
      queryClient.setQueryData(["newsletter-default-design"], { default_design: result.default_design });
      setDefaultDesignStatus({
        message: result.message.replace("defini par defaut", "défini par défaut"),
        tone: "success",
      });
      await defaultDesignQuery.refetch();
    },
    onError: (error) => {
      setDefaultDesignStatus({ message: normalizeApiError(error).message, tone: "error" });
    },
  });

  async function prepareCampaignPayload(form: CampaignFormState, saveAsDraft: boolean) {
    const basePayload = buildCampaignPayload(form, editingDraftIdRef.current, saveAsDraft);
    if (form.contact_group_ids.length === 0) {
      return {
        payload: {
          ...basePayload,
          resolved_group_recipients: [] as string[],
        },
        resolution: {
          groups: [] as ContactsNewsletterGroup[],
          recipients: [] as string[],
          missing_group_ids: [] as number[],
        },
        totalRecipients: analyzeRecipients(form.raw_recipients).valid.length,
      };
    }

    if (!hasContactsIntegration) {
      throw new Error("Les groupes de contacts sont disponibles depuis l'espace Relyo.");
    }

    const resolution = await fetchJson<ContactsNewsletterResolution>(routeConfig.contactsResolveApiPath, {
      method: "POST",
      body: JSON.stringify({ group_ids: form.contact_group_ids }),
    });

    const nextLabels = resolution.groups.map((group) => group.name);
    if (nextLabels.length > 0) {
      setCampaignForm((current) => ({
        ...current,
        contact_group_labels: nextLabels,
      }));
    }

    return {
      payload: {
        ...basePayload,
        contact_group_labels: nextLabels.length > 0 ? nextLabels : form.contact_group_labels,
        resolved_group_recipients: resolution.recipients,
      },
      resolution,
      totalRecipients: mergeUniqueEmails(analyzeRecipients(form.raw_recipients).valid, resolution.recipients).length,
    };
  }

  function switchToClassicWithoutAlert() {
    setCampaignForm((current) => ({
      ...current,
      html_body: "",
    }));
    setAssistantRenderedHtml("");
    setComposerContentMode("richText");
    clearCampaignFieldError("campaign_editor");
    setCampaignStatus({ message: "", tone: "neutral" });
  }

  function handleSwitchToClassic() {
    if (!assistantDraftHasMeaningfulContent(assistantDraft)) {
      switchToClassicWithoutAlert();
      return;
    }

    setIsClassicSwitchDialogOpen(true);
  }

  function handleSwitchBackToAssistant() {
    if (classicComposerHasMeaningfulContent(campaignForm.html_body)) {
      return;
    }

    const nextAssistantDraft = createPreferredAssistantDraft();
    setAssistantDraft(nextAssistantDraft);
    setSelectedAssistantSectionId(nextAssistantDraft.sections[0]?.id || null);
    setAssistantRenderedHtml("");
    setCampaignForm((current) => ({ ...current, html_body: "" }));
    setComposerContentMode("assistant");
    clearCampaignFieldError("campaign_editor");
    setCampaignStatus({ message: "", tone: "neutral" });
  }

  async function handleConfirmClassicSwitch() {
    setCampaignFieldErrors([]);
    setIsClassicSwitchPending(true);

    try {
      const assistantHtmlBody = await renderAssistantSnapshotHtml();
      const workingForm = ensureDraftName({
        ...buildCampaignFormSnapshot(),
        html_body: assistantHtmlBody,
      });

      syncComposerSnapshot(workingForm);
      const preparedPayload = await prepareCampaignPayload(workingForm, true);
      if (preparedPayload.totalRecipients > MAX_CAMPAIGN_RECIPIENTS) {
        setCampaignFieldErrors(["campaign_recipients"]);
        setCampaignStatus({
          message: `Une campagne ne peut pas dépasser ${MAX_CAMPAIGN_RECIPIENTS} adresses email.`,
          tone: "error",
        });
        return;
      }

      const savedAssistantDraft = await saveDraftRequest(preparedPayload.payload);
      const classicPayload: Record<string, unknown> = {
        ...preparedPayload.payload,
        name: buildClassicCopyName(savedAssistantDraft.name || workingForm.name),
        html_body: buildPlainClassicEmailHtmlFromAssistantDraft(assistantDraft),
      };
      delete classicPayload.campaign_id;

      const classicDraft = await saveDraftRequest(classicPayload);
      await draftsQuery.refetch();
      setIsClassicSwitchDialogOpen(false);
      openDraftInEditor(
        classicDraft,
        "Copie classique créée. Votre brouillon assistant a été conservé."
      );
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      setCampaignFieldErrors(resolveFieldErrors(normalizedError, inferCampaignFieldErrors(normalizedError.message)));
      setCampaignStatus({ message: normalizedError.message, tone: "error" });
    } finally {
      setIsClassicSwitchPending(false);
    }
  }

  async function handleSaveDraft() {
    setCampaignFieldErrors([]);
    try {
      const snapshotForm =
        composerContentMode === "assistant"
          ? {
              ...composerFormSnapshot,
              html_body: await renderAssistantSnapshotHtml(),
            }
          : composerFormSnapshot;
      const workingForm = ensureDraftName(snapshotForm);
      syncComposerSnapshot(workingForm);
      const preparedPayload = await prepareCampaignPayload(workingForm, true);
      if (preparedPayload.totalRecipients > MAX_CAMPAIGN_RECIPIENTS) {
        setCampaignFieldErrors(["campaign_recipients"]);
        setCampaignStatus({
          message: `Une campagne ne peut pas dépasser ${MAX_CAMPAIGN_RECIPIENTS} adresses email.`,
          tone: "error",
        });
        return;
      }

      await saveDraftMutation.mutateAsync(preparedPayload.payload);
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      setCampaignFieldErrors(resolveFieldErrors(normalizedError, inferCampaignFieldErrors(normalizedError.message)));
      setCampaignStatus({ message: normalizedError.message, tone: "error" });
    }
  }

  async function handleCreateCampaign(options?: { skipProtectionConfirmation?: boolean }) {
    setCampaignFieldErrors([]);
    const workingForm =
      composerContentMode === "assistant"
        ? {
            ...buildCampaignFormSnapshot(),
            html_body: await renderAssistantSnapshotHtml(),
          }
        : buildCampaignFormSnapshot();
    syncComposerSnapshot(workingForm);

    if (recipientAnalysis.invalid.length > 0) {
      setCampaignFieldErrors(["campaign_recipients"]);
      setCampaignStatus({
        message: "Corrigez les adresses email invalides affichées en rouge avant la planification.",
        tone: "error",
      });
      return;
    }
    if (!workingForm.name.trim()) {
      setCampaignFieldErrors(["campaign_name"]);
      setCampaignStatus({ message: "Le nom de campagne est obligatoire.", tone: "error" });
      return;
    }
    if (!workingForm.subject.trim()) {
      setCampaignFieldErrors(["campaign_subject"]);
      setCampaignStatus({ message: "L'objet est obligatoire.", tone: "error" });
      return;
    }
    if (!workingForm.raw_recipients.trim() && workingForm.contact_group_ids.length === 0) {
      setCampaignFieldErrors(["campaign_recipients"]);
      setCampaignStatus({ message: "Ajoutez au moins une adresse email ou un groupe de contacts.", tone: "error" });
      return;
    }
    if (!workingForm.scheduled_at.trim()) {
      setCampaignFieldErrors(["campaign_scheduled_at"]);
      setCampaignStatus({ message: "La date et l'heure d'envoi sont obligatoires.", tone: "error" });
      return;
    }
    if (scheduledAtIsInPast(workingForm.scheduled_at)) {
      setCampaignFieldErrors(["campaign_scheduled_at"]);
      setCampaignStatus({ message: "La date et l'heure d'envoi doivent être dans le futur.", tone: "error" });
      return;
    }
    if (!editorHtmlHasContent(workingForm.html_body)) {
      setCampaignFieldErrors(["campaign_editor"]);
      setCampaignStatus({ message: "Le contenu du mail est obligatoire.", tone: "error" });
      return;
    }

    try {
      const preparedPayload = await prepareCampaignPayload(workingForm, false);
      if (workingForm.contact_group_ids.length > 0 && preparedPayload.resolution.recipients.length === 0) {
        setCampaignFieldErrors(["campaign_recipients"]);
        setCampaignStatus({
          message: "Les groupes sélectionnés ne contiennent aucun destinataire exploitable.",
          tone: "error",
        });
        return;
      }
      if (preparedPayload.totalRecipients > MAX_CAMPAIGN_RECIPIENTS) {
        setCampaignFieldErrors(["campaign_recipients"]);
        setCampaignStatus({
          message: `Une campagne ne peut pas dépasser ${MAX_CAMPAIGN_RECIPIENTS} adresses email.`,
          tone: "error",
        });
        return;
      }
      if (preparedPayload.totalRecipients === 0) {
        setCampaignFieldErrors(["campaign_recipients"]);
        setCampaignStatus({ message: "Aucun destinataire valide n'a été détecté.", tone: "error" });
        return;
      }

      if (
        !options?.skipProtectionConfirmation &&
        protectionRecommendationsNeedConfirmation(guardrailsQuery.data, {
          totalRecipients: preparedPayload.totalRecipients,
          htmlBody: workingForm.html_body,
          trackingEnabled: workingForm.tracking_enabled,
        })
      ) {
        setIsProtectionConfirmationDialogOpen(true);
        return;
      }

      await createCampaignMutation.mutateAsync(preparedPayload.payload);
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      setCampaignFieldErrors(resolveFieldErrors(normalizedError, inferCampaignFieldErrors(normalizedError.message)));
      setCampaignStatus({ message: normalizedError.message, tone: "error" });
    }
  }

  function openTestEmailDialog() {
    if (sesRouteBlocksSending) {
      const message = "L'envoi de test sera disponible dès que la configuration du domaine sera vérifiée.";
      setTestEmailStatus({ message, tone: "neutral" });
      setCampaignStatus({ message, tone: "neutral" });
      return;
    }
    setTestEmailRecipient(window.localStorage.getItem(TEST_RECIPIENT_STORAGE_KEY) || "");
    setTestEmailState("idle");
    setTestEmailStatus({ message: "", tone: "neutral" });
    setIsTestEmailDialogOpen(true);
  }

  function closeTestEmailDialog() {
    if (testEmailState === "sending") {
      return;
    }

    setIsTestEmailDialogOpen(false);
    setTestEmailState("idle");
    setTestEmailStatus({ message: "", tone: "neutral" });
  }

  async function handleSendTestEmail() {
    setCampaignFieldErrors([]);
    if (sesRouteBlocksSending) {
      const message = "L'envoi de test sera disponible dès que la configuration du domaine sera vérifiée.";
      setTestEmailStatus({ message, tone: "neutral" });
      setCampaignStatus({ message, tone: "neutral" });
      return;
    }
    const testRecipientEmail = testEmailRecipient.trim();
    const analysis = analyzeRecipients(testRecipientEmail);

    if (!testRecipientEmail) {
      setTestEmailStatus({ message: "Renseignez une adresse email de test.", tone: "error" });
      setCampaignStatus({ message: "Renseignez une adresse email de test.", tone: "error" });
      return;
    }
    if (analysis.invalid.length > 0 || analysis.valid.length !== 1) {
      setTestEmailStatus({ message: "Renseignez une adresse email de test valide.", tone: "error" });
      setCampaignStatus({ message: "Renseignez une adresse email de test valide.", tone: "error" });
      return;
    }
    const testEmailSubject = campaignForm.subject.trim() || campaignForm.name.trim() || "Email de test Relyo";
    const htmlBody =
      composerContentMode === "assistant" ? await renderAssistantSnapshotHtml() : campaignForm.html_body;

    if (!editorHtmlHasContent(htmlBody)) {
      setCampaignFieldErrors(["campaign_editor"]);
      setTestEmailStatus({ message: "Le contenu du mail est obligatoire pour l'email de test.", tone: "error" });
      setCampaignStatus({ message: "Le contenu du mail est obligatoire pour l'email de test.", tone: "error" });
      return;
    }

    try {
      setTestEmailState("sending");
      setTestEmailStatus({ message: "Envoi en cours...", tone: "neutral" });
      setCampaignStatus({ message: "Envoi de l'email de test...", tone: "neutral" });
      const draftSaved = await handleAutoSaveDraft(true);
      if (
        !draftSaved ||
        !editingDraftIdRef.current ||
        latestComposerDraftSignatureRef.current !== lastPersistedDraftSignatureRef.current
      ) {
        throw new Error("Le brouillon n'a pas pu être enregistré. Réessayez avant d'envoyer le test.");
      }
      const result = await fetchJson<{ recipient: string }>(newsletterApiUrl("test-email"), {
        method: "POST",
        body: JSON.stringify(
          buildTestEmailPayload(
            {
              ...campaignForm,
              subject: testEmailSubject,
              html_body: htmlBody,
            },
            isHostedNewsletter && hostedPublicBaseUrl
              ? { ...settingsForm, public_base_url: hostedPublicBaseUrl }
              : settingsForm,
            analysis.valid[0]
          )
        ),
      });
      window.localStorage.setItem(TEST_RECIPIENT_STORAGE_KEY, result.recipient);
      setTestEmailRecipient(result.recipient);
      setTestEmailState("sent");
      setTestEmailStatus({ message: `Email de test envoyé à ${result.recipient}.`, tone: "success" });
      setCampaignStatus({ message: `Email de test envoyé à ${result.recipient}.`, tone: "success" });
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      setTestEmailState("idle");
      setTestEmailStatus({ message: normalizedError.message, tone: "error" });
      setCampaignFieldErrors(resolveFieldErrors(normalizedError, inferTestEmailFieldErrors(normalizedError.message)));
      setCampaignStatus({ message: normalizedError.message, tone: "error" });
    }
  }

  function handleSettingsSubmit() {
    saveSettingsMutation.mutate(
      isHostedNewsletter && hostedPublicBaseUrl ? { ...settingsForm, public_base_url: hostedPublicBaseUrl } : settingsForm
    );
  }

  function handleVerifyEmailConnection() {
    verifyEmailConnectionMutation.mutate(
      isHostedNewsletter && hostedPublicBaseUrl ? { ...settingsForm, public_base_url: hostedPublicBaseUrl } : settingsForm
    );
  }

  async function openNewComposer() {
    const refreshed=isHostedNewsletter?await engagementQuery.refetch():null;
    const current=refreshed?.isSuccess?refreshed.data:null;
    resetCampaignForm({trackingEnabled:Boolean(current?.enabled && current?.operational)});
    const nextAssistantDraft = createAssistantDraftForNewCampaign();
    if (defaultDesign) {
      skipAssistantDefaultsPersistRef.current = {
        theme: nextAssistantDraft.theme,
        logo: {
          header_logo_url: nextAssistantDraft.header_logo_url,
          header_logo_alt: nextAssistantDraft.header_logo_alt,
        },
      };
    }
    setAssistantDraft(nextAssistantDraft);
    setSelectedAssistantSectionId(nextAssistantDraft.sections[0]?.id || null);
    setAssistantRenderedHtml("");
    setComposerContentMode("assistant");
    setSelectedCampaignId(null);
    setIsComposerOpen(true);
    setActiveTab("drafts");
  }

  const settingsVersionNote = metaQuery.data
    ? `Version ${metaQuery.data.app_version} · ${humanizeReleaseChannel(metaQuery.data.release_channel)} · Schéma ${metaQuery.data.schema_version}. Les paramètres et l'historique sont conservés entre versions.`
    : "";
  const effectiveSettingsForm = settingsHydratedRef.current
    ? settingsForm
    : settingsQuery.data
      ? normalizeSettingsForm(settingsQuery.data)
      : settingsForm;
  const resolvedEmailProviderChoice = resolveEmailProviderChoice(
    effectiveSettingsForm,
    emailProviderMode === "custom" ? undefined : emailProviderMode
  );
  const selectedEmailProviderChoice =
    EMAIL_PROVIDER_CHOICES.find((choice) => choice.id === resolvedEmailProviderChoice) ||
    EMAIL_PROVIDER_CHOICES.find((choice) => choice.id === "unknown") ||
    EMAIL_PROVIDER_CHOICES[0];
  const selectedEmailAccountKind =
    EMAIL_ACCOUNT_KIND_OPTIONS.find((option) => option.value === effectiveSettingsForm.email_account_kind) ||
    EMAIL_ACCOUNT_KIND_OPTIONS[2];
  const emailOnboardingPreviousRouteComplete = Boolean(
    String(effectiveSettingsForm.email_onboarding_previous_sending_route || "").trim()
  );
  const emailOnboardingPreviousUsageComplete =
    Boolean(String(effectiveSettingsForm.email_onboarding_previous_send_size_range || "").trim()) &&
    Boolean(String(effectiveSettingsForm.email_onboarding_previous_send_frequency || "").trim());
  const emailOnboardingIntendedUsageComplete =
    Boolean(String(effectiveSettingsForm.email_onboarding_intended_send_size_range || "").trim()) &&
    Boolean(String(effectiveSettingsForm.email_onboarding_intended_send_frequency || "").trim()) &&
    Boolean(String(effectiveSettingsForm.email_onboarding_intended_email_type || "").trim());
  const resolvedEmailOnboardingRecommendation =
    emailOnboardingRecommendation ||
    buildEmailOnboardingRecommendationFallback(
      String(effectiveSettingsForm.email_onboarding_recommended_route || "").trim(),
      String(effectiveSettingsForm.email_onboarding_initial_guardrail_profile || "").trim()
    );
  const activeEmailProviderPreset = emailProviderMode === "custom" ? null : getEmailProviderPreset(emailProviderMode);
  const emailProviderDevelopmentNotice = emailProviderDevelopmentMessage(resolvedEmailProviderChoice);
  const emailProviderCanContinue = !emailProviderDevelopmentNotice;
  const emailProviderDisplayLabel = selectedEmailProviderChoice.label;
  const emailProviderConnectionMode = resolveEmailProviderConnectionMode(resolvedEmailProviderChoice);
  const isOauthConnectedForSelectedProvider =
    effectiveSettingsForm.auth_method === "oauth" &&
    effectiveSettingsForm.oauth_provider === resolvedEmailProviderChoice &&
    Boolean(effectiveSettingsForm.oauth_connected_email.trim());
  const secureProviderConnectionIsReady =
    isOauthConnectedForSelectedProvider ||
    (emailProviderConnectionMode === "secure_sign_in" &&
      emailConnectionVerifyState === "verified" &&
      emailConnectionStep === "review" &&
      !showAdvancedEmailSettings);
  const gmailSecureConnectionIsReady = resolvedEmailProviderChoice === "gmail" && secureProviderConnectionIsReady;
  const emailProviderUsernameValue = effectiveSettingsForm.smtp_username || effectiveSettingsForm.imap_username;
  const emailProviderPasswordValue = effectiveSettingsForm.smtp_password || effectiveSettingsForm.imap_password;
  const emailProviderUsernameHasError = EMAIL_PROVIDER_USERNAME_FIELD_IDS.some((fieldId) =>
    hasFieldError(settingsFieldErrors, fieldId)
  );
  const emailProviderPasswordHasError = EMAIL_PROVIDER_PASSWORD_FIELD_IDS.some((fieldId) =>
    hasFieldError(settingsFieldErrors, fieldId)
  );
  const smtpIsConfigured = hasConfiguredSmtpSettings(effectiveSettingsForm);
  const sesIdentityMode = normalizeSesIdentityMode(effectiveSettingsForm.ses_identity_mode);
  const sesRouteIsConfigured = effectiveSettingsForm.email_route_type.trim() === "relyo_managed_ses";
  const sesStatus = effectiveSettingsForm.ses_status.trim();
  const sesIsReady = sesRouteIsConfigured && sesStatus === "ready";
  const sesRouteBlocksSending = sesRouteIsConfigured && !sesIsReady;
  const sesDedicatedIdentity = splitDedicatedSesIdentity(effectiveSettingsForm.ses_domain_identity);
  const sesDedicatedPrefix = sesDedicatedIdentity.prefix || "news";
  const sesDedicatedRootDomain =
    sesDedicatedIdentity.domain ||
    (sesIdentityMode === "dedicated_subdomain" ? emailDomain(effectiveSettingsForm.sender_email || effectiveSettingsForm.smtp_username) : "");
  const sesExistingSenderEmail =
    sesIdentityMode === "existing_from_address" ? effectiveSettingsForm.sender_email : effectiveSettingsForm.smtp_username || "";
  const sesDnsRecords = parseSesDnsRecords(effectiveSettingsForm.ses_dns_records_json);
  const sesDkimIsVerified = booleanSettingIsTrue(effectiveSettingsForm.ses_dkim_verified);
  const sesMailFromIsVerified = booleanSettingIsTrue(effectiveSettingsForm.ses_mail_from_verified);
  const sesDnsRecordGroups = groupSesDnsRecords(sesDnsRecords, effectiveSettingsForm.ses_mail_from_domain);
  const sesCanPrepare =
    sesIdentityMode === "existing_from_address"
      ? Boolean(effectiveSettingsForm.sender_email.trim())
      : Boolean(sesDedicatedRootDomain.trim() && sesDedicatedPrefix.trim());
  const gmailConnectionIsActive = isOauthConnectedForSelectedProvider && resolvedEmailProviderChoice === "gmail";
  const microsoft365SendOnlyConnectionIsActive =
    effectiveSettingsForm.auth_method === "oauth" &&
    effectiveSettingsForm.oauth_provider === "outlook" &&
    effectiveSettingsForm.oauth_connection_mode.trim() === "microsoft365_send_only" &&
    Boolean(effectiveSettingsForm.oauth_connected_email.trim());
  const showMissingSmtpAlert = !settingsQuery.isLoading && !smtpIsConfigured && !sesRouteIsConfigured;
  const deliveryStatusLabel = smtpIsConfigured
    ? activeEmailProviderPreset?.label || "Votre boîte mail actuelle"
    : sesRouteIsConfigured
      ? "Amazon SES"
    : "Configuration à terminer";
  const deliveryStatusTitle = smtpIsConfigured || sesRouteIsConfigured ? "Statut d'envoi" : "Avant votre premier envoi";
  const deliveryStatusMessage = sesRouteIsConfigured
    ? sesIsReady
      ? "Votre domaine SES est vérifié. La protection indique les conditions restantes avant les envois."
      : "Votre route Amazon SES est préparée, mais les enregistrements du domaine doivent encore être vérifiés avant les envois."
    : smtpIsConfigured
    ? "Votre adresse d'envoi est connectée. Relyo adapte automatiquement le rythme d'envoi selon votre volume et les signaux observés."
    : "Connectez d'abord votre adresse d'envoi habituelle. Relyo pourra ensuite vérifier chaque campagne avant l'envoi.";
  const deliveryStatusPoints = sesRouteIsConfigured
    ? [
        `Statut SES : ${sesStatusLabel(sesStatus)}.`,
        sesIsReady
          ? "Relyo commencera doucement pour protéger vos prochains envois."
          : "Les tests et envois restent bloqués jusqu'à validation du domaine.",
      ]
    : smtpIsConfigured
    ? [
        `Route actuelle : ${deliveryStatusLabel}.`,
        "Si une campagne doit partir par étapes, Relyo vous l'expliquera au moment de l'envoi.",
      ]
    : [
        "Renseignez votre connexion email.",
        "Relyo vous indiquera ensuite comment vos campagnes seront envoyées.",
      ];
  const emailConnectionStepIndex = EMAIL_CONNECTION_STEPS.findIndex((step) => step.id === emailConnectionStep);
  const emailConnectionReviewItems = [
    {
      label: "Diagnostic initial",
      value: getPreviousRouteLabel(effectiveSettingsForm.email_onboarding_previous_sending_route),
    },
    {
      label: "Route recommandee",
      value: getRecommendedRouteLabel(effectiveSettingsForm.email_onboarding_recommended_route),
    },
    {
      label: "Profil initial",
      value: getGuardrailProfileLabel(effectiveSettingsForm.email_onboarding_initial_guardrail_profile),
    },
    {
      label: "Service choisi",
      value:
        effectiveSettingsForm.email_onboarding_chosen_route === "amazon_ses"
          ? "Amazon SES"
          : providerChoiceLabel(resolvedEmailProviderChoice, emailProviderMode),
    },
    {
      label: "Type d'adresse",
      value: selectedEmailAccountKind.label,
    },
    {
      label: "Adresse utilisée pour l'envoi",
      value:
        sesRouteIsConfigured
          ? "Configuration SES à venir"
          : emailProviderUsernameValue.trim() || "À renseigner",
    },
    {
      label: "Identifiant",
      value: emailProviderUsernameValue.trim() || "À renseigner",
    },
    {
      label: "Connexion",
      value:
        gmailSecureConnectionIsReady
          ? "Connexion Gmail active"
          : secureProviderConnectionIsReady
            ? "Connexion sécurisée vérifiée"
            : "Connexion classique",
    },
  ];
  const emailOnboardingSnapshotItems = [
    {
      label: "Avant Relyo",
      value: getPreviousRouteLabel(effectiveSettingsForm.email_onboarding_previous_sending_route),
    },
    {
      label: "Volume avant",
      value: getSendSizeLabel(effectiveSettingsForm.email_onboarding_previous_send_size_range),
    },
    {
      label: "Frequence avant",
      value: getSendFrequencyLabel(effectiveSettingsForm.email_onboarding_previous_send_frequency),
    },
    {
      label: "Volume vise",
      value: getSendSizeLabel(effectiveSettingsForm.email_onboarding_intended_send_size_range),
    },
    {
      label: "Frequence visee",
      value: getSendFrequencyLabel(effectiveSettingsForm.email_onboarding_intended_send_frequency),
    },
    {
      label: "Type d'email",
      value: getIntendedEmailTypeLabel(effectiveSettingsForm.email_onboarding_intended_email_type),
    },
  ];
  const detail = detailQuery.data;
  const accountGuardrailsPayload = useMemo(
    () => ({
      raw_recipients: "",
      resolved_group_recipients: [],
      tracking_enabled: false,
      html_body: "",
      subject: "",
      sender_email: settingsForm.smtp_username,
    }),
    [settingsForm.smtp_username]
  );
  const accountGuardrailsQuery = useQuery({
    queryKey: ["newsletter-guardrails-account", JSON.stringify(accountGuardrailsPayload)],
    queryFn: () =>
      fetchJson<NewsletterProtectionDecision>(newsletterApiUrl("guardrails/evaluate"), {
        method: "POST",
        body: JSON.stringify({schema_version: 2, scope: "account"}),
      }).then(protectionView),
    enabled: isHostedNewsletter && activeTab === "settings" && (smtpIsConfigured || sesRouteIsConfigured),
    staleTime: 15_000,
  });
  const accountRiskMeta = useMemo(() => guardrailRiskMeta(accountGuardrailsQuery.data), [accountGuardrailsQuery.data]);
  const accountObservedSignals = useMemo(
    () => buildGuardrailObservedSignals(accountGuardrailsQuery.data),
    [accountGuardrailsQuery.data]
  );
  const accountAutomaticActions = useMemo(
    () => buildGuardrailAutomaticActions(accountGuardrailsQuery.data),
    [accountGuardrailsQuery.data]
  );
  const accountLimitsSummary = useMemo(
    () => buildGuardrailLimitsSummary(accountGuardrailsQuery.data),
    [accountGuardrailsQuery.data]
  );
  const reviewRiskMeta = useMemo(() => guardrailRiskMeta(guardrailsQuery.data), [guardrailsQuery.data]);
  const reviewObservedSignals = useMemo(
    () => buildGuardrailObservedSignals(guardrailsQuery.data),
    [guardrailsQuery.data]
  );
  const reviewAutomaticActions = useMemo(
    () => buildGuardrailAutomaticActions(guardrailsQuery.data),
    [guardrailsQuery.data]
  );
  const detailRiskMeta = useMemo(() => guardrailRiskMeta(detail), [detail]);
  const detailObservedSignals = useMemo(() => buildGuardrailObservedSignals(detail), [detail]);
  const detailAutomaticActions = useMemo(() => buildGuardrailAutomaticActions(detail), [detail]);
  const emailConnectionPanelTitle =
    emailProviderConnectionMode === "secure_sign_in" ? "Connexion sécurisée" : "Connecter votre adresse";
  const emailConnectionPanelDescription =
    emailProviderConnectionMode === "secure_sign_in"
      ? resolvedEmailProviderChoice === "gmail"
        ? "Ouvrez la fenêtre Google, autorisez l'accès, puis revenez ici. Avec Gmail, Relyo peut envoyer vos campagnes, mais ne détecte pas automatiquement les adresses qui refusent les messages."
        : "Ouvrez la fenêtre Microsoft, autorisez l'accès, puis revenez ici. Relyo vérifiera ensuite automatiquement la connexion."
      : emailProviderConnectionMode === "app_password"
        ? `${emailProviderDisplayLabel} demande généralement un mot de passe spécifique à l'application pour les outils externes comme Relyo.`
        : emailProviderConnectionMode === "manual"
          ? "Relyo commence par une connexion simple. Les paramètres avancés n'apparaissent que si le service n'est pas reconnu ou si la vérification échoue plusieurs fois."
          : "Relyo utilise votre service actuel. Les paramètres avancés n'apparaissent que si le service n'est pas reconnu ou si la vérification échoue plusieurs fois.";
  const emailConnectionUsernameLabel =
    emailProviderConnectionMode === "secure_sign_in" || emailProviderConnectionMode === "app_password"
      ? "Adresse du compte mail"
      : "Identifiant email";
  const emailConnectionPasswordLabel =
    resolvedEmailProviderChoice === "gmail"
      ? "Mot de passe d'application Google"
      : resolvedEmailProviderChoice === "outlook"
        ? "Mot de passe d'application ou mot de passe du compte"
        : emailProviderConnectionMode === "app_password"
          ? "Mot de passe spécifique à l'application"
          : "Mot de passe";
  const emailConnectionProviderCallout =
    emailProviderConnectionMode === "secure_sign_in"
      ? resolvedEmailProviderChoice === "gmail"
        ? {
            title: "Ce que Gmail permet ici",
            message:
              "Avec Gmail, Relyo peut envoyer vos campagnes, mais ne détecte pas automatiquement les adresses qui refusent les messages.",
          }
        : {
            title: "Le plus simple pour ce service",
            message:
              "Passez par Microsoft si possible. Si votre connexion manuelle est refusée, votre compte peut demander un mot de passe d'application.",
          }
      : emailProviderConnectionMode === "app_password"
        ? {
            title: "Mot de passe spécifique requis",
            message: `Pour ${emailProviderDisplayLabel}, un mot de passe créé depuis votre compte mail fonctionne souvent mieux que le mot de passe habituel.`,
          }
        : null;

  const selectedDraftName = campaignForm.name.trim() || "Brouillon sans titre";
  const selectedDraft = drafts.find((draft) => draft.id === selectedDraftId) || null;
  const selectedDraftRecipientAnalysis = selectedDraft
    ? analyzeRecipients(selectedDraft.raw_recipients || "")
    : { valid: [], invalid: [] };
  const settingsSaveIconId: NewsletterIconId =
    settingsSaveState === "saving" ? "icon-refresh" : "icon-check-circle";
  const composerStepIndex = COMPOSER_STEPS.findIndex((step) => step.id === composerStep);
  const enabledComposerFeatures = [
    campaignForm.tracking_enabled ? "Suivi indicatif" : null,
    composerContentMode === "assistant" || campaignForm.unsubscribe_enabled ? "Lien de désinscription" : null,
  ].filter(Boolean) as string[];
  const resolvedGroupRecipientsCount = groupResolutionQuery.data?.recipients.length || 0;
  const composerTitle = editingDraftId ? "Modifier le brouillon" : "Nouvelle newsletter";
  const reviewForm = buildCampaignFormSnapshot();
  const rawReviewPreviewHtml =
    composerContentMode === "assistant" && assistantRenderedHtml ? assistantRenderedHtml : reviewForm.html_body;
  const reviewPreviewHtml = useMemo(
    () => extractEmailPreviewHtml(rawReviewPreviewHtml),
    [rawReviewPreviewHtml]
  );
  const reviewHasPreview = editorHtmlHasContent(reviewPreviewHtml);
  const isReviewPreviewLoading = composerContentMode === "assistant" && isAssistantRenderPending && !reviewHasPreview;
  const classicModeCanReturnToAssistant = !classicComposerHasMeaningfulContent(campaignForm.html_body);
  const autoSaveLabel =
    autoSaveState === "saving"
      ? "Enregistrement automatique..."
      : autoSaveState === "saved"
        ? "Brouillon auto-enregistré"
        : autoSaveState === "error"
          ? "L'enregistrement automatique n'a pas abouti."
          : autoSaveState === "pending"
            ? "Modifications non enregistrées"
            : "";

  function renderDefaultDesignBadge(campaign: Pick<NewsletterCampaign, "id"> | null) {
    if (!campaign || defaultDesign?.source_campaign_id !== campaign.id) {
      return null;
    }
    return <span className="newsletter-default-design-badge">Design par défaut</span>;
  }

  function renderDefaultDesignOverflow(campaign: NewsletterCampaign | NewsletterCampaignDetail | null) {
    if (!campaign || !hasAssistantNewsletterMetadata(campaign.html_body)) {
      return null;
    }
    const isDefaultDesign = defaultDesign?.source_campaign_id === campaign.id;
    const isMenuOpen = defaultDesignMenuCampaignId === campaign.id;
    return (
      <div
        className="newsletter-detail-overflow-menu"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setDefaultDesignMenuCampaignId(null);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setDefaultDesignMenuCampaignId(null);
          }
        }}
      >
        <button
          type="button"
          className="icon-button newsletter-detail-more-action"
          aria-label="Plus d'actions"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          onClick={(event) => {
            event.stopPropagation();
            setDefaultDesignMenuCampaignId((current) => (current === campaign.id ? null : campaign.id));
          }}
        >
          <Icon iconId="icon-more-horizontal" />
        </button>
        {isMenuOpen ? (
          <div className="newsletter-detail-overflow-dropdown" role="menu" aria-label="Plus d'actions">
            <button
              type="button"
              role="menuitem"
              className="newsletter-detail-overflow-item"
              disabled={isDefaultDesign || setDefaultDesignMutation.isPending}
              onClick={() => {
                setDefaultDesignMenuCampaignId(null);
                setDefaultDesignMutation.mutate(campaign.id);
              }}
            >
              {isDefaultDesign ? "Déjà design par défaut" : "Définir comme design par défaut"}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  function renderComposerSteps(
    steps: readonly { id: ComposerStepId; label: string }[],
    className: string,
    stepClassName: string,
    stepIndexOffset = 0
  ) {
    return (
      <div className={className} role="group" aria-label="Etapes de création">
        {steps.map((step, index) => (
          <button
            key={step.id}
            type="button"
            className={`${stepClassName}${composerStep === step.id ? " is-active" : ""}`}
            aria-current={composerStep === step.id ? "step" : undefined}
            onClick={() => void goToComposerStep(step.id)}
          >
            <span className={`${stepClassName}-index`}>{index + stepIndexOffset}</span>
            <span>{step.label}</span>
          </button>
        ))}
      </div>
    );
  }

  const hasUnsavedComposerChanges = composerDraftSignature !== lastPersistedDraftSignatureRef.current;

  return (
    <>
      <AppChrome
        className="newsletter-page-shell"
        title={settingsOnly ? undefined : "Newsletter"}
          headerActions={settingsOnly ? undefined : (<div className="app-catalog-actions">
                  <button
                    id={activeTab === "drafts" ? "refresh-drafts" : "refresh-campaigns"}
                    className="button app-catalog-secondary-button"
                    type="button"
                    onClick={() => (activeTab === "drafts" ? draftsQuery.refetch() : campaignsQuery.refetch())}
                  >
                    <IconLabel iconId="icon-refresh" label="Rafraîchir" />
                  </button>
                  <button
                    id="open-event-newsletter-generator"
                    type="button"
                    className="button app-catalog-secondary-button"
                    onClick={openEventNewsletterModal}
                  >
                    <IconLabel iconId="icon-mail" label="Générer depuis les événements" />
                  </button>
                  <button id="open-newsletter-composer" type="button" className="button button-primary" onClick={openNewComposer}>
                    <IconLabel iconId="icon-plus-box" label="Nouvelle newsletter" />
                  </button>
                </div>)}
        topBarStart={settingsOnly ? null : (
          <div className="event-view-toggle" role="group" aria-label="Choix de la vue newsletter">
            <button
              className={`event-view-toggle-button${activeTab === "drafts" ? " is-active" : ""}`}
              type="button"
              aria-pressed={activeTab === "drafts"}
              onClick={() => setActiveTab("drafts")}
            >
              <IconLabel iconId="icon-draft" label="Brouillons" />
            </button>
            <button
              className={`event-view-toggle-button${activeTab === "sent" ? " is-active" : ""}`}
              type="button"
              aria-pressed={activeTab === "sent"}
              onClick={() => setActiveTab("sent")}
            >
              <IconLabel iconId="icon-chart" label="Envoyees" />
            </button>
          </div>
        )}
        topBarEnd={isHostedNewsletter ? null : (
          <IconButton
            iconId="icon-settings"
            label="Paramètres"
            active={activeTab === "settings"}
            onClick={openSettings}
          />
        )}
      >
        <AppSurface>
          {defaultDesignStatus.message ? (
            <div className="campaign-feedback" aria-live="polite">
              <p className={statusClassName(defaultDesignStatus)} id="campaign-status">
                {defaultDesignStatus.message}
              </p>
            </div>
          ) : null}

          {activeTab === "settings" ? (!settingsOnly &&
            <AppActionBar
              trailing={
                <button
                  id="save-settings"
                  className={`button button-primary${settingsSaveState === "success" ? " button-success" : ""}`}
                  type="button"
                  disabled={settingsSaveState === "saving"}
                  onClick={handleSettingsSubmit}
                >
                  <IconLabel
                    iconId={settingsSaveIconId}
                    label={
                      settingsSaveState === "saving"
                        ? "Vérification..."
                        : settingsSaveState === "success"
                          ? "Enregistré"
                          : "Enregistrer"
                    }
                  />
                </button>
              }
            />
          ) : (
            <div className="app-catalog-toolbar">
              <div className="app-catalog-toolbar-main">
                <div className="app-catalog-search-shell newsletter-catalog-search-shell">
                  <div className="app-catalog-searchbar">
                    <Icon iconId="icon-search" />
                    <input
                      type="search"
                      value={activeTab === "drafts" ? draftSearch : campaignSearch}
                      placeholder={
                        activeTab === "drafts"
                          ? "Nom du brouillon, sujet, groupe..."
                          : "Nom de campagne, sujet, groupe..."
                      }
                      aria-label={activeTab === "drafts" ? "Rechercher des brouillons" : "Rechercher des campagnes"}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        if (activeTab === "drafts") {
                          setDraftSearch(value);
                        } else {
                          setCampaignSearch(value);
                        }
                      }}
                    />
                    {(activeTab === "drafts" ? draftSearch : campaignSearch).trim() ? (
                      <button
                        type="button"
                        className="app-catalog-clear-search"
                        aria-label="Effacer la recherche"
                        title="Effacer la recherche"
                        onClick={() => (activeTab === "drafts" ? setDraftSearch("") : setCampaignSearch(""))}
                      >
                        <Icon iconId="icon-x" />
                      </button>
                    ) : null}
                  </div>
                  {activeTab === "sent" ? (
                    <button
                      type="button"
                      className={`app-catalog-filter-toggle${activeCampaignFiltersCount > 0 ? " has-active-filters" : ""}${isCampaignFiltersOpen ? " is-open" : ""}`}
                      aria-expanded={isCampaignFiltersOpen}
                      aria-controls="newsletter-campaign-filters"
                      onClick={() => setIsCampaignFiltersOpen((current) => !current)}
                    >
                      <Icon iconId="icon-filter" />
                      <span>Filtres</span>
                      {activeCampaignFiltersCount > 0 ? (
                        <span className="app-catalog-filter-count">{activeCampaignFiltersCount}</span>
                      ) : null}
                    </button>
                  ) : null}
                </div>


              </div>

              <div className="app-catalog-meta">
                <div className="app-catalog-meta-main">
                  <strong>
                    {activeTab === "drafts"
                      ? `${filteredDrafts.length} brouillon${filteredDrafts.length > 1 ? "s" : ""}`
                      : `${filteredCampaigns.length} campagne${filteredCampaigns.length > 1 ? "s" : ""}`}
                  </strong>
                  {(activeTab === "drafts" ? activeDraftFiltersCount : activeCampaignFiltersCount) > 0 ? (
                    <span className="app-catalog-meta-detail">
                      {(activeTab === "drafts" ? activeDraftFiltersCount : activeCampaignFiltersCount)} filtre
                      {(activeTab === "drafts" ? activeDraftFiltersCount : activeCampaignFiltersCount) > 1 ? "s" : ""} actif
                      {(activeTab === "drafts" ? activeDraftFiltersCount : activeCampaignFiltersCount) > 1 ? "s" : ""}
                    </span>
                  ) : null}
                </div>
              </div>

              {activeTab === "sent" && isCampaignFiltersOpen ? (
                <div id="newsletter-campaign-filters" className="app-catalog-advanced-filters">
                  <div className="app-catalog-advanced-grid is-one-column">
                    <label className="app-catalog-advanced-field">
                      <span className="app-catalog-field-label">
                        <Icon iconId="icon-filter" />
                        Statut
                      </span>
                      <div className="toolbar-dropdown">
                        <select
                          className="toolbar-select"
                          value={campaignStatusFilter}
                          onChange={(event) => {
                            const value = event.currentTarget.value as CampaignStatusFilter;
                            setCampaignStatusFilter(value);
                          }}
                        >
                          <option value="all">Tous les statuts</option>
                          <option value="scheduled">Programmee</option>
                          <option value="running">En cours</option>
                          <option value="sent">Envoyee</option>
                          <option value="cancelled">Annulee</option>
                          <option value="failed">Erreur</option>
                        </select>
                      </div>
                    </label>
                  </div>
                </div>
              ) : null}

              {(activeTab === "drafts" && draftSearch.trim()) || (activeTab === "sent" && (campaignSearch.trim() || campaignStatusFilter !== "all")) ? (
                <div className="app-active-filter-chips" aria-label="Filtres actifs">
                  {activeTab === "drafts" && draftSearch.trim() ? (
                    <button type="button" className="app-active-filter-chip" onClick={() => setDraftSearch("")}>
                      <span>{`Recherche: ${draftSearch.trim()}`}</span>
                      <Icon iconId="icon-x" />
                    </button>
                  ) : null}
                  {activeTab === "sent" && campaignSearch.trim() ? (
                    <button type="button" className="app-active-filter-chip" onClick={() => setCampaignSearch("")}>
                      <span>{`Recherche: ${campaignSearch.trim()}`}</span>
                      <Icon iconId="icon-x" />
                    </button>
                  ) : null}
                  {activeTab === "sent" && campaignStatusFilter !== "all" ? (
                    <button type="button" className="app-active-filter-chip" onClick={() => setCampaignStatusFilter("all")}>
                      <span>{`Statut: ${campaignStatusLabel(campaignStatusFilter)}`}</span>
                      <Icon iconId="icon-x" />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}

          {showMissingSmtpAlert && !settingsOnly ? (
            <div className="app-context-alert is-warning newsletter-smtp-alert">
              <div className="app-context-alert-copy">
                <strong>Ajoutez un compte mail</strong>
                <p>Ajoutez un compte mail pour envoyer des tests et lancer vos newsletters.</p>
              </div>
              {activeTab !== "settings" ? (
                <div className="app-context-alert-actions">
                  <button type="button" className="button button-primary" onClick={() => openSettings()}>
                    Ajouter un compte mail
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {activeTab === "drafts" && selectedDraftCount > 0 ? (
            <AppSecondaryBar>
              <button
                id="delete-selected-drafts"
                className="button button-danger"
                type="button"
                disabled={selectedDraftCount === 0}
                onClick={() => {
                  if (
                    selectedDraftCount > 0 &&
                    window.confirm(
                      `Supprimer ${selectedDraftCount} brouillon${selectedDraftCount > 1 ? "s" : ""} ?`
                    )
                  ) {
                    bulkDeleteDraftsMutation.mutate(selectedDraftIds);
                  }
                }}
              >
                <IconLabel
                  iconId="icon-trash"
                  label={
                    selectedDraftCount > 0
                      ? `Supprimer ${selectedDraftCount} brouillon${selectedDraftCount > 1 ? "s" : ""}`
                      : "Supprimer la sélection"
                  }
                />
              </button>
            </AppSecondaryBar>
          ) : null}

          <ModalShell
            isOpen={isEventNewsletterModalOpen}
            title="Generer une newsletter"
            onClose={() => setIsEventNewsletterModalOpen(false)}
          >
            <NewsletterFromEventsBuilder
              events={eventNewsletterEvents}
              period={eventNewsletterPeriod}
              defaultDesign={defaultDesign}
              canOpenNewsletterComposer={true}
              onPeriodChange={setEventNewsletterPeriod}
              onOpenNewsletterDraft={handleOpenEventNewsletterDraft}
            />
          </ModalShell>

          <main className="tab-panels">
            <ModalShell
              isOpen={isComposerOpen}
              title={composerTitle}
              chromeMode="minimal"
              onClose={() => {
                void handleCloseComposer();
              }}
            >
              <div className="newsletter-composer">
                {renderComposerSteps(
                  COMPOSER_STEPS,
                  "newsletter-composer-stepper-compact newsletter-composer-stepper-main",
                  "newsletter-composer-step-pill"
                )}

                {isClassicSwitchDialogOpen ? (
                  <div className="newsletter-inline-dialog-backdrop" role="presentation">
                    <div className="newsletter-inline-dialog" role="dialog" aria-modal="true" aria-labelledby="classic-switch-title">
                      <div className="newsletter-inline-dialog-head">
                        <p className="section-kicker">Passage vers le classique</p>
                        <h3 id="classic-switch-title">Passer à l&apos;éditeur mail classique ?</h3>
                      </div>
                      <div className="newsletter-inline-dialog-body">
                        <p>Vous allez créer une copie de cette newsletter dans l&apos;éditeur mail classique.</p>
                        <p>Votre version créée avec l&apos;assistant sera conservée en brouillon.</p>
                        <p>La copie classique pourra être modifiée librement, mais ne pourra plus revenir dans l&apos;assistant.</p>
                      </div>
                      <div className="newsletter-inline-dialog-actions">
                        <button
                          type="button"
                          className="button"
                          onClick={() => setIsClassicSwitchDialogOpen(false)}
                          disabled={isClassicSwitchPending}
                        >
                          Annuler
                        </button>
                        <button
                          type="button"
                          className="button button-primary"
                          onClick={() => void handleConfirmClassicSwitch()}
                          disabled={isClassicSwitchPending}
                        >
                          {isClassicSwitchPending ? "Création..." : "Créer une copie classique"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {isTestEmailDialogOpen ? (
                  <div className="newsletter-inline-dialog-backdrop" role="presentation">
                    <div className="newsletter-inline-dialog" role="dialog" aria-modal="true" aria-labelledby="test-email-title">
                      <div className="newsletter-inline-dialog-head">
                        <p className="section-kicker">Email de test</p>
                        <h3 id="test-email-title">Envoyer un email de test</h3>
                      </div>
                      <div className="newsletter-inline-dialog-body newsletter-test-email-dialog-body">
                        <label>
                          Adresse email
                          <input
                            id="test-email-recipient"
                            type="email"
                            placeholder="alice@example.com"
                            value={testEmailRecipient}
                            onChange={(event) => {
                              setTestEmailRecipient(event.target.value);
                              if (testEmailStatus.message) {
                                setTestEmailState("idle");
                                setTestEmailStatus({ message: "", tone: "neutral" });
                              }
                            }}
                            disabled={testEmailState === "sending"}
                          />
                        </label>

                        <p className="muted">Un message de test sera envoyé avec le contenu actuel de la newsletter.</p>
                      </div>
                      <div className="campaign-feedback newsletter-test-email-feedback" aria-live="polite">
                        <p className={statusClassName(testEmailStatus)} hidden={!testEmailStatus.message}>
                          {testEmailStatus.message}
                        </p>
                      </div>
                      <div className="newsletter-inline-dialog-actions">
                        <button
                          type="button"
                          className="button"
                          onClick={closeTestEmailDialog}
                          disabled={testEmailState === "sending"}
                        >
                          {testEmailState === "sent" ? "Fermer" : "Annuler"}
                        </button>
                        <button
                          type="button"
                          className="button button-primary"
                          onClick={() => void handleSendTestEmail()}
                          disabled={testEmailState === "sending" || testEmailState === "sent"}
                        >
                          {testEmailState === "sending"
                            ? "Envoi en cours..."
                            : testEmailState === "sent"
                              ? "Envoyé"
                              : "Envoyer le test"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {composerStep === "naming" ? (
                  <section className="newsletter-composer-panel newsletter-composer-panel-naming">
                    <div className="newsletter-naming-card">
                      <div className="newsletter-naming-head">
                        <p className="section-kicker">Étape 0</p>
                        <h3>Nommer le brouillon</h3>
                      </div>

                      <label className="newsletter-naming-field">
                        <span>Nom de campagne</span>
                        <input
                          id="campaign_name"
                          type="text"
                          className={hasFieldError(campaignFieldErrors, "campaign_name") ? "is-field-invalid" : ""}
                          aria-invalid={hasFieldError(campaignFieldErrors, "campaign_name") || undefined}
                          placeholder="Newsletter mars"
                          value={campaignForm.name}
                          onChange={(event) => {
                            clearCampaignFieldError("campaign_name");
                            setCampaignForm((current) => ({ ...current, name: event.target.value }));
                          }}
                        />
                      </label>
                    </div>
                  </section>
                ) : null}

                {composerStep === "content" ? (
                  <section className="newsletter-composer-panel newsletter-composer-content-layout">
                    <div className="newsletter-composer-main">
                      <div className="newsletter-composer-content-head is-toolbar-only">
                        <div className="newsletter-content-compact-bar">
                          <div className="newsletter-content-compact-meta">
                            <span className="newsletter-content-mode-chip">
                              {composerContentMode === "assistant" ? "Assistant visuel" : "Éditeur classique"}
                            </span>
                            <p className="newsletter-autosave-status" aria-live="polite" hidden={!autoSaveLabel}>
                              {autoSaveLabel}
                            </p>
                            {composerContentMode === "assistant" ? (
                              <button type="button" className="newsletter-classic-switch-link" onClick={handleSwitchToClassic}>
                                Passer sur l&apos;éditeur mail classique &gt;
                              </button>
                            ) : classicModeCanReturnToAssistant ? (
                              <button type="button" className="newsletter-classic-switch-link" onClick={handleSwitchBackToAssistant}>
                                Revenir à l&apos;assistant
                              </button>
                            ) : (
                              <span className="newsletter-content-compact-note">Version libre du mail</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {contentGuardrailAlert ? (
                        <div
                          className={`newsletter-inline-guardrail-banner${
                            contentGuardrailAlert.tone === "warning" ? " newsletter-inline-guardrail-banner-warning" : ""
                          }`}
                        >
                          <div className="newsletter-inline-guardrail-banner-head">
                            {contentGuardrailAlert.tone === "warning" ? (
                              <span className="newsletter-inline-guardrail-banner-badge">Attention</span>
                            ) : null}
                            <strong>{contentGuardrailAlert.title}</strong>
                          </div>
                          <p>{contentGuardrailAlert.message}</p>
                          {contentGuardrailAlert.points?.length ? (
                            <ul className="newsletter-guardrail-points">
                              {contentGuardrailAlert.points.map((point) => (
                                <li key={point}>{point}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}

                      {composerContentMode === "assistant" ? (
                        <div className={hasFieldError(campaignFieldErrors, "campaign_editor") ? "is-field-invalid" : ""}>
                          <AssistantVisualEditor
                            draft={assistantDraft}
                            previewHtml={extractEmailPreviewHtml(assistantRenderedHtml)}
                            isPreviewLoading={isAssistantRenderPending}
                            selectedSectionId={selectedAssistantSectionId}
                            onSelectSection={(sectionId) => {
                              clearCampaignFieldError("campaign_editor");
                              setSelectedAssistantSectionId(sectionId);
                            }}
                            onUpdateDraft={(patch) => {
                              clearCampaignFieldError("campaign_editor");
                              setAssistantDraft((current) => ({ ...current, ...patch }));
                            }}
                            onUpdateTheme={(patch) => {
                              clearCampaignFieldError("campaign_editor");
                              setAssistantDraft((current) => ({
                                ...current,
                                ...resolveAssistantImageLayoutsForThemeChange(current, patch),
                                theme: (() => {
                                  const nextTheme = {
                                    ...current.theme,
                                    ...patch,
                                  };

                                  if (patch.style && patch.style !== current.theme.style) {
                                    const previousDefaults = ASSISTANT_THEME_STYLE_DEFAULTS[current.theme.style];
                                    if (!current.theme.primary_color || current.theme.primary_color === previousDefaults.primary_color) {
                                      nextTheme.primary_color = "";
                                    }
                                    if (!current.theme.accent_color || current.theme.accent_color === previousDefaults.accent_color) {
                                      nextTheme.accent_color = "";
                                    }
                                  }

                                  return nextTheme;
                                })(),
                              }));
                            }}
                            onUpdateSection={(sectionId, patch) => {
                              clearCampaignFieldError("campaign_editor");
                              updateAssistantSection(sectionId, patch);
                            }}
                            onOpenMediaLibrary={(sectionId) => {
                              clearCampaignFieldError("campaign_editor");
                              setMediaTarget({ kind: "assistant-section", sectionId });
                            }}
                            onOpenLogoMediaLibrary={() => {
                              clearCampaignFieldError("campaign_editor");
                              setMediaTarget({ kind: "assistant-logo" });
                            }}
                            onOpenHeroMediaLibrary={() => {
                              clearCampaignFieldError("campaign_editor");
                              setMediaTarget({ kind: "assistant-hero" });
                            }}
                            onMoveSection={(sectionId, direction) => {
                              clearCampaignFieldError("campaign_editor");
                              moveAssistantSection(sectionId, direction);
                            }}
                            onAddSection={(insertionIndex) => {
                              clearCampaignFieldError("campaign_editor");
                              addAssistantSection(insertionIndex);
                            }}
                            onDuplicateSection={(sectionId) => {
                              clearCampaignFieldError("campaign_editor");
                              duplicateAssistantSection(sectionId);
                            }}
                            onRemoveSection={(sectionId) => {
                              clearCampaignFieldError("campaign_editor");
                              removeAssistantSection(sectionId);
                            }}
                          />
                        </div>
                      ) : null}

                      {composerContentMode === "richText" ? (
                        <section className="newsletter-classic-mode-panel">
                          {!classicModeCanReturnToAssistant ? (
                            <div className="newsletter-classic-mode-note">
                              Les emails modifies librement ne peuvent plus revenir dans l&apos;assistant, car leur
                              structure n&apos;est plus decoupee en sections.
                            </div>
                          ) : null}

                          <div className={hasFieldError(campaignFieldErrors, "campaign_editor") ? "is-field-invalid" : ""}>
                            <RichTextEditor
                              editorId="campaign_editor"
                              value={campaignForm.html_body}
                              placeholder="Redigez votre email ici."
                              onRequestInsertImage={(insertImage) => {
                                richTextImageInsertRef.current = insertImage;
                                setMediaTarget({ kind: "richtext" });
                              }}
                              onChange={(value) => {
                                clearCampaignFieldError("campaign_editor");
                                setCampaignForm((current) => ({ ...current, html_body: value }));
                              }}
                            />
                          </div>
                        </section>
                      ) : null}
                    </div>

                  </section>
                ) : null}

                {composerStep === "audience" ? (
                  <section className="newsletter-composer-panel newsletter-composer-panel-audience">
                    <div className="newsletter-form-grid newsletter-form-grid-2">
                      <label>
                        Objet
                        <input
                          id="campaign_subject"
                          type="text"
                          className={hasFieldError(campaignFieldErrors, "campaign_subject") ? "is-field-invalid" : ""}
                          aria-invalid={hasFieldError(campaignFieldErrors, "campaign_subject") || undefined}
                          placeholder="Nos nouveautés"
                          value={campaignForm.subject}
                          onChange={(event) => {
                            clearCampaignFieldError("campaign_subject");
                            setCampaignForm((current) => ({ ...current, subject: event.target.value }));
                          }}
                        />
                      </label>

                      <label>
                        Envoi
                        <input
                          id="campaign_scheduled_at"
                          type="datetime-local"
                          className={hasFieldError(campaignFieldErrors, "campaign_scheduled_at") ? "is-field-invalid" : ""}
                          aria-invalid={hasFieldError(campaignFieldErrors, "campaign_scheduled_at") || undefined}
                          value={campaignForm.scheduled_at}
                          onChange={(event) => {
                            clearCampaignFieldError("campaign_scheduled_at");
                            setCampaignForm((current) => ({ ...current, scheduled_at: event.target.value }));
                          }}
                        />
                      </label>
                    </div>

                    <div className="feature-switches feature-switches-inline newsletter-composer-switches">
                      <label className="switch-card" htmlFor="campaign_tracking_enabled">
                        <div className="switch-copy">
                          <span className="switch-title">Mesurer les ouvertures</span>
                          <span className="switch-description">
                            Nombre et taux d’ouvertures observées, sans liste de lecteurs. {engagementQuery.data?.enabled && engagementQuery.data?.operational && campaignForm.tracking_enabled ? "Activé selon les réglages de votre association." : "Disponible après activation dans les réglages de l’espace."}
                          </span>
                        </div>
                        <span className="switch-control switch-control-mantine">
                          <Switch
                            id="campaign_tracking_enabled"
                            disabled={isHostedNewsletter && !campaignForm.tracking_enabled && (engagementQuery.isError || !engagementQuery.data?.enabled || !engagementQuery.data?.operational)}
                            size="lg"
                            color="easyBlue"
                            checked={campaignForm.tracking_enabled}
                            onChange={(event) =>
                              setCampaignForm((current) => ({
                                ...current,
                                tracking_enabled: event.target.checked,
                              }))
                            }
                            onLabel="On"
                            offLabel="Off"
                          />
                        </span>
                      </label>

                      {campaignForm.unsubscribe_enabled || composerContentMode === "assistant" ? <p>Un lien de désinscription est inclus dans chaque newsletter.</p> : <div role="alert"><p>Ce brouillon ancien ne contient pas de lien de désinscription. Ajoutez-le avant de programmer l’envoi.</p><button type="button" className="button" onClick={() => setCampaignForm(current => ({...current,unsubscribe_enabled:true}))}>Ajouter le lien obligatoire</button></div>}
                    </div>

                    <p className="muted">Pour vos newsletters, utilisez une liste de personnes qui attendent vos messages.</p>
                    <NewsletterProtectionPanel decision={guardrailsQuery.data?.protection} scope="campaign" loading={guardrailsQuery.isFetching} error={guardrailsQuery.isError} />

                    {audienceGuardrailAlert ? (
                      <div
                        className={`newsletter-inline-guardrail-banner${
                          audienceGuardrailAlert.tone === "warning" ? " newsletter-inline-guardrail-banner-warning" : ""
                        }`}
                      >
                        <div className="newsletter-inline-guardrail-banner-head">
                          {audienceGuardrailAlert.tone === "warning" ? (
                            <span className="newsletter-inline-guardrail-banner-badge">Attention</span>
                          ) : null}
                          <strong>{audienceGuardrailAlert.title}</strong>
                        </div>
                        <p>{audienceGuardrailAlert.message}</p>
                        {audienceGuardrailAlert.points?.length ? (
                          <ul className="newsletter-guardrail-points">
                            {audienceGuardrailAlert.points.map((point) => (
                              <li key={point}>{point}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}

                    <Alert
                      id="campaign-public-url-note"
                      className={`inline-notice newsletter-inline-alert${publicUrlNotice?.isWarning ? " is-warning" : ""}`}
                      variant="light"
                      radius="xl"
                      color={publicUrlNotice?.isWarning ? "easyPeach" : "easyBlue"}
                      hidden={!publicUrlNotice}
                    >
                      {publicUrlNotice?.message}
                    </Alert>

                    <div className="newsletter-recipient-block">
                      <label htmlFor="campaign_recipients_input">Destinataires</label>
                      <div
                        className={`newsletter-recipient-field${hasFieldError(campaignFieldErrors, "campaign_recipients") ? " is-invalid" : ""}`}
                        onClick={() => recipientInputRef.current?.focus()}
                      >
                        {selectedContactGroups.map((group) => (
                          <span key={group.id} className="newsletter-recipient-token is-group">
                            <span className="newsletter-group-dot" style={{ backgroundColor: group.color || "#d5dced" }} />
                            <span>{group.name}</span>
                            <button type="button" aria-label={`Retirer ${group.name}`} onClick={() => removeSelectedContactGroup(group.id)}>
                              <Icon iconId="icon-x" size={14} />
                            </button>
                          </span>
                        ))}

                        {visibleManualRecipients.map((email) => (
                          <span key={email} className="newsletter-recipient-token">
                            <span>{email}</span>
                            <button type="button" aria-label={`Retirer ${email}`} onClick={() => removeManualRecipient(email)}>
                              <Icon iconId="icon-x" size={14} />
                            </button>
                          </span>
                        ))}

                        {hiddenManualRecipientsCount > 0 ? (
                          <span className="newsletter-recipient-token is-count">+{hiddenManualRecipientsCount} emails</span>
                        ) : null}

                        <input
                          ref={recipientInputRef}
                          id="campaign_recipients_input"
                          type="text"
                          className="newsletter-recipient-input"
                          placeholder="Saisir un email, coller une liste, ou rechercher un groupe"
                          value={recipientEntryValue}
                          onChange={(event) => {
                            clearCampaignFieldError("campaign_recipients");
                            setRecipientEntryValue(event.target.value);
                          }}
                          onFocus={() => setIsRecipientFieldFocused(true)}
                          onBlur={() => window.setTimeout(() => setIsRecipientFieldFocused(false), 120)}
                          onKeyDown={handleRecipientInputKeyDown}
                          onPaste={handleRecipientInputPaste}
                        />
                      </div>

                      <div className="newsletter-recipient-summary">
                        <p className="muted" id="recipient-preview">
                          {recipientAnalysis.valid.length} email{recipientAnalysis.valid.length > 1 ? "s" : ""} manuel{recipientAnalysis.valid.length > 1 ? "s" : ""}
                          {campaignForm.contact_group_ids.length > 0
                            ? ` + ${resolvedGroupRecipientsCount} via groupe${campaignForm.contact_group_ids.length > 1 ? "s" : ""}`
                            : ""}
                          . Total unique estimé: {totalAudienceRecipients.length}.
                        </p>
                        {!hasContactsIntegration ? (
                          <p className="muted newsletter-recipient-note">Les groupes sont disponibles dans l'espace Relyo.</p>
                        ) : null}
                        {contactsGroupsQuery.isLoading && hasContactsIntegration ? (
                          <p className="muted newsletter-recipient-note">Chargement des groupes...</p>
                        ) : null}
                        {groupResolutionQuery.error instanceof Error ? (
                          <p className="status-line error">{groupResolutionQuery.error.message}</p>
                        ) : null}
                      </div>

                      {(isRecipientFieldFocused || recipientEntryValue.trim()) && recipientGroupSuggestions.length > 0 ? (
                        <div className="newsletter-recipient-suggestions">
                          {recipientGroupSuggestions.map((group) => (
                            <button
                              key={group.id}
                              type="button"
                              className="newsletter-recipient-suggestion"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                clearCampaignFieldError("campaign_recipients");
                                applySelectedContactGroups([...campaignForm.contact_group_ids, group.id]);
                                setRecipientEntryValue("");
                                focusRecipientInput();
                              }}
                            >
                              <span className="newsletter-group-dot" style={{ backgroundColor: group.color || "#d5dced" }} />
                              <span>{group.name}</span>
                              <span className="newsletter-recipient-suggestion-count">
                                {group.contact_count} contact{group.contact_count > 1 ? "s" : ""}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}

                      <div id="invalid-email-notice" className="invalid-email-notice" hidden={invalidRecipientTokens.length === 0}>
                        <div className="newsletter-invalid-head">
                          <strong id="invalid-email-title">
                            {invalidRecipientTokens.length} entrée{invalidRecipientTokens.length > 1 ? "s" : ""} ignorée{invalidRecipientTokens.length > 1 ? "s" : ""}
                          </strong>
                          <button type="button" className="button button-ghost" onClick={clearInvalidRecipients}>
                            Nettoyer
                          </button>
                        </div>
                        <div id="invalid-email-list" className="invalid-email-list">
                          {invalidRecipientTokens.map((email) => (
                            <span key={email} className="invalid-email-chip">
                              {email}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </section>
                ) : null}

                {composerStep === "review" ? (
                  <section className="newsletter-composer-panel newsletter-review-layout">
                    <div className="newsletter-review-main">
                      <NewsletterProtectionPanel decision={guardrailsQuery.data?.protection} scope="campaign" loading={guardrailsQuery.isFetching} error={guardrailsQuery.isError} />
                      <div className="newsletter-review-grid">
                        <article className="newsletter-review-card">
                          <span>Campagne</span>
                          <strong>{reviewForm.name || "-"}</strong>
                        </article>
                        <article className="newsletter-review-card">
                          <span>Objet</span>
                          <strong>{reviewForm.subject || "-"}</strong>
                        </article>
                        <article className="newsletter-review-card">
                          <span>Envoi</span>
                          <strong>{formatDateTimeLabel(reviewForm.scheduled_at)}</strong>
                        </article>
                        <article className="newsletter-review-card">
                          <span>Destinataires</span>
                          <strong>{totalAudienceRecipients.length}</strong>
                        </article>
                      </div>

                      <div className="newsletter-review-list">
                        <div className="newsletter-review-row">
                          <span>Emails manuels</span>
                          <strong>{reviewForm.raw_recipients ? analyzeRecipients(reviewForm.raw_recipients).valid.length : 0}</strong>
                        </div>
                        <div className="newsletter-review-row">
                          <span>Groupes</span>
                          <strong>{reviewForm.contact_group_ids.length}</strong>
                        </div>
                        <div className="newsletter-review-row">
                          <span>Options</span>
                          <strong>{enabledComposerFeatures.length > 0 ? enabledComposerFeatures.join(", ") : "Aucune"}</strong>
                        </div>
                      </div>

                      {selectedContactGroups.length > 0 ? (
                        <div className="newsletter-group-summary-list">
                          {selectedContactGroups.map((group) => (
                            <span key={group.id} className="newsletter-group-chip">
                              <span className="newsletter-group-dot" style={{ backgroundColor: group.color || "#d5dced" }} />
                              {group.name}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      {invalidRecipientTokens.length > 0 ? (
                        <div className="invalid-email-notice">
                          <strong>Des entrées ont été ignorées pendant la préparation.</strong>
                          <div className="invalid-email-list">
                            {invalidRecipientTokens.map((value) => (
                              <span key={value} className="invalid-email-chip">
                                {value}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <aside className="newsletter-preview-panel">
                      {progressiveChangePanel ? (
                        <article className="newsletter-progress-panel newsletter-review-progress-panel">
                          <div className="newsletter-progress-panel-head">
                            <div>
                              <p className="section-kicker">Mode progressif</p>
                              <strong>{progressiveChangePanel.title}</strong>
                            </div>
                            {guardrailsQuery.data?.provider_label ? (
                              <span className="newsletter-guardrail-provider">{guardrailsQuery.data.provider_label}</span>
                            ) : null}
                          </div>
                          <p className="muted newsletter-guardrail-message">{progressiveChangePanel.message}</p>
                          <ul className="newsletter-guardrail-points">
                            {progressiveChangePanel.points.map((point) => (
                              <li key={point}>{point}</li>
                            ))}
                          </ul>
                          {guardrailsQuery.data?.next_send_window_at ? (
                            <p className="muted newsletter-guardrail-next-window">
                              Prochaine reprise prévue : {formatDateTimeLabel(guardrailsQuery.data.next_send_window_at)}
                            </p>
                          ) : null}
                        </article>
                      ) : null}
                      <div className="panel-head newsletter-preview-headline">
                        <div>
                          <h3>Aperçu</h3>
                        </div>
                      </div>
                      {reviewHasPreview ? (
                        <div className="newsletter-email-preview" dangerouslySetInnerHTML={{ __html: reviewPreviewHtml }} />
                      ) : isReviewPreviewLoading ? (
                        <div className="empty-state newsletter-preview-empty">
                          <p>Generation de l&apos;aperçu en cours...</p>
                        </div>
                      ) : (
                        <div className="empty-state newsletter-preview-empty">
                          <p>Le contenu de l&apos;email apparaîtra ici.</p>
                        </div>
                      )}
                    </aside>
                  </section>
                ) : null}

                <div className="campaign-feedback" aria-live="polite">
                  <p className={statusClassName(campaignStatus)} id="campaign-status" hidden={!campaignStatus.message}>
                    {campaignStatus.message}
                  </p>
                </div>

                <div className="campaign-footer newsletter-composer-footer">
                  <button id="save-draft" type="button" className="button" onClick={handleSaveDraft}>
                    <IconLabel
                      iconId="icon-pen"
                      label={editingDraftId ? "Mettre à jour le brouillon" : "Enregistrer le brouillon"}
                    />
                  </button>

                  <div className="campaign-actions">
                    {composerContentMode === "assistant" && editingDraftId ? (
                      defaultDesign?.source_campaign_id === editingDraftId ? (
                        <span className="newsletter-default-design-inline-state">Design par défaut</span>
                      ) : (
                        <button
                          type="button"
                          className="button"
                          onClick={async () => {
                            if (hasUnsavedComposerChanges) {
                              const saved = await handleAutoSaveDraft();
                              if (
                                !saved ||
                                latestComposerDraftSignatureRef.current !== lastPersistedDraftSignatureRef.current
                              ) {
                                setCampaignStatus({
                                  message: "Enregistrez le brouillon avant de le définir comme design par défaut.",
                                  tone: "error",
                                });
                                return;
                              }
                            }
                            const sourceDraftId = editingDraftIdRef.current;
                            if (sourceDraftId) {
                              setDefaultDesignMutation.mutate(sourceDraftId);
                            }
                          }}
                          disabled={saveDraftMutation.isPending || setDefaultDesignMutation.isPending}
                        >
                          <IconLabel iconId="icon-star" label="Définir comme design par défaut" />
                        </button>
                      )
                    ) : null}

                    {composerStep !== "naming" && composerStep !== "review" ? (
                      <button
                        id="send-test-email"
                        type="button"
                        className="button"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          openTestEmailDialog();
                        }}
                        onClick={openTestEmailDialog}
                        disabled={sesRouteBlocksSending}
                        title={
                          sesRouteBlocksSending
                            ? "Disponible après vérification de la configuration du domaine."
                            : undefined
                        }
                      >
                        <IconLabel iconId="icon-mail" label="Envoyer un test" />
                      </button>
                    ) : null}

                    {composerStepIndex > 0 ? (
                      <button
                        type="button"
                        className="button"
                        onClick={() => setComposerStep(COMPOSER_STEPS[composerStepIndex - 1].id)}
                      >
                        Retour
                      </button>
                    ) : null}

                    {composerStep !== "review" ? (
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={() => void goToComposerStep(COMPOSER_STEPS[composerStepIndex + 1].id)}
                      >
                        {composerStep === "naming" ? "Commencer le contenu" : "Continuer"}
                      </button>
                    ) : (
                      <>
                        <button
                          id="send-test-email"
                          type="button"
                          className="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            openTestEmailDialog();
                          }}
                          onClick={openTestEmailDialog}
                          disabled={sesRouteBlocksSending}
                          title={
                            sesRouteBlocksSending
                              ? "Disponible après vérification de la configuration du domaine."
                              : undefined
                          }
                        >
                          <IconLabel iconId="icon-mail" label="Envoyer un test" />
                        </button>
                        <button
                          id="create-campaign"
                          type="button"
                          className="button button-primary"
                          onClick={handleCreateCampaign}
                          disabled={sesRouteBlocksSending}
                          title={
                            sesRouteBlocksSending
                              ? "Disponible après vérification de la configuration du domaine."
                              : undefined
                          }
                        >
                          <IconLabel iconId="icon-time" label="Planifier l'envoi" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </ModalShell>

          <section className={`tab-panel${activeTab === "drafts" ? " is-active" : ""}`} hidden={activeTab !== "drafts"}>
            <div className="newsletter-split-view">
              <section className="panel panel-primary newsletter-master-panel">
                <div className="panel-head newsletter-master-head">
                  <div>
                    <p className="section-kicker">Préparation</p>
                    <h2>Brouillons</h2>
                  </div>
                  <div className="newsletter-master-head-actions">
                    {filteredDrafts.length > 0 ? (
                      <label className="newsletter-master-select-all">
                        <input
                          type="checkbox"
                          checked={areAllDraftsSelected}
                          onChange={toggleAllDraftsSelection}
                        />
                        <span>Tout sélectionner</span>
                      </label>
                    ) : null}
                    <span className="newsletter-master-count">{filteredDrafts.length}</span>
                  </div>
                </div>

                {drafts.length === 0 ? (
                  <div className="app-empty-state newsletter-master-empty newsletter-master-empty-state">
                    <div className="app-empty-state-visual">
                      <Icon iconId="icon-mail" size={36} />
                    </div>
                    <div className="app-empty-state-copy">
                      <h3>Créez votre première newsletter</h3>
                      <p>Commencez par un brouillon. Vous pourrez envoyer un test dès que votre compte mail sera ajouté.</p>
                    </div>
                    <div className="app-empty-state-actions">
                      <button
                        id="open-newsletter-composer-empty"
                        type="button"
                        className="button button-primary"
                        onClick={openNewComposer}
                      >
                        <IconLabel iconId="icon-pen" label="Créer une newsletter" />
                      </button>
                    </div>
                  </div>
                ) : filteredDrafts.length === 0 ? (
                  <div className="empty-state newsletter-master-empty">Aucun brouillon ne correspond aux filtres.</div>
                ) : (
                  <div id="draft-table-body" className="newsletter-master-list" role="listbox" aria-label="Brouillons">
                    {filteredDrafts.map((draft) => (
                      <div
                        key={draft.id}
                        role="button"
                        tabIndex={0}
                        className={`newsletter-master-item${draft.id === selectedDraftId ? " is-selected" : ""}`}
                        aria-selected={draft.id === selectedDraftId}
                        onClick={() => setSelectedDraftId(draft.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedDraftId(draft.id);
                          }
                        }}
                      >
                        <div className="newsletter-master-item-select">
                          <label
                            className="newsletter-master-checkbox"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDraftIds.includes(draft.id)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => toggleDraftSelection(draft.id)}
                            />
                            <span>Sélectionner {draft.name || "ce brouillon"}</span>
                          </label>
                        </div>
                        <div className="newsletter-master-item-topline">
                          <strong>{draft.name || "Brouillon sans titre"}</strong>
                          <span className={campaignStatusClassName("draft")}>{campaignStatusLabel("draft")}</span>
                        </div>
                        <p className="newsletter-master-item-subtitle">{draft.subject || "Sans objet"}</p>
                        <div className="newsletter-master-item-meta">
                          <span>{formatDateTimeLabel(draft.updated_at)}</span>
                          <span>{draft.total_recipient_count || 0} destinataire{(draft.total_recipient_count || 0) > 1 ? "s" : ""}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="panel panel-primary newsletter-detail-panel newsletter-draft-detail-panel">
                {!selectedDraft ? (
                  <div className="empty-state newsletter-detail-empty">
                    Sélectionnez un brouillon pour voir son aperçu et ses informations.
                  </div>
                ) : (
                  <div className="newsletter-detail-stack">
                    <div className="newsletter-detail-two-column is-draft">
                      <div className="newsletter-detail-info-column">
                        <div className="newsletter-detail-header">
                          <div>
                            <p className="section-kicker">Brouillon sélectionné</p>
                            <h2>{selectedDraft.name || "Brouillon sans titre"}</h2>
                            <p className="muted">
                              {selectedDraft.subject || "Sans objet"} · mis à jour le {formatDateTimeLabel(selectedDraft.updated_at)}
                            </p>
                          </div>
                          <div className="newsletter-detail-status-stack">
                            <span className={campaignStatusClassName("draft")}>{campaignStatusLabel("draft")}</span>
                            {renderDefaultDesignBadge(selectedDraft)}
                          </div>
                        </div>

                        <div className="newsletter-detail-metrics">
                          <article className="newsletter-detail-metric">
                            <span>Dernière mise à jour</span>
                            <strong className="newsletter-detail-short-date" title={formatDateTimeLabel(selectedDraft.updated_at)}>
                              {formatShortDateLabel(selectedDraft.updated_at)}
                            </strong>
                          </article>
                          <article className="newsletter-detail-metric">
                            <span>Créneau saisi</span>
                            <strong className="newsletter-detail-short-date" title={formatDateTimeLabel(selectedDraft.scheduled_at)}>
                              {formatShortDateLabel(selectedDraft.scheduled_at)}
                            </strong>
                          </article>
                          <article className="newsletter-detail-metric">
                            <span>Destinataires</span>
                            <strong>{selectedDraft.total_recipient_count || selectedDraftRecipientAnalysis.valid.length}</strong>
                          </article>
                          <article className="newsletter-detail-metric">
                            <span>Groupes</span>
                            <strong>{selectedDraft.contact_group_labels?.length || 0}</strong>
                          </article>
                        </div>

                        {selectedDraft.contact_group_labels?.length ? (
                          <div className="newsletter-detail-chips">
                            {selectedDraft.contact_group_labels.map((label) => (
                              <span key={label} className="newsletter-group-chip">
                                {label}
                              </span>
                            ))}
                          </div>
                        ) : null}

                        {selectedDraft.scheduled_at ? (
                          <p className="muted">
                            Ce brouillon ne sera pas envoyé tant que vous n&apos;aurez pas cliqué sur « Planifier l&apos;envoi ».
                          </p>
                        ) : null}
                      </div>

                      <aside className="newsletter-detail-visual-column">
                        <div className="newsletter-detail-actions">
                          <button
                            type="button"
                            className="button"
                            onClick={() => openDraftInEditor(selectedDraft)}
                          >
                            <IconLabel iconId="icon-login" label="Continuer" />
                          </button>
                          <button
                            type="button"
                            className="button button-danger"
                            onClick={() => {
                              if (window.confirm("Supprimer ce brouillon ?")) {
                                deleteDraftMutation.mutate(selectedDraft.id);
                              }
                            }}
                          >
                            <IconLabel iconId="icon-trash" label="Supprimer" />
                          </button>
                          {renderDefaultDesignOverflow(selectedDraft)}
                        </div>
                        <NewsletterDetailPreview campaign={selectedDraft} variant="draft" />
                      </aside>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </section>

          <section className={`tab-panel${activeTab === "sent" ? " is-active" : ""}`} hidden={activeTab !== "sent"}>
            <div className="newsletter-split-view">
              <section className="panel panel-primary newsletter-master-panel">
                <div className="panel-head newsletter-master-head">
                  <div>
                    <p className="section-kicker">Historique</p>
                    <h2>Campagnes envoyees</h2>
                  </div>
                  <span className="newsletter-master-count">{filteredCampaigns.length}</span>
                </div>

                {campaigns.length === 0 ? (
                  <div className="empty-state newsletter-master-empty">Aucune campagne pour le moment.</div>
                ) : filteredCampaigns.length === 0 ? (
                  <div className="empty-state newsletter-master-empty">Aucune campagne ne correspond aux filtres.</div>
                ) : (
                  <div id="campaign-table-body" className="newsletter-master-list" role="listbox" aria-label="Campagnes envoyees">
                    {filteredCampaigns.map((campaign) => (
                      <button
                        key={campaign.id}
                        type="button"
                        className={`newsletter-master-item${campaign.id === selectedCampaignId ? " is-selected" : ""}`}
                        aria-selected={campaign.id === selectedCampaignId}
                        onClick={() => {
                          setSelectedCampaignId(campaign.id);
                          setActiveTab("sent");
                        }}
                      >
                        <div className="newsletter-master-item-topline">
                          <strong>{campaign.name}</strong>
                          <span className={campaignDeliveryClassName(campaign)}>
                            {campaignDeliveryLabel(campaign)}
                          </span>
                        </div>
                        <p className="newsletter-master-item-subtitle">
                          {campaign.contact_group_labels?.length
                            ? `Groupes: ${campaign.contact_group_labels.join(", ")}`
                            : campaign.subject || "Sans objet"}
                        </p>
                        <div className="newsletter-master-item-meta">
                          <span>{formatDateTimeLabel(campaign.scheduled_at)}</span>
                          <span>{campaign.sent_count || 0} / {campaign.total_recipient_count || 0}</span>
                        </div>
                        {(campaign.delivery_progress_percent || 0) > 0 && (campaignDeliveryLabel(campaign) === "En cours d'envoi" || campaignIsSuspended(campaign)) ? (
                          <div className="newsletter-campaign-progress">
                            <div className="newsletter-campaign-progress-head">
                              <span>{campaign.delivery_progress_percent}% envoyé</span>
                              <span>
                                {campaign.sent_count || 0} / {campaign.total_recipient_count || 0}
                              </span>
                            </div>
                            <div className="newsletter-campaign-progress-bar" aria-hidden="true">
                              <span style={{ width: `${campaign.delivery_progress_percent || 0}%` }} />
                            </div>
                            <p className="newsletter-campaign-progress-note">
                              {campaign.guardrail_summary_title || "Le reste partira automatiquement par étapes."}
                            </p>
                          </div>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section className="panel panel-primary newsletter-detail-panel" id="report-panel">
                <div id="report-empty" className="empty-state newsletter-detail-empty" hidden={Boolean(detail) || detailQuery.isLoading}>
                  Sélectionnez une campagne pour voir le détail.
                </div>

                {detailQuery.isLoading && selectedCampaignId ? (
                  <div className="empty-state newsletter-detail-empty">Chargement du rapport de campagne...</div>
                ) : null}

                <div id="report-content" className="report newsletter-detail-stack" hidden={!detail || detailQuery.isLoading}>
                  <div className="newsletter-detail-two-column is-sent">
                    <div className="newsletter-detail-info-column">
                      <div className="newsletter-detail-header">
                        <div>
                          <p className="section-kicker">Rapport</p>
                          <h2 id="report-selected-name">{detail ? detail.name : "Aucune campagne sélectionnée"}</h2>
                          <p id="report-selected-meta" className="muted">
                            {detail
                              ? `Planifiée le ${formatDateTimeLabel(detail.scheduled_at)} · ${detail.total_recipient_count || 0} destinataires`
                              : "Choisissez une campagne dans la liste pour afficher le rapport détaillé."}
                          </p>
                          {detail?.contact_group_labels?.length ? (
                            <p className="muted">Groupes utilises: {detail.contact_group_labels.join(", ")}</p>
                          ) : null}
                        </div>
                        <div className="newsletter-detail-status-stack">
                          <span className={campaignDeliveryClassName(detail)}>{campaignDeliveryLabel(detail)}</span>
                          {renderDefaultDesignBadge(detail)}
                        </div>
                      </div>

                      {campaignIsSuspended(detail) && detail?.protection && <NewsletterRecoveryNotice
                        key={`recovery:${detail.id}`} decision={detail.protection} addresses={detail.bad_addresses || []}
                        unavailable={detailQuery.isFetching || detailQuery.isError}
                        onRequestRecovery={async () => {
                          await fetchJson(newsletterApiUrl(`campaigns/${detail.id}/protection/recovery`), {method:"POST", body:JSON.stringify({audience_reviewed:true})});
                          await Promise.all([detailQuery.refetch(), campaignsQuery.refetch()]);
                        }} />}

                      {detail?.campaign_delivery_state === "in_progress" ? (
                        <div className="newsletter-progress-panel">
                          <div className="newsletter-progress-panel-head">
                            <div>
                              <strong>Envoi en cours</strong>
                              <p className="muted">
                                {detail.delivery_progress_percent || 0}% envoyé · {detail.sent_count || 0} / {detail.total_recipient_count || 0}
                              </p>
                            </div>
                            {detail.next_batch_at ? <span className="newsletter-guardrail-provider">Prochaine vague : {formatDateTimeLabel(detail.next_batch_at)}</span> : null}
                          </div>
                          <div className="newsletter-campaign-progress-bar" aria-hidden="true">
                            <span style={{ width: `${detail.delivery_progress_percent || 0}%` }} />
                          </div>
                          <details className="newsletter-progress-details">
                            <summary>Pourquoi tout n'est pas encore parti ?</summary>
                            <div className="newsletter-progress-details-body">
                              <p>
                                {detail.guardrail_summary_message ||
                                  "Relyo envoie cette campagne par étapes pour éviter que vos emails arrivent dans les indésirables."}
                              </p>
                              <p>
                                {detail.sent_count || 0} emails sont déjà partis. {detail.remaining_recipient_count || 0} partiront ensuite automatiquement.
                              </p>
                              {detail.guardrail_summary_points?.length ? (
                                <ul className="newsletter-guardrail-points">
                                  {detail.guardrail_summary_points.map((point) => (
                                    <li key={point}>{point}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          </details>
                        </div>
                      ) : null}

                      {detail && <NewsletterReportMetrics key={`metrics:${detail.id}`} campaign={detail} />}

                      <NewsletterProtectionPanel key={`protection:${detail?.id}`} decision={detail?.protection} scope="campaign" variant="report"
                        loading={detailQuery.isFetching} error={detailQuery.isError} onOpenSettings={() => openSettings()} />
                    </div>

                    <aside className="newsletter-detail-visual-column">
                      <div className="newsletter-detail-actions">
                        <button
                          type="button"
                          className="button"
                          onClick={() => detail && duplicateCampaignMutation.mutate(detail.id)}
                          disabled={!detail || duplicateCampaignMutation.isPending}
                        >
                          <IconLabel iconId="icon-plus-box" label="Copier en brouillon" />
                        </button>
                        {detail && ["scheduled", "running"].includes(detail.status) ? (
                          <button
                            type="button"
                            className="button button-danger"
                            onClick={() => cancelCampaignMutation.mutate(detail.id)}
                            disabled={cancelCampaignMutation.isPending}
                          >
                            <IconLabel iconId="icon-minus-box" label="Annuler la campagne" />
                          </button>
                        ) : null}
                        {renderDefaultDesignOverflow(detail)}
                      </div>
                      <NewsletterDetailPreview campaign={detail} variant="sent" />
                    </aside>
                  </div>


                  <div>
                    <h3>Derniers destinataires</h3>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Email</th>
                            <th>Statut</th>
                            <th>Détail</th>
                          </tr>
                        </thead>
                        <tbody id="recent-recipients">
                          {!detail?.recent_recipients?.length ? (
                            <tr>
                              <td colSpan={3} className="empty-cell">
                                Aucun destinataire.
                              </td>
                            </tr>
                          ) : (
                            detail.recent_recipients.map((recipient) => (
                              <tr key={`${recipient.email}-${recipient.status}-${recipient.status_detail || "-"}`}>
                                <td>{recipient.email}</td>
                                <td>{recipient.status}</td>
                                <td>{recipient.status_detail || "-"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section className={`tab-panel${activeTab === "settings" ? " is-active" : ""}`} hidden={activeTab !== "settings"}>
            <div className="newsletter-settings-page newsletter-send-settings">
              {accountGuardrailsQuery.data?.protection?.action.kind === "pause" && <div className="newsletter-settings-notice" role="alert"><strong>Les envois sont suspendus</strong><p>Une vérification est nécessaire avant de reprendre les envois.</p><button type="button" className="button" onClick={()=>{const details=sendingProtectionRef.current?.querySelector("details");if(details){details.open=true;details.scrollIntoView({block:"nearest"});}}}>Voir ce qui bloque</button></div>}
              {settingsQuery.isLoading ? <p role="status">Chargement des réglages…</p> : settingsQuery.isError ? <p role="alert">Les réglages d’envoi sont indisponibles. Réessayez en rechargeant la page.</p> : <>
                <section className="newsletter-settings-section" aria-labelledby="sending-account-title">
                  <div className="newsletter-settings-section-head"><h2 id="sending-account-title">Compte d’envoi</h2><span className="newsletter-settings-badge">{gmailConnectionIsActive ? "Connecté" : smtpIsConfigured || sesRouteIsConfigured ? "Configuré" : "À configurer"}</span></div>
                  <div className="newsletter-sending-account">
                    <div><strong>{smtpIsConfigured || sesRouteIsConfigured ? effectiveSettingsForm.sender_email || emailProviderUsernameValue.trim() || "Adresse à renseigner" : "Connectez votre adresse email"}</strong>
                      <p className="muted">{smtpIsConfigured || sesRouteIsConfigured ? (sesRouteIsConfigured ? "Amazon SES" : providerChoiceLabel(resolvedEmailProviderChoice,emailProviderMode)) : "Elle servira à envoyer vos newsletters."}</p></div>
                    <button type="button" className={`button${smtpIsConfigured || sesRouteIsConfigured ? "" : " button-primary"}`} onClick={openEmailConnectionWizard}>{smtpIsConfigured || sesRouteIsConfigured ? "Modifier le compte mail" : "Configurer une adresse email"}</button>
                  </div>
                  {!smtpIsConfigured && !sesRouteIsConfigured && <p className="newsletter-settings-note">L’expéditeur et la capacité d’envoi apparaîtront une fois la connexion terminée.</p>}
                </section>
                {(smtpIsConfigured || sesRouteIsConfigured || !isHostedNewsletter) && <>
                  <section className="newsletter-settings-section" aria-labelledby="sending-identity-title">
                    <h2 id="sending-identity-title">Identité de l’expéditeur</h2>
                    <div className="newsletter-sender-fields">
                      <label>Nom affiché<input id="sender_name" type="text" className={hasFieldError(settingsFieldErrors,"sender_name") ? "is-field-invalid" : ""} value={settingsForm.sender_name} onChange={event=>{clearSettingsFieldError("sender_name");setSettingsForm(current=>({...current,sender_name:event.target.value}));}} /></label>
                      <label>Adresse de réponse<input id="reply_to_email" type="email" className={hasFieldError(settingsFieldErrors,"reply_to_email") ? "is-field-invalid" : ""} value={settingsForm.reply_to_email} onChange={event=>{clearSettingsFieldError("reply_to_email");setSettingsForm(current=>({...current,reply_to_email:event.target.value}));}} /><span className="newsletter-settings-note">Les réponses de vos destinataires arriveront ici.</span></label>
                      {!isHostedNewsletter && <label>URL publique<input id="public_base_url" type="url" value={settingsForm.public_base_url} onChange={event=>setSettingsForm(current=>({...current,public_base_url:event.target.value}))} /></label>}
                    </div>
                    <p className="newsletter-sender-preview">Les destinataires verront : <strong>{settingsForm.sender_name} &lt;{effectiveSettingsForm.sender_email || emailProviderUsernameValue.trim()}&gt;</strong></p>
                    {settingsOnly && settingsAreDirty && <div className="newsletter-settings-actions"><button type="button" className="button" disabled={settingsSaveState==="saving"} onClick={cancelSenderSettings}>Annuler</button><button type="button" id="save-settings" className="button button-primary" disabled={settingsSaveState==="saving"} onClick={handleSettingsSubmit}>{settingsSaveState==="saving" ? "Enregistrement…" : "Enregistrer l’expéditeur"}</button></div>}
                  </section>
                  <section className="newsletter-settings-section" aria-labelledby="sending-status-title">
                    <div className="newsletter-settings-section-head"><h2 id="sending-status-title">État des envois</h2><span className="newsletter-settings-badge">{accountGuardrailsQuery.isError ? "Vérification indisponible" : accountGuardrailsQuery.isFetching ? "Vérification en cours…" : accountGuardrailsQuery.data?.protection ? ({send:"Envoi possible",defer:"Envoi reporté",pause:"Envoi suspendu",block:"Configuration à compléter"})[accountGuardrailsQuery.data.protection.action.kind] : "À vérifier"}</span></div>
                    <p className="muted">{accountGuardrailsQuery.data?.protection?.action.kind === "pause" ? "Les conditions de reprise sont indiquées dans les vérifications ci-dessous." : deliveryStatusMessage}</p>
                    <p className="newsletter-settings-capacity"><strong>{accountGuardrailsQuery.data?.protection?.capacity.relyo_limits.per_24h ?? "—"}</strong> emails / 24 h <span className="newsletter-settings-note">· Plafond prudent actuel</span></p>
                    {deliveryStatusPoints.length>0 && <ul className="newsletter-sending-points">{deliveryStatusPoints.map(point=><li key={point}>{point}</li>)}</ul>}
                    {(isOauthConnectedForSelectedProvider && resolvedEmailProviderChoice==="gmail" || microsoft365SendOnlyConnectionIsActive) && <p className="newsletter-settings-note">Les retours de livraison sont limités avec cette connexion : tous les rejets ne peuvent pas être détectés.</p>}
                    <div ref={sendingProtectionRef}><NewsletterProtectionPanel decision={accountGuardrailsQuery.data?.protection} scope="account" loading={accountGuardrailsQuery.isFetching} error={accountGuardrailsQuery.isError} /></div>
                  </section>
                </>}
              </>}
              <p className={statusClassName(settingsStatus)} id="settings-status" role={settingsStatus.tone==="error" ? "alert" : "status"} hidden={!settingsStatus.message}>{settingsStatus.message}</p>
            </div>
          </section>
          <ModalShell
            isOpen={isEmailConnectionWizardOpen}
            title="Configurer une adresse email"
            onClose={closeEmailConnectionWizard}
          >
            <div
              ref={emailWizardModalBodyRef}
              className="newsletter-email-wizard-modal"
              onScroll={(event) => {
                emailWizardModalScrollTopRef.current = event.currentTarget.scrollTop;
              }}
            >
              <Stepper active={emailConnectionStepIndex} className="newsletter-email-stepper-shell" allowNextStepsSelect={false}>
                {EMAIL_CONNECTION_STEPS.map((step, index) => (
                  <Stepper.Step
                    key={step.id}
                    label={step.label}
                    description={step.hint}
                    onClick={() => {
                      if (index <= emailConnectionStepIndex) {
                        setEmailConnectionStep(step.id);
                      }
                    }}
                  />
                ))}
              </Stepper>

              {emailConnectionStep === "onboarding_previous_route" ? (
                <div className="newsletter-email-wizard-panel">
                  <div className="newsletter-email-wizard-copy">
                    <h4>Avant de connecter votre adresse</h4>
                    <p>Relyo adapte vos premiers envois a votre usage avant de vous proposer un service de connexion.</p>
                  </div>

                  <div className="newsletter-email-account-kind">
                    <div className="newsletter-email-wizard-copy">
                      <h4>Avant Relyo, comment envoyiez-vous ce type d&apos;emails ?</h4>
                      <p>Cette réponse prépare le cadre de départ sans vous demander de détails techniques.</p>
                    </div>
                    <div className="newsletter-email-account-kind-grid">
                      {PREVIOUS_ROUTE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`newsletter-email-account-kind-card${
                            effectiveSettingsForm.email_onboarding_previous_sending_route === option.value ? " is-selected" : ""
                          }`}
                          onClick={() =>
                            updateEmailOnboardingAnswers({
                              email_onboarding_previous_sending_route: option.value,
                            })
                          }
                        >
                          <strong>{option.label}</strong>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="newsletter-email-wizard-actions">
                    <button type="button" className="button" onClick={closeEmailConnectionWizard}>
                      Annuler
                    </button>
                    <div className="newsletter-email-wizard-actions-right">
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={() => setEmailConnectionStep("onboarding_previous_usage")}
                        disabled={!emailOnboardingPreviousRouteComplete}
                      >
                        Continuer
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {emailConnectionStep === "onboarding_previous_usage" ? (
                <div className="newsletter-email-wizard-panel">
                  <div className="newsletter-email-wizard-copy">
                    <h4>Quel etait a peu pres votre usage avant Relyo ?</h4>
                    <p>On reste volontairement simple: un ordre de grandeur suffit pour choisir un depart prudent.</p>
                  </div>

                  <div className="newsletter-email-account-kind">
                    <div className="newsletter-email-wizard-copy">
                      <h4>Taille habituelle d&apos;envoi</h4>
                    </div>
                    <div className="newsletter-email-account-kind-grid">
                      {SEND_SIZE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`newsletter-email-account-kind-card${
                            effectiveSettingsForm.email_onboarding_previous_send_size_range === option.value ? " is-selected" : ""
                          }`}
                          onClick={() =>
                            updateEmailOnboardingAnswers({
                              email_onboarding_previous_send_size_range: option.value,
                            })
                          }
                        >
                          <strong>{option.label}</strong>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="newsletter-email-account-kind">
                    <div className="newsletter-email-wizard-copy">
                      <h4>Frequence habituelle</h4>
                    </div>
                    <div className="newsletter-email-account-kind-grid">
                      {SEND_FREQUENCY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`newsletter-email-account-kind-card${
                            effectiveSettingsForm.email_onboarding_previous_send_frequency === option.value ? " is-selected" : ""
                          }`}
                          onClick={() =>
                            updateEmailOnboardingAnswers({
                              email_onboarding_previous_send_frequency: option.value,
                            })
                          }
                        >
                          <strong>{option.label}</strong>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="newsletter-email-wizard-actions">
                    <button type="button" className="button" onClick={() => setEmailConnectionStep("onboarding_previous_route")}>
                      Retour
                    </button>
                    <div className="newsletter-email-wizard-actions-right">
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={() => setEmailConnectionStep("onboarding_intended_usage")}
                        disabled={!emailOnboardingPreviousUsageComplete}
                      >
                        Continuer
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {emailConnectionStep === "onboarding_intended_usage" ? (
                <div className="newsletter-email-wizard-panel">
                  <div className="newsletter-email-wizard-copy">
                    <h4>Ce que vous comptez envoyer avec Relyo</h4>
                    <p>Ces réponses aident Relyo a vous proposer le bon point de départ avant le choix du provider.</p>
                  </div>

                  <div className="newsletter-email-account-kind">
                    <div className="newsletter-email-wizard-copy">
                      <h4>Taille prévue</h4>
                    </div>
                    <div className="newsletter-email-account-kind-grid">
                      {SEND_SIZE_OPTIONS.map((option) => (
                        <button
                          key={`intended-size-${option.value}`}
                          type="button"
                          className={`newsletter-email-account-kind-card${
                            effectiveSettingsForm.email_onboarding_intended_send_size_range === option.value ? " is-selected" : ""
                          }`}
                          onClick={() =>
                            updateEmailOnboardingAnswers({
                              email_onboarding_intended_send_size_range: option.value,
                            })
                          }
                        >
                          <strong>{option.label}</strong>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="newsletter-email-account-kind">
                    <div className="newsletter-email-wizard-copy">
                      <h4>Frequence prévue</h4>
                    </div>
                    <div className="newsletter-email-account-kind-grid">
                      {SEND_FREQUENCY_OPTIONS.map((option) => (
                        <button
                          key={`intended-frequency-${option.value}`}
                          type="button"
                          className={`newsletter-email-account-kind-card${
                            effectiveSettingsForm.email_onboarding_intended_send_frequency === option.value ? " is-selected" : ""
                          }`}
                          onClick={() =>
                            updateEmailOnboardingAnswers({
                              email_onboarding_intended_send_frequency: option.value,
                            })
                          }
                        >
                          <strong>{option.label}</strong>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="newsletter-email-account-kind">
                    <div className="newsletter-email-wizard-copy">
                      <h4>Type d&apos;email prevu</h4>
                    </div>
                    <div className="newsletter-email-account-kind-grid">
                      {INTENDED_EMAIL_TYPE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`newsletter-email-account-kind-card${
                            effectiveSettingsForm.email_onboarding_intended_email_type === option.value ? " is-selected" : ""
                          }`}
                          onClick={() =>
                            updateEmailOnboardingAnswers({
                              email_onboarding_intended_email_type: option.value,
                            })
                          }
                        >
                          <strong>{option.label}</strong>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="newsletter-email-wizard-actions">
                    <button type="button" className="button" onClick={() => setEmailConnectionStep("onboarding_previous_usage")}>
                      Retour
                    </button>
                    <div className="newsletter-email-wizard-actions-right">
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={continueEmailOnboardingToService}
                        disabled={!emailOnboardingIntendedUsageComplete || onboardingRecommendationMutation.isPending}
                      >
                        {onboardingRecommendationMutation.isPending ? "Analyse en cours..." : "Continuer"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {emailConnectionStep === "onboarding_recommendation" ? (
                <div className="newsletter-email-wizard-panel">
                  <div className="newsletter-email-wizard-copy">
                    <h4>{resolvedEmailOnboardingRecommendation?.summary_title || "Analyse de votre usage en cours"}</h4>
                    <p>
                      {resolvedEmailOnboardingRecommendation?.summary_message ||
                        "Relyo vérifie votre diagnostic pour vous conseiller le point de départ le plus prudent."}
                    </p>
                  </div>

                  <div className="newsletter-email-review-grid">
                    {emailOnboardingSnapshotItems.map((item) => (
                      <div key={item.label} className="newsletter-email-review-card">
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    ))}
                  </div>

                  <div className="newsletter-provider-summary">
                    <strong>Ce que Relyo vous conseille</strong>
                    {resolvedEmailOnboardingRecommendation?.summary_points?.length ? (
                      <ul className="newsletter-guardrail-mini-list">
                        {resolvedEmailOnboardingRecommendation.summary_points.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="newsletter-settings-hint">Chargement de la recommandation en cours...</p>
                    )}
                  </div>

                  {resolvedEmailOnboardingRecommendation?.show_ses_cost_note ? (
                    <div className="newsletter-email-provider-callout">
                      <strong>À garder en tête</strong>
                      <p>Amazon SES implique un coût dédié. Cette étape sera détaillée proprement dans la prochaine itération.</p>
                    </div>
                  ) : null}

                  <p
                    className={statusClassName(emailOnboardingRecommendationStatus)}
                    hidden={!emailOnboardingRecommendationStatus.message}
                  >
                    {emailOnboardingRecommendationStatus.message}
                  </p>

                  <div className="newsletter-email-wizard-actions">
                    <button type="button" className="button" onClick={() => setEmailConnectionStep("onboarding_intended_usage")}>
                      Retour
                    </button>
                    <div className="newsletter-email-wizard-actions-right">
                      {resolvedEmailOnboardingRecommendation?.recommended_route === "amazon_ses" ? (
                        <>
                          <button type="button" className="button" onClick={acceptClassicMailboxAfterRecommendation}>
                            Continuer avec mon adresse actuelle
                          </button>
                          <button type="button" className="button button-primary" onClick={acceptSesRecommendation}>
                            Continuer avec Amazon SES
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="button" onClick={acceptSesRecommendation}>
                            Continuer avec Amazon SES
                          </button>
                          <button
                            type="button"
                            className="button button-primary"
                            onClick={acceptClassicMailboxAfterRecommendation}
                            disabled={!resolvedEmailOnboardingRecommendation || onboardingRecommendationMutation.isPending}
                          >
                            {onboardingRecommendationMutation.isPending
                              ? "Analyse en cours..."
                              : "Continuer avec mon adresse actuelle"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {emailConnectionStep === "service" ? (
                <div className="newsletter-email-wizard-panel">
                  <div className="newsletter-email-wizard-copy">
                    <h4>Quel service utilisez-vous pour envoyer vos emails ?</h4>
                    <p>
                      Relyo préremplit la connexion quand c&apos;est possible, puis adapte les prochains envois à votre
                      situation.
                    </p>
                  </div>

                  <div className="newsletter-email-provider-grid">
                    {EMAIL_PROVIDER_CHOICES.map((choice) => {
                      const isSelected = resolvedEmailProviderChoice === choice.id;
                      const iconStyle: CSSProperties = choice.iconUrl
                        ? ({
                            ["--provider-color" as string]: choice.brandColor || "#203042",
                            ["--provider-icon" as string]: `url("${choice.iconUrl}")`,
                          } as CSSProperties)
                        : {};

                      return (
                        <button
                          key={choice.id}
                          type="button"
                          className={`newsletter-email-provider-card${isSelected ? " is-selected" : ""}`}
                          onClick={() => handleEmailProviderChoiceSelect(choice.id)}
                        >
                          <div className="newsletter-email-provider-card-top">
                            {choice.iconUrl ? (
                              <span className="newsletter-email-provider-logo" style={iconStyle} aria-hidden="true" />
                            ) : (
                              <span className="newsletter-email-provider-fallback" aria-hidden="true">
                                {choice.id === "custom" ? "+" : "?"}
                              </span>
                            )}
                          </div>
                          <strong>{choice.label}</strong>
                        </button>
                      );
                    })}
                  </div>

                  <div className="newsletter-email-account-kind">
                    <div className="newsletter-email-wizard-copy">
                      <h4>Quel type d&apos;adresse utilisez-vous ?</h4>
                      <p>Cette information aide Relyo à partir d&apos;une hypothèse de volume plus juste.</p>
                    </div>
                    <div className="newsletter-email-account-kind-grid">
                      {EMAIL_ACCOUNT_KIND_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`newsletter-email-account-kind-card${effectiveSettingsForm.email_account_kind === option.value ? " is-selected" : ""}`}
                          onClick={() => handleEmailAccountKindChange(option.value)}
                        >
                          <strong>{option.label}</strong>
                          <p>{option.hint}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {emailProviderDevelopmentNotice ? (
                    <div className="newsletter-email-provider-callout">
                      <strong>{emailProviderDevelopmentNotice.title}</strong>
                      <p>{emailProviderDevelopmentNotice.message}</p>
                    </div>
                  ) : null}

                  {activeEmailProviderPreset?.note &&
                  resolvedEmailProviderChoice === activeEmailProviderPreset.id &&
                  !emailProviderDevelopmentNotice ? (
                    <div className="newsletter-provider-summary">
                      <strong>{activeEmailProviderPreset.label}</strong>
                      <p className="newsletter-provider-preset-note">{activeEmailProviderPreset.note}</p>
                    </div>
                  ) : null}

                  <div className="newsletter-email-wizard-actions">
                    <button type="button" className="button" onClick={closeEmailConnectionWizard}>
                      Annuler
                    </button>
                    <div className="newsletter-email-wizard-actions-right">
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={() => setEmailConnectionStep("credentials")}
                        disabled={!emailProviderCanContinue}
                      >
                        Continuer
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {emailConnectionStep === "credentials" ? (
                <div className="newsletter-email-wizard-panel">
                  <div className="newsletter-email-wizard-copy">
                    <h4>{emailConnectionPanelTitle}</h4>
                    <p>{emailConnectionPanelDescription}</p>
                  </div>

                  <div className="newsletter-provider-summary">
                    <strong>{selectedEmailProviderChoice.label}</strong>
                    <p>
                      {emailProviderUsernameValue.trim()
                        ? `Adresse du compte mail : ${emailProviderUsernameValue.trim()}`
                        : "Renseignez l'identifiant du compte mail à utiliser pour l'envoi."}
                    </p>
                  </div>

                  {emailConnectionProviderCallout ? (
                    <div className="newsletter-email-provider-callout">
                      <strong>{emailConnectionProviderCallout.title}</strong>
                      <p>{emailConnectionProviderCallout.message}</p>
                    </div>
                  ) : null}

                  {emailProviderConnectionMode === "secure_sign_in" && !showAdvancedEmailSettings ? (
                    <div className="newsletter-email-oauth-panel">
                      <div className="newsletter-provider-summary">
                        <strong>Chemin recommandé</strong>
                        <p>
                          Ouvrez la fenêtre {emailProviderDisplayLabel}, autorisez l&apos;accès, puis revenez ici.
                          Relyo vérifiera ensuite automatiquement que tout fonctionne.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-2">
                        <label>
                          {emailConnectionUsernameLabel}
                          <input
                            id="email_provider_username"
                            type="text"
                            className={emailProviderUsernameHasError ? "is-field-invalid" : ""}
                            value={emailProviderUsernameValue}
                            onChange={(event) => {
                              clearSettingsFieldErrors(EMAIL_PROVIDER_USERNAME_FIELD_IDS);
                              setEmailConnectionVerifyFailureCount(0);
                              setEmailConnectionVerifyState("idle");
                              setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
                              setSettingsForm((current) => ({
                                ...current,
                                smtp_username: event.target.value,
                                imap_username: event.target.value,
                              }));
                            }}
                          />
                        </label>
                        <label className="field-span-2">
                          {emailConnectionPasswordLabel}
                          <input
                            id="email_provider_password"
                            type="password"
                            className={emailProviderPasswordHasError ? "is-field-invalid" : ""}
                            value={emailProviderPasswordValue}
                            onChange={(event) => {
                              clearSettingsFieldErrors(EMAIL_PROVIDER_PASSWORD_FIELD_IDS);
                              setEmailConnectionVerifyFailureCount(0);
                              setEmailConnectionVerifyState("idle");
                              setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
                              setSettingsForm((current) => ({
                                ...current,
                                smtp_password: event.target.value,
                                imap_password: event.target.value,
                              }));
                            }}
                          />
                        </label>
                      </div>

                      {emailProviderConnectionMode === "secure_sign_in" || emailProviderConnectionMode === "app_password" ? (
                        <p className="newsletter-settings-hint">
                          {resolvedEmailProviderChoice === "gmail"
                            ? "N'utilisez pas le mot de passe habituel de votre compte Google si Gmail le refuse. Un mot de passe d'application est souvent nécessaire."
                            : resolvedEmailProviderChoice === "outlook"
                              ? "Selon votre compte Microsoft, un mot de passe d'application peut être nécessaire si le mot de passe habituel est refusé."
                              : `Si ${emailProviderDisplayLabel} refuse le mot de passe habituel, essayez un mot de passe spécifique à l'application.`}
                        </p>
                      ) : null}
                    </>
                  )}

                  {showAdvancedEmailSettings ? (
                    <div className="newsletter-provider-advanced">
                      <div className="advanced-card newsletter-email-advanced-card">
                        <p className="muted newsletter-settings-hint">
                          Si votre service n&apos;est pas reconnu automatiquement, renseignez ici les réglages SMTP et
                          IMAP fournis par votre messagerie.
                        </p>
                        <div className="grid grid-3">
                          <label>
                            SMTP host
                            <input
                              id="smtp_host"
                              type="text"
                              className={hasFieldError(settingsFieldErrors, "smtp_host") ? "is-field-invalid" : ""}
                              placeholder="mail.infomaniak.com"
                              value={settingsForm.smtp_host}
                              onChange={(event) => {
                                clearSettingsFieldError("smtp_host");
                                setSettingsForm((current) => ({ ...current, smtp_host: event.target.value }));
                              }}
                            />
                          </label>
                          <label>
                            SMTP port
                            <input
                              id="smtp_port"
                              type="number"
                              className={hasFieldError(settingsFieldErrors, "smtp_port") ? "is-field-invalid" : ""}
                              value={settingsForm.smtp_port}
                              onChange={(event) => {
                                clearSettingsFieldError("smtp_port");
                                setSettingsForm((current) => ({ ...current, smtp_port: event.target.value }));
                              }}
                            />
                          </label>
                          <label>
                            Sécurité SMTP
                            <select
                              id="smtp_security"
                              className={hasFieldError(settingsFieldErrors, "smtp_security") ? "is-field-invalid" : ""}
                              value={settingsForm.smtp_security}
                              onChange={(event) => {
                                clearSettingsFieldError("smtp_security");
                                setSettingsForm((current) => ({ ...current, smtp_security: event.target.value }));
                              }}
                            >
                              <option value="starttls">STARTTLS</option>
                              <option value="ssl">SSL/TLS</option>
                              <option value="plain">Aucune</option>
                            </select>
                          </label>
                          <label>
                            IMAP host
                            <input
                              id="imap_host"
                              type="text"
                              className={hasFieldError(settingsFieldErrors, "imap_host") ? "is-field-invalid" : ""}
                              placeholder="mail.infomaniak.com"
                              value={settingsForm.imap_host}
                              onChange={(event) => {
                                clearSettingsFieldError("imap_host");
                                setSettingsForm((current) => ({ ...current, imap_host: event.target.value }));
                              }}
                            />
                          </label>
                          <label>
                            IMAP port
                            <input
                              id="imap_port"
                              type="number"
                              className={hasFieldError(settingsFieldErrors, "imap_port") ? "is-field-invalid" : ""}
                              value={settingsForm.imap_port}
                              onChange={(event) => {
                                clearSettingsFieldError("imap_port");
                                setSettingsForm((current) => ({ ...current, imap_port: event.target.value }));
                              }}
                            />
                          </label>
                          <label>
                            SSL IMAP
                            <select
                              id="imap_ssl"
                              className={hasFieldError(settingsFieldErrors, "imap_ssl") ? "is-field-invalid" : ""}
                              value={settingsForm.imap_ssl}
                              onChange={(event) => {
                                clearSettingsFieldError("imap_ssl");
                                setSettingsForm((current) => ({ ...current, imap_ssl: event.target.value }));
                              }}
                            >
                              <option value="true">Oui</option>
                              <option value="false">Non</option>
                            </select>
                          </label>
                          <label>
                            Dossier IMAP
                            <input
                              id="imap_folder_advanced"
                              type="text"
                              className={hasFieldError(settingsFieldErrors, "imap_folder") ? "is-field-invalid" : ""}
                              value={settingsForm.imap_folder}
                              onChange={(event) => {
                                clearSettingsFieldError("imap_folder");
                                setSettingsForm((current) => ({ ...current, imap_folder: event.target.value }));
                              }}
                            />
                          </label>
                          <label>
                            Utilisateur SMTP
                            <input
                              id="smtp_username"
                              type="text"
                              className={hasFieldError(settingsFieldErrors, "smtp_username") ? "is-field-invalid" : ""}
                              value={settingsForm.smtp_username}
                              onChange={(event) => {
                                clearSettingsFieldError("smtp_username");
                                setSettingsForm((current) => ({ ...current, smtp_username: event.target.value }));
                              }}
                            />
                          </label>
                          <label className="field-span-2">
                            Mot de passe SMTP
                            <input
                              id="smtp_password"
                              type="password"
                              className={hasFieldError(settingsFieldErrors, "smtp_password") ? "is-field-invalid" : ""}
                              value={settingsForm.smtp_password}
                              onChange={(event) => {
                                clearSettingsFieldError("smtp_password");
                                setSettingsForm((current) => ({ ...current, smtp_password: event.target.value }));
                              }}
                            />
                          </label>
                          <label>
                            Utilisateur IMAP
                            <input
                              id="imap_username"
                              type="text"
                              className={hasFieldError(settingsFieldErrors, "imap_username") ? "is-field-invalid" : ""}
                              value={settingsForm.imap_username}
                              onChange={(event) => {
                                clearSettingsFieldError("imap_username");
                                setSettingsForm((current) => ({ ...current, imap_username: event.target.value }));
                              }}
                            />
                          </label>
                          <label className="field-span-2">
                            Mot de passe IMAP
                            <input
                              id="imap_password"
                              type="password"
                              className={hasFieldError(settingsFieldErrors, "imap_password") ? "is-field-invalid" : ""}
                              value={settingsForm.imap_password}
                              onChange={(event) => {
                                clearSettingsFieldError("imap_password");
                                setSettingsForm((current) => ({ ...current, imap_password: event.target.value }));
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <p
                    className={statusClassName(emailConnectionVerifyStatus)}
                    role="status"
                    aria-live="polite"
                    hidden={!emailConnectionVerifyStatus.message}
                  >
                    {emailConnectionVerifyStatus.message}
                  </p>

                  {!showAdvancedEmailSettings && emailConnectionVerifyFailureCount === 1 ? (
                    <p className="newsletter-settings-hint">
                      Vous pouvez réessayer une deuxième fois. Relyo n&apos;ouvrira les réglages avancés que si la
                      connexion ne passe toujours pas.
                    </p>
                  ) : null}

                  <div className="newsletter-email-wizard-actions">
                    <button type="button" className="button" onClick={() => setEmailConnectionStep("service")}>
                      Retour
                    </button>
                    <div className="newsletter-email-wizard-actions-right">
                      {emailProviderConnectionMode === "secure_sign_in" && !showAdvancedEmailSettings ? (
                        <>
                          <button
                            type="button"
                            className="button"
                            onClick={() => {
                              setShowAdvancedEmailSettings(true);
                              setEmailConnectionVerifyStatus({ message: "", tone: "neutral" });
                            }}
                          >
                            Méthode manuelle
                          </button>
                          <button
                            type="button"
                            className="button button-primary"
                            onClick={handleStartSecureProviderConnection}
                            disabled={isEmailOauthPending}
                          >
                            {isEmailOauthPending
                              ? "Connexion en cours..."
                              : resolvedEmailProviderChoice === "gmail"
                                ? "Continuer avec Google"
                                : "Continuer avec Microsoft"}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="button button-primary"
                          onClick={handleVerifyEmailConnection}
                          disabled={emailConnectionVerifyState === "verifying"}
                        >
                          {emailConnectionVerifyState === "verifying" ? "Vérification en cours..." : "Vérifier la connexion"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {emailConnectionStep === "review" ? (
                <div className="newsletter-email-wizard-panel">
                  {effectiveSettingsForm.email_onboarding_chosen_route === "amazon_ses" ? (
                    <>
                      <div className="newsletter-email-wizard-copy">
                        <h4>Configurer l&apos;envoi dédié avec Amazon SES</h4>
                        <p>
                          Choisissez l&apos;adresse qui portera vos newsletters. Relyo prépare ensuite la configuration du
                          domaine et garde les envois bloqués jusqu&apos;à validation.
                        </p>
                      </div>

                      <div className="newsletter-ses-choice-grid">
                        <button
                          type="button"
                          className={`newsletter-ses-choice${sesIdentityMode === "dedicated_subdomain" ? " is-selected" : ""}`}
                          onClick={() => updateSesIdentityMode("dedicated_subdomain")}
                        >
                          <span>Recommandé</span>
                          <strong>Relyo crée une adresse dédiée à vos newsletters</strong>
                          <p>
                            C&apos;est l&apos;option la plus protectrice pour vos emails habituels, avec une activation progressive.
                          </p>
                        </button>
                        <button
                          type="button"
                          className={`newsletter-ses-choice${sesIdentityMode === "existing_from_address" ? " is-selected" : ""}`}
                          onClick={() => updateSesIdentityMode("existing_from_address")}
                        >
                          <span>Continuité</span>
                          <strong>Conserver une adresse newsletter existante</strong>
                          <p>
                            Vous gardez l&apos;adresse déjà connue de vos contacts, puis Relyo vérifie la configuration avant
                            d&apos;augmenter les envois.
                          </p>
                        </button>
                      </div>

                      <div className="newsletter-ses-form">
                        {sesIdentityMode === "dedicated_subdomain" ? (
                          <div className="grid grid-3">
                            <label className="field-span-2">
                              Domaine principal
                              <input
                                id="ses_domain_root"
                                type="text"
                                placeholder="exemple.fr"
                                className={hasFieldError(settingsFieldErrors, "ses_domain_identity") ? "is-field-invalid" : ""}
                                value={sesDedicatedRootDomain}
                                onChange={(event) => {
                                  clearSettingsFieldError("ses_domain_identity");
                                  updateDedicatedSesDomain(event.target.value);
                                }}
                              />
                            </label>
                            <label>
                              Préfixe
                              <select
                                id="ses_subdomain_prefix"
                                value={sesDedicatedPrefix}
                                onChange={(event) => updateDedicatedSesPrefix(event.target.value)}
                              >
                                <option value="news">news</option>
                                <option value="newsletter">newsletter</option>
                                <option value="mail">mail</option>
                              </select>
                            </label>
                            <div className="newsletter-ses-derived-address field-span-3">
                              <span>Adresse prévue</span>
                              <strong>
                                {sesDedicatedRootDomain
                                  ? `newsletter@${sesDedicatedPrefix}.${sesDedicatedRootDomain}`
                                  : "newsletter@news.votre-domaine.fr"}
                              </strong>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-2">
                            <label className="field-span-2">
                              Adresse newsletter existante
                              <input
                                id="ses_existing_sender_email"
                                type="email"
                                placeholder="newsletter@exemple.fr"
                                className={hasFieldError(settingsFieldErrors, "sender_email") ? "is-field-invalid" : ""}
                                value={sesExistingSenderEmail}
                                onChange={(event) => {
                                  clearSettingsFieldError("sender_email");
                                  updateExistingSesSender(event.target.value);
                                }}
                              />
                            </label>
                          </div>
                        )}
                        <label>
                          Adresse de réponse
                          <input
                            id="ses_reply_to_email"
                            type="email"
                            placeholder="contact@exemple.fr"
                            className={hasFieldError(settingsFieldErrors, "reply_to_email") ? "is-field-invalid" : ""}
                            value={effectiveSettingsForm.reply_to_email}
                            onChange={(event) => {
                              clearSettingsFieldError("reply_to_email");
                              setSettingsForm((current) => ({ ...current, reply_to_email: event.target.value }));
                            }}
                          />
                        </label>
                      </div>

                      <div className="newsletter-ses-warning">
                        <strong>À prévoir avant l&apos;activation</strong>
                        <p>
                          {effectiveSettingsForm.ses_setup_warning ||
                            "Cette étape demande un accès aux réglages de votre domaine. Si vous ne les gérez pas vous-même, transmettez ces informations à la personne qui s'occupe de votre site ou de votre nom de domaine."}
                        </p>
                        <div className="newsletter-ses-help-entry">
                          <button
                            type="button"
                            className="button"
                            onClick={() =>
                              setEmailConnectionVerifyStatus({
                                message:
                                  "Demandez de l'aide à la personne qui gère votre domaine ou transmettez-lui cette checklist DNS.",
                                tone: "neutral",
                              })
                            }
                          >
                            Demander de l&apos;aide
                          </button>
                          <span>Si vous ne gérez pas le domaine vous-même, cette personne peut simplement recopier les lignes ci-dessous.</span>
                        </div>
                      </div>

                      <div className="newsletter-ses-status">
                        <div>
                          <span>Statut</span>
                          <strong>{sesStatusLabel(sesStatus)}</strong>
                        </div>
                        <div>
                          <span>DKIM</span>
                          <strong>{sesDkimIsVerified ? "Vérifié" : "À vérifier"}</strong>
                        </div>
                        <div>
                          <span>MAIL FROM</span>
                          <strong>{sesMailFromIsVerified ? "Vérifié" : "À vérifier"}</strong>
                        </div>
                      </div>

                      {effectiveSettingsForm.ses_setup_guidance ? (
                        <p className="newsletter-settings-hint">{effectiveSettingsForm.ses_setup_guidance}</p>
                      ) : null}

                      <div className="newsletter-ses-dns-checklist">
                        <div className="newsletter-email-wizard-copy">
                          <h4>Enregistrements à transmettre</h4>
                          <p>
                            Copiez ces lignes dans les réglages DNS de votre domaine. Les libellés techniques doivent rester
                            identiques.
                          </p>
                        </div>
                        {sesDnsRecordGroups.length > 0 ? (
                          <div className="newsletter-ses-dns-list">
                            {sesDnsRecordGroups.map((group) => (
                              <section key={group.purpose} className="newsletter-ses-dns-group">
                                <div className="newsletter-ses-dns-group-head">
                                  <div>
                                    <h4>{group.title}</h4>
                                    <p>{group.description}</p>
                                  </div>
                                </div>
                                <div className="newsletter-ses-dns-group-records">
                                  {group.records.map((record, index) => {
                                    const statusLabel =
                                      sesDnsProviderStatusLabel(record.status) ||
                                      sesDnsFallbackStatusLabel(group.purpose, sesDkimIsVerified, sesMailFromIsVerified);
                                    return (
                                      <div key={`${record.type}-${record.name}-${index}`} className="newsletter-ses-dns-row">
                                        <div className="newsletter-ses-dns-row-type">
                                          <span>{record.type}</span>
                                          <strong className={sesDnsStatusClassName(statusLabel)}>{statusLabel}</strong>
                                        </div>
                                        <div className="newsletter-ses-dns-row-values">
                                          <div className="newsletter-ses-dns-value-line">
                                            <span>Nom</span>
                                            <strong>{record.name}</strong>
                                            <button
                                              type="button"
                                              className="button newsletter-ses-copy-button"
                                              onClick={() => void handleCopySesDnsText(record.name, "Nom")}
                                              aria-label={`Copier le nom DNS ${record.name}`}
                                            >
                                              Copier
                                            </button>
                                          </div>
                                          <div className="newsletter-ses-dns-value-line">
                                            <span>Valeur</span>
                                            <code>{record.value}</code>
                                            <button
                                              type="button"
                                              className="button newsletter-ses-copy-button"
                                              onClick={() => void handleCopySesDnsText(record.value, "Valeur")}
                                              aria-label={`Copier la valeur DNS ${record.value}`}
                                            >
                                              Copier
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </section>
                            ))}
                          </div>
                        ) : (
                          <p className="newsletter-settings-hint">
                            Préparez la configuration pour générer la checklist DNS.
                          </p>
                        )}
                        <p
                          className={statusClassName(sesDnsCopyStatus)}
                          role="status"
                          aria-live="polite"
                          hidden={!sesDnsCopyStatus.message}
                        >
                          {sesDnsCopyStatus.message}
                        </p>
                      </div>

                      {effectiveSettingsForm.ses_last_checked_at ? (
                        <p className="newsletter-settings-hint">
                          Dernière vérification : {formatDateTimeLabel(effectiveSettingsForm.ses_last_checked_at)}
                        </p>
                      ) : null}
                      {effectiveSettingsForm.ses_last_error ? (
                        <p className="status-error">{effectiveSettingsForm.ses_last_error}</p>
                      ) : null}

                      <p
                        className={statusClassName(emailConnectionVerifyStatus)}
                        role="status"
                        aria-live="polite"
                        hidden={!emailConnectionVerifyStatus.message}
                      >
                        {emailConnectionVerifyStatus.message}
                      </p>

                      <div className="newsletter-email-wizard-actions">
                        <button type="button" className="button" onClick={closeEmailConnectionWizard}>
                          Annuler
                        </button>
                        <div className="newsletter-email-wizard-actions-right">
                          <button type="button" className="button" onClick={returnToClassicMailboxFlow}>
                            Revenir à ma boîte mail actuelle
                          </button>
                          {sesDnsRecords.length > 0 ? (
                            <button
                              type="button"
                              className="button"
                              onClick={() => refreshSesRouteMutation.mutate()}
                              disabled={refreshSesRouteMutation.isPending}
                            >
                              {refreshSesRouteMutation.isPending ? "Vérification..." : "Vérifier la configuration"}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="button button-primary"
                            onClick={handlePrepareSesRoute}
                            disabled={!sesCanPrepare || prepareSesRouteMutation.isPending}
                          >
                            {prepareSesRouteMutation.isPending
                              ? "Préparation..."
                              : sesDnsRecords.length > 0
                                ? "Mettre à jour la configuration"
                                : "Préparer la configuration DNS"}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="newsletter-email-wizard-copy">
                        <h4>Vérifier avant d&apos;enregistrer</h4>
                        <p>
                          La connexion a bien répondu. Vous pouvez maintenant l&apos;enregistrer pour que Relyo l&apos;utilise
                          pour vos prochaines newsletters.
                        </p>
                      </div>

                      <div className="newsletter-email-review-grid">
                        {emailConnectionReviewItems.map((item) => (
                          <div key={item.label} className="newsletter-email-review-card">
                            <span>{item.label}</span>
                            <strong>{item.value}</strong>
                          </div>
                        ))}
                      </div>

                      {microsoft365SendOnlyConnectionIsActive ? (
                        <div className="newsletter-provider-summary">
                          <strong>Connexion Microsoft 365 active</strong>
                          <p>
                            Avec Microsoft 365, Relyo peut envoyer vos campagnes, mais ne détecte pas automatiquement les
                            adresses qui refusent les messages.
                          </p>
                          <p>
                            Si vous avez besoin d&apos;un suivi plus complet des rejets, il faudra passer par une solution
                            d&apos;envoi dédiée.
                          </p>
                        </div>
                      ) : null}

                      {gmailSecureConnectionIsReady ? (
                        <div className="newsletter-provider-summary">
                          <strong>Connexion Gmail active</strong>
                          <p>
                            Avec Gmail, Relyo peut envoyer vos campagnes, mais ne détecte pas automatiquement les adresses
                            qui refusent les messages.
                          </p>
                          <p>
                            Si vous avez besoin d&apos;un suivi plus complet des rejets et d&apos;un envoi plus avancé, il
                            faudra passer par une solution d&apos;envoi dédiée.
                          </p>
                        </div>
                      ) : null}

                      <div className="newsletter-provider-summary">
                        <strong>Ce que Relyo fera ensuite</strong>
                        <p>
                          Après enregistrement, Relyo vérifie chaque campagne et peut l&apos;envoyer par étapes si cela réduit
                          le risque d&apos;indésirables.
                        </p>
                      </div>

                      <p
                        className={statusClassName(emailConnectionVerifyStatus)}
                        role="status"
                        aria-live="polite"
                        hidden={!emailConnectionVerifyStatus.message}
                      >
                        {emailConnectionVerifyStatus.message}
                      </p>

                      <div className="newsletter-email-wizard-actions">
                        <button type="button" className="button" onClick={() => setEmailConnectionStep("credentials")}>
                          Modifier la connexion
                        </button>
                        <div className="newsletter-email-wizard-actions-right">
                          <button type="button" className="button" onClick={switchToSesRouteFromClassicReview}>
                            Configurer Amazon SES à la place
                          </button>
                          {isOauthConnectedForSelectedProvider ? (
                            <button type="button" className="button button-primary" onClick={closeEmailConnectionWizard}>
                              Terminer
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="button button-primary"
                              onClick={handleSettingsSubmit}
                              disabled={settingsSaveState === "saving"}
                            >
                              {settingsSaveState === "saving" ? "Enregistrement..." : "Enregistrer la connexion"}
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </ModalShell>
          <ModalShell
            isOpen={isProtectionConfirmationDialogOpen}
            title={protectionBeforeSendCopy.title}
            onClose={() => setIsProtectionConfirmationDialogOpen(false)}
          >
            <div className="newsletter-tracking-dialog newsletter-protection-dialog">
              <p className="muted newsletter-tracking-dialog-message">{protectionBeforeSendCopy.message}</p>
              <div className="newsletter-protection-callout">
                <strong>Chemin recommandé</strong>
                <p>Avant de planifier cet envoi, revenez une étape en arrière pour alléger la campagne et repartir plus prudemment.</p>
              </div>
              <ul className="newsletter-guardrail-points">
                {protectionBeforeSendCopy.recommendations.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <div className="newsletter-email-wizard-actions">
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    setIsProtectionConfirmationDialogOpen(false);
                    void handleCreateCampaign({ skipProtectionConfirmation: true });
                  }}
                >
                  {protectionBeforeSendCopy.secondaryLabel}
                </button>
                <div className="newsletter-email-wizard-actions-right">
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={() => {
                      setIsProtectionConfirmationDialogOpen(false);
                      setComposerStep("audience");
                      setCampaignStatus({
                        message: "Relyo vous ramène à la campagne pour alléger cet envoi avant planification.",
                      tone: "neutral",
                    });
                    }}
                  >
                  {protectionBeforeSendCopy.primaryLabel}
                  </button>
                </div>
              </div>
            </div>
          </ModalShell>
          <MediaLibraryModal
            opened={mediaTarget !== null}
            title="Choisir une image pour la newsletter"
            subtitle="La médiathèque est partagée avec Évènements et permet aussi l'import direct."
            confirmLabel={
              mediaTarget?.kind === "richtext"
                ? "Inserer dans l'editeur"
                : mediaTarget?.kind === "assistant-hero"
                  ? "Utiliser dans le hero"
                : mediaTarget?.kind === "assistant-logo"
                  ? "Utiliser comme logo"
                  : "Utiliser dans la section"
            }
            usage={{
              source_app: "newsletter",
              usage_context:
                mediaTarget?.kind === "richtext"
                  ? "richtext-editor"
                  : mediaTarget?.kind === "assistant-hero"
                    ? "hero-image"
                  : mediaTarget?.kind === "assistant-logo"
                    ? "assistant-logo"
                    : "assistant-section",
              usage_label:
                mediaTarget?.kind === "assistant-section"
                  ? assistantDraft.sections.find((section) => section.id === mediaTarget.sectionId)?.title ||
                    campaignForm.name ||
                    "Section newsletter"
                  : mediaTarget?.kind === "assistant-hero"
                    ? assistantDraft.intro_title || campaignForm.name || "Hero newsletter"
                  : mediaTarget?.kind === "assistant-logo"
                    ? campaignForm.name || "Logo newsletter"
                  : campaignForm.name || "Contenu newsletter",
            }}
            onClose={() => {
              setMediaTarget(null);
              richTextImageInsertRef.current = null;
            }}
            onConfirm={(item: MediaLibraryItem) => {
              if (mediaTarget?.kind === "assistant-section") {
                updateAssistantSection(mediaTarget.sectionId, {
                  image_url: item.public_url,
                  image_alt: item.alt_text.trim() || item.original_name,
                });
              } else if (mediaTarget?.kind === "assistant-hero") {
                setAssistantDraft((current) => ({
                  ...current,
                  intro_image_url: item.public_url,
                  intro_image_alt: item.alt_text.trim() || item.original_name,
                }));
              } else if (mediaTarget?.kind === "assistant-logo") {
                setAssistantDraft((current) => ({
                  ...current,
                  header_logo_url: item.public_url,
                  header_logo_alt: item.alt_text.trim() || item.original_name,
                }));
              } else if (mediaTarget?.kind === "richtext") {
                richTextImageInsertRef.current?.(item.public_url, item.alt_text);
              }
              setMediaTarget(null);
              richTextImageInsertRef.current = null;
            }}
          />
        </main>
        </AppSurface>
      </AppChrome>
    </>
  );
}

export function createNewsletterRoutes(routes: NewsletterRouteConfig = NEWSLETTER_STANDALONE_ROUTES) {
  return [
    {
      path: routes.listPath,
      element: withNewsletterRouteConfig(<NewsletterPage />, routes),
    },
  ];
}

export function HostedNewsletterSettingsPage() {
  return withNewsletterRouteConfig(<NewsletterPage key="settings" settingsOnly />, NEWSLETTER_HOSTED_ROUTES);
}

export function createHostedNewsletterRoutes() {
  return createNewsletterRoutes(NEWSLETTER_HOSTED_ROUTES);
}

const router = createBrowserRouter(createNewsletterRoutes(), { basename: resolveMountedBasename("newsletter") });

export function App() {
  return (
    <NewsletterErrorBoundary>
      <RouterProvider router={router} />
    </NewsletterErrorBoundary>
  );
}
